/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-useless-escape */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Checkbox,
  Select,
  MenuItem,
  Pagination,
  Menu,
} from "@mui/material";
import {
  riskAnalysisService,
  RiskAnalysisResponse,
  RISK_LLM_SECTION,
  surveyReportService,
  validateRiskAnalysisUploadSize,
  EmployeeAgreementAnalysisResponse,
  EmployeeAgreementRequestError,
  EmployeeAgreementFileResult,
  sortMultifileAnalysisUploadsByName,
} from "@services/riskAnalysis";
import {
  buildMergedExtractionFileResults,
  retainExtractionFileResults,
} from "@utils/extractionProgress";
import { EmployeeAgreementResults } from "./EmployeeAgreementResults";
import {
  affablebpmIntegrationService,
  ExternalGetRecordAndFilesDetailsResponse,
  ExternalGetFileByFileIdResponse,
} from "@services/affablebpmIntegration";
import { continuousComplianceService } from "@services/continuousCompliance";
import { GlobalAdminLlmPromptButton } from "./GlobalAdminLlmPromptButton";
import jsPDF from "jspdf";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Footer,
  PageNumber,
  TabStopType,
  TabStopPosition,
} from "docx";
import { useAffableConnect } from "@hooks/useAffableConnect";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import AffableConnectedIcon from "./AffableConnectedIcon";
import { toast } from "react-toastify";
import { auditLogService } from "@services/auditLog";
import { getCurrentUserId } from "@utils/userUtils";
import { DeleteFileIcon } from "@icons/DeleteFileIcon";
import surveyIcon from "@assets/survey_icon.svg";
import incidentIcon from "@assets/incident_icon.svg";
import { assetUrl } from "@utils/publicPath";
import type { PayorContractAnalysisProps } from "./PayorContractAnalysis";

/** Root-absolute — PUBLIC_URL=. emits relative static/media paths that break nested routes. */
const surveyIconSrc = assetUrl(surveyIcon);
const incidentIconSrc = assetUrl(incidentIcon);
function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"] as const;
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
}

const EMPLOYEE_AGREEMENT_FILE_RE = /\.(pdf|docx|doc|txt)$/i;

function filterEmployeeAgreementFiles(files: File[]): File[] {
  return files.filter((f) => EMPLOYEE_AGREEMENT_FILE_RE.test(f.name));
}

/** Omit bulky OCR payloads from persisted history; keep statuses/errors. */
function metadataForEmployeeAgreementHistory(
  metadata: EmployeeAgreementAnalysisResponse["metadata"] | undefined,
): EmployeeAgreementAnalysisResponse["metadata"] {
  const base = metadata ? { ...metadata } : {};
  const fr = base.file_results;
  if (!Array.isArray(fr)) return base;
  return {
    ...base,
    file_results: fr.map((row) => {
      const { extracted_text: _omit, ...rest } = row;
      return rest;
    }),
  };
}

/** Employee-type label for Recently Reviewed titles (envelope metadata.employee_type). */
function employeeAgreementRoleLabel(employeeType: string | undefined): string {
  const t = String(employeeType || "physician").trim().toLowerCase();
  if (t === "physician") return "Physician";
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Physician";
}

/**
 * Guess clinician name from filenames when analysis.metadata.primary_individual is absent
 * (e.g. "(Eugenia Greenberg, D.O.)" in each file name).
 */
function inferPrimaryIndividualFromAgreementFilenames(
  filenames: string[],
): string {
  if (!filenames.length) return "";
  const candidates: string[] = [];
  for (const fn of filenames) {
    const matches = [...fn.matchAll(/\(([^)]+)\)/g)];
    for (const m of matches) {
      const inner = m[1].trim();
      if (/\b(D\.O\.|M\.D\.|DO|MD)\b/i.test(inner) && inner.length > 3) {
        candidates.push(inner);
      }
    }
  }
  if (candidates.length === 0) return "";
  const counts = new Map<string, number>();
  for (const c of candidates) {
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  let best = candidates[0];
  let bestN = 0;
  for (const [k, v] of counts) {
    if (v > bestN) {
      best = k;
      bestN = v;
    }
  }
  return best;
}

/**
 * Single Recently Reviewed line per employee-agreement analysis (one batch = one row).
 */
function formatEmployeeAgreementRecentlyReviewedTitle(historyItem: any): string {
  const envelope = historyItem?.data_obj?.metadata;
  const analysisMeta = historyItem?.data_obj?.analysis?.metadata;
  const fromLlm = String(analysisMeta?.primary_individual ?? "").trim();
  let name = fromLlm;
  if (!name) {
    let fromFiles: string[] = [];
    if (Array.isArray(envelope?.filenames) && envelope.filenames.length > 0) {
      fromFiles = envelope.filenames.map((s: unknown) => String(s));
    } else if (
      typeof envelope?.filename === "string" &&
      envelope.filename.includes(";")
    ) {
      fromFiles = envelope.filename.split(";").map((s: string) => s.trim());
    }
    name = inferPrimaryIndividualFromAgreementFilenames(fromFiles);
  }
  const role = employeeAgreementRoleLabel(envelope?.employee_type);
  if (name) {
    return `${name} ${role} Agreement`;
  }
  const n = Number(envelope?.file_count) || 0;
  if (n > 1) {
    return `${role} Agreement (${n} files)`;
  }
  if (typeof envelope?.filename === "string" && envelope.filename.trim()) {
    const single = envelope.filename.trim().split(";")[0].trim();
    return single || `${role} Agreement`;
  }
  return `${role} Agreement`;
}

/** Backend sets can_access_employee_agreements from app.config.employee_agreement_access. */
function readEmployeeAgreementAccessFromStorage(): boolean {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return false;
    const u = JSON.parse(raw) as { can_access_employee_agreements?: boolean };
    return u.can_access_employee_agreements === true;
  } catch {
    return false;
  }
}

function formatClientUploadTimestamp(date: Date = new Date()): string {
  return `${date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })}, ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

/** Alias for history rows — same output as formatClientUploadTimestamp */
const formatHistoryUploadedAt = formatClientUploadTimestamp;

/**
 * When analysis finished (envelope metadata or nested LLM analysis metadata).
 */
function getHistoryItemExplicitAnalysisTimeMs(historyItem: any): number | null {
  const env = historyItem?.data_obj?.metadata;
  if (
    typeof env?.analysis_timestamp === "string" &&
    env.analysis_timestamp.trim()
  ) {
    const d = new Date(env.analysis_timestamp.trim());
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  const nested = historyItem?.data_obj?.analysis?.metadata;
  if (
    nested &&
    typeof nested.analysis_timestamp === "string" &&
    nested.analysis_timestamp.trim()
  ) {
    const d = new Date(nested.analysis_timestamp.trim());
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return null;
}

function getHistoryRowAnalyzedAndUploadLabels(historyItem: any): {
  analyzedLabel: string | null;
  showUploaded: boolean;
} {
  const explicitMs = getHistoryItemExplicitAnalysisTimeMs(historyItem);
  const createdMs = historyItem?.created_at
    ? new Date(historyItem.created_at).getTime()
    : null;
  const displayAnalyzedMs = explicitMs ?? createdMs;
  const analyzedLabel =
    displayAnalyzedMs != null
      ? formatClientUploadTimestamp(new Date(displayAnalyzedMs))
      : null;
  const showUploaded =
    createdMs != null &&
    explicitMs != null &&
    Math.abs(explicitMs - createdMs) > 2000;
  return { analyzedLabel, showUploaded };
}

/** Document outline — same glyph as Continuous Compliance / Recently Reviewed */
function ComplianceHistoryFileGlyph(): React.ReactElement {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6 5C6 3.89543 6.89543 3 8 3H16V10C16 11.6569 17.3431 13 19 13H26V27C26 28.1046 25.1046 29 24 29H8C6.89543 29 6 28.1046 6 27V5ZM25.927 12H19C17.8954 12 17 11.1046 17 10V3.07303C17.3293 3.16456 17.6327 3.33984 17.8787 3.58579L25.4142 11.1213C25.6602 11.3673 25.8354 11.6707 25.927 12ZM8 2C6.34315 2 5 3.34315 5 5V27C5 28.6569 6.34315 30 8 30H24C25.6569 30 27 28.6569 27 27V12.5355C27 11.7399 26.6839 10.9768 26.1213 10.4142L18.5858 2.87868C18.0232 2.31607 17.2601 2 16.4645 2H8Z"
        fill="#262626"
      />
    </svg>
  );
}

// Type definitions for Affable API response normalization
interface RawAffableImage {
  ImageId?: number | string;
  ImageName?: string;
  ImageType?: string;
  ImageCreatedDate?: string;
}

interface RawAffableFolder {
  FolderId?: number | string;
  FolderName?: string;
  Images?: RawAffableImage[];
}

interface RawAffableDocument {
  DocumentId?: number | string;
  Folders?: RawAffableFolder[];
}

interface RawAffableDataItem {
  RecordList?: any[];
  RecordFileData?: RawAffableDocument[];
}

interface RawAffableResponse extends ExternalGetRecordAndFilesDetailsResponse {
  Status?: string;
  ErrorMsg?: string;
  Message?: string;
  Data?: RawAffableDataItem[];
  Record_TotalCount?: number | string;
}

interface AffableImageItem {
  id: number;
  name: string;
  type: string;
  createdAt: string;
}

interface AffableFolderItem {
  id: number;
  name: string;
  images: AffableImageItem[];
}

interface AffableDocumentItem {
  id: number;
  folders: AffableFolderItem[];
}

interface AffableRecordItem {
  DocumentID: number;
  DeptID: number;
  RowNum: number;
  [key: string]: any; // Allow any additional dynamic fields
}

// Helper functions for API response normalization
function toNumericId(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function safeStringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isNonNullable<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Normalizes the raw Affable API response into a structured format
 */
function normalizeAffableResponse(
  response: ExternalGetRecordAndFilesDetailsResponse | null | undefined,
): {
  documents: AffableDocumentItem[];
  records: AffableRecordItem[];
  totalCount: number;
} {
  const raw = (response || {}) as RawAffableResponse;
  // New API structure: Data is an array with one object containing RecordFileData and RecordList
  const dataItem =
    Array.isArray(raw?.Data) && raw.Data.length > 0 ? raw.Data[0] : null;
  const rawDocuments =
    dataItem?.RecordFileData && Array.isArray(dataItem.RecordFileData)
      ? dataItem.RecordFileData
      : [];
  const rawRecords =
    dataItem?.RecordList && Array.isArray(dataItem.RecordList)
      ? dataItem.RecordList
      : [];

  const documents: AffableDocumentItem[] = rawDocuments
    .map((doc) => {
      const documentId = toNumericId(doc?.DocumentId);
      if (documentId == null) return null;

      const folderList = Array.isArray(doc?.Folders) ? doc.Folders : [];
      const folders: AffableFolderItem[] = folderList
        .map((folder) => {
          const folderId = toNumericId(folder?.FolderId);
          if (folderId == null) return null;

          const imageList = Array.isArray(folder?.Images) ? folder.Images : [];
          const images: AffableImageItem[] = imageList
            .map((image) => {
              const imageId = toNumericId(image?.ImageId);
              if (imageId == null) return null;
              return {
                id: imageId,
                name: safeStringValue(image?.ImageName) || `File ${imageId}`,
                type: safeStringValue(image?.ImageType),
                createdAt: safeStringValue(image?.ImageCreatedDate),
              };
            })
            .filter(isNonNullable);

          const rawFolderName = safeStringValue(folder?.FolderName);
          const displayName =
            rawFolderName.toLowerCase() === "uncategorized"
              ? "Default"
              : rawFolderName || `Folder ${folderId}`;

          return {
            id: folderId,
            name: displayName,
            images,
          };
        })
        .filter(isNonNullable)
        .sort((a, b) => {
          const aIsDefault = a.name === "Default";
          const bIsDefault = b.name === "Default";
          if (aIsDefault && !bIsDefault) return -1;
          if (!aIsDefault && bIsDefault) return 1;
          return a.name.localeCompare(b.name);
        });

      return { id: documentId, folders };
    })
    .filter(isNonNullable);

  // Normalize RecordList data - preserve all fields dynamically
  const records: AffableRecordItem[] = rawRecords
    .map((record) => {
      const normalized: AffableRecordItem = {
        DocumentID: toNumericId(record?.DocumentID) ?? 0,
        DeptID: toNumericId(record?.DeptID) ?? 0,
        RowNum: toNumericId(record?.RowNum) ?? 0,
      };

      // Copy all other fields dynamically
      if (record && typeof record === "object") {
        Object.keys(record).forEach((key) => {
          if (key !== "DocumentID" && key !== "DeptID" && key !== "RowNum") {
            normalized[key] = record[key] ?? null;
          }
        });
      }

      return normalized;
    })
    .filter((r) => r.DocumentID > 0);

  const totalCount = toNumericId(raw?.Record_TotalCount) ?? documents.length;

  return { documents, records, totalCount };
}

const RiskAnalysisContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  gap: "24px",
  width: "100%",
  backgroundColor: "#ffffff",
  alignItems: "flex-start",
});

const AccordionsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: "1",
  minWidth: "0",
  width: "100%",
  transition: "width 0.3s ease-in-out",
});

const StyledAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "0 0 8px 0",
  },
  "& .accordion-title": {
    color: "#262626",
    transition: "color 0.3s ease",
    width: "100%",
    minWidth: 0,
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
  "& .accordion-title > .accordion-count-badge": {
    marginLeft: "auto",
    flexShrink: 0,
  },
  "& .accordion-title > span + .MuiBox-root": {
    marginLeft: "auto",
    flexShrink: 0,
  },
  "& .MuiAccordionSummary-root": {
    alignItems: "center",
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    flex: "1 1 auto",
    flexWrap: "nowrap",
    width: "100%",
    minWidth: 0,
    margin: "0",
  },
  "& .MuiAccordionDetails-root": { paddingTop: "0px" },
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Rethink Sans, sans-serif",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  width: "100%",
  minWidth: 0,
  flex: "1 1 auto",
}) as typeof Typography;

const AccordionTitleLeading = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  flex: "1 1 auto",
});

const AccordionCountBadge = styled(Box)({
  display: "flex",
  padding: "2px 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  borderRadius: "16px",
  border: "1px solid #086E86",
  background: "#FFF",
  color: "#086E86",
  flexShrink: 0,
});

const AccordionIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  flexShrink: 0,
});

const DescriptionText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  color: "#475569",
  lineHeight: 1.6,
  margin: 0,
  padding: "8px 12px",
  fontWeight: 400,
});

const ComplianceHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  gap: "8px",
  padding: "4px",
  background: "#fff",
  alignSelf: "stretch",
  width: "100%",
});

const UploadContainer = styled(Box)({
  // Match ContinuousCompliance upload container layout
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const UploadArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isDragOver",
})<{ $isDragOver?: boolean }>(({ $isDragOver }) => ({
  border: `1px dashed ${$isDragOver ? "#0e7490" : "#5bbfd6"}`,
  borderRadius: "8px",
  padding: "16px 8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1,
  backgroundColor: $isDragOver ? "#f0fdff" : "transparent",
  transition: "all 0.2s ease-in-out",
  cursor: $isDragOver ? "copy" : "pointer",
  "&:hover": {
    backgroundColor: "#f0fdff",
    borderColor: "#0e7490",
  },
}));

const UploadButton = styled(Button)({
  backgroundColor: "#e6fafc",
  color: "#065B70",
  border: "1px solid #5bbfd6",
  borderRadius: "8px",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: '"Rethink Sans", sans-serif',
  minHeight: "36px",
  "&:hover": {
    backgroundColor: "#d1f5f8",
    borderColor: "#5bbfd6",
  },
  "&:active": {
    backgroundColor: "#c1eff5",
  },
  "&:disabled": {
    backgroundColor: "#f3f4f6",
    color: "#9ca3af",
    border: "1px solid #d1d5db",
  },
});

const ReportDetailsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  padding: "16px",
  backgroundColor: "#f9fafb",
  borderRadius: "8px",
  overflow: "hidden",
});

const DownloadButton = styled(Button)({
  background: "linear-gradient(180deg, #0ea7c8 0%, #0a8faf 100%)",
  border: "1px solid #0b91b1",
  boxShadow: "0px 1px 2px rgba(6, 91, 112, 0.25)",
  color: "#ffffff",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 500,
  textTransform: "none",
  borderRadius: "8px",
  padding: "8px 16px",
  minHeight: "36px",
  lineHeight: 1,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: "linear-gradient(180deg, #10b0d1 0%, #0b99ba 100%)",
    borderColor: "#0a8faf",
  },
  "&:active": {
    background: "linear-gradient(180deg, #0b96b5 0%, #087f9d 100%)",
  },
  "&.Mui-disabled": {
    color: "#ffffff",
    opacity: 0.6,
  },
});

const UploadActionButton = styled(Button)({
  backgroundColor: "#0e7490",
  color: "#ffffff",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "6px",
  padding: "6px 14px",
  "&:hover": { backgroundColor: "#0b5f75" },
  "&:disabled": { opacity: 0.6 },
});

const HiddenInput = styled("input")({
  display: "none",
});

const FileDisplayContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  backgroundColor: "#f0f9ff",
  border: "1px solid #bae6fd",
  borderRadius: "8px",
  width: "calc(100% - 32px)",
});

const FileInfoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flex: 1,
  minWidth: 0,
});

const FileIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  flexShrink: 0,
  boxSizing: "border-box",
  backgroundColor: "#e6fafc",
  borderRadius: "8px",
  border: "1px solid #bae6fd",
});

const FileDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  minWidth: 0,
  flex: 1,
});

const FileName = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  color: "#0f172a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const FileSize = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "11px",
  color: "#64748b",
  textTransform: "uppercase",
});

/** Icon-only: white tile + light gray border (reference mocks) */
const DeleteButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  boxSizing: "border-box",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  flexShrink: 0,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
});

/** Secondary outlined — height/padding aligned with View Analysis */
const ReUploadButton = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "40px",
  height: "40px",
  padding: "10px 20px",
  boxSizing: "border-box",
  borderRadius: "8px",
  border: "1px solid #5bbfd6",
  backgroundColor: "#e6fafc",
  cursor: "pointer",
  color: "#065b70",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  flexShrink: 0,
  lineHeight: 1,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    backgroundColor: "#d1f5f8",
    borderColor: "#0e9aba",
  },
  "&:active": {
    backgroundColor: "#c1eff5",
  },
});

/** Primary — same gradient + shadow treatment as Potential Penalties Download XLS */
const ViewReportButton = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "10px 20px",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  color: "#ffffff",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  flexShrink: 0,
  minHeight: "40px",
  height: "40px",
  lineHeight: 1,
  boxSizing: "border-box",
  background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
  boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
  transition: "background 0.2s ease, box-shadow 0.2s ease, border 0.2s ease",
  "&:hover": {
    backgroundColor: "#065b70",
    boxShadow: "0px 4px 8px rgba(6, 91, 112, 0.3)",
    border: "1px solid #065b70",
  },
  "&:active": {
    backgroundColor: "#065b70",
    boxShadow: "0px 1px 2px rgba(6, 91, 112, 0.2)",
  },
});

const AffableFileRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: 1,
  minWidth: 0,
});

const AffableFileName = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  color: "#0f172a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const AffableFileMeta = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "11px",
  color: "#64748b",
});

// Survey-specific styled components
const SurveyCard = styled(Accordion)({
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  border: "1px solid #e0e0e0",
  borderRadius: "8px !important",
  marginBottom: "8px",
  maxWidth: "100%",
  overflow: "hidden",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: "0 0 8px 0" },
  "& .accordion-title": {
    color: "#262626",
    transition: "color 0.3s ease",
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
});

const SurveyCardSummary = styled(AccordionSummary)({
  minHeight: "45px",
  borderRadius: "4px",
  border: "1px solid #9EE5F4",
  backgroundColor: "#FFF",
  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
  padding: "0 16px",
  overflow: "hidden",
  "&.Mui-expanded": {
    minHeight: "45px",
    borderRadius: "4px",
    border: "1px solid #9EE5F4",
    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
  },
  "& .MuiAccordionSummary-content": {
    margin: "7px 0",
    minWidth: 0,
    flex: "1 1 auto",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&.Mui-expanded": {
      margin: "7px 0",
    },
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    flexShrink: 0,
  },
});

const SurveyCardTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  color: "#262626",
  flex: "1 1 auto",
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const SurveyCardDetails = styled(AccordionDetails)({
  padding: "20px 16px 16px",
  backgroundColor: "#ffffff",
});

const SurveyInfoRow = styled(Box)({
  display: "flex",
  gap: "8px",
  padding: "6px 16px",
  alignItems: "flex-start",
  borderBottom: "1px solid #f1f5f9",
});

const SurveyInfoLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  color: "#475569",
  minWidth: "120px",
  flexShrink: 0,
});

const SurveyInfoValue = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  color: "#1e293b",
  flex: 1,
});

const SurveySubSectionTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 700,
  color: "#065b70",
  marginBottom: "8px",
});

const SurveySummaryRow = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
  marginBottom: "8px",
});

const SurveySummaryLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  color: "#475569",
  minWidth: "80px",
  flexShrink: 0,
});

const SurveySummaryValue = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  color: "#1e293b",
  flex: 1,
  lineHeight: 1.6,
});

const GuidelinesAccordion = styled(Accordion)({
  boxShadow: "none",
  border: "1px solid #e2e8f0",
  borderRadius: "6px !important",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
});

const GuidelinesAccordionSummary = styled(AccordionSummary)({
  minHeight: "40px",
  padding: "0 12px",
  backgroundColor: "#f8fafc",
  borderRadius: "6px",
  "&.Mui-expanded": { borderRadius: "6px 6px 0 0", minHeight: "40px" },
  "& .MuiAccordionSummary-content.Mui-expanded": { margin: "8px 0" },
});

const GuidelinesTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  color: "#0e7490",
});

const SurveyListBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: "4px 0",
});

const SurveyListItem = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
  padding: "4px 8px",
  borderRadius: "4px",
});

const SurveyListNumber = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 700,
  color: "#065b70",
  minWidth: "20px",
  flexShrink: 0,
});

const SurveyListText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  color: "#334155",
  lineHeight: 1.6,
  flex: 1,
});

const AuthoritySection = styled(Box)({
  backgroundColor: "#f0f9ff",
  border: "1px solid #bae6fd",
  borderRadius: "8px",
  padding: "12px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

// Utility functions used in report rendering
function normalizePriority(priority: any): string {
  const value = String(priority || "").toUpperCase();
  if (["CRITICAL", "HIGH", "MEDIUM", "LOW"].includes(value)) return value;
  return value || "N/A";
}

function extractRecommendationsFromData(data: any): any[] {
  if (!data) return [];
  const table = (data?.recommendations as any)?.recommendation_table;
  if (Array.isArray(table)) return table;
  const direct = data?.recommendations;
  if (Array.isArray(direct)) return direct;
  const fallback = (data as any)?.analysis?.recommendations;
  if (Array.isArray(fallback)) return fallback;
  const items = (data?.recommendations as any)?.items;
  if (Array.isArray(items)) return items;
  return [];
}

function prettifyLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function stringifyValue(value: any): string {
  if (value == null) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function getRiskSectionLlmDebug(
  analysis: RiskAnalysisResponse | null | undefined,
  sectionKey: string,
): { prompt?: string; wallSec?: number | null } {
  return {
    prompt: analysis?.llmPromptsBySection?.[sectionKey],
    wallSec: analysis?.llmInvokeWallSecondsBySection?.[sectionKey] ?? null,
  };
}

interface RiskAnalysisProps {
  facilityData?: any;
  /** True when the Risk Analysis tab is visible. */
  riskTabActive?: boolean;
  focusUploadSection?: "incident" | "survey" | null;
  onFocusUploadSectionConsumed?: () => void;
  onWatchGuideClick?: () => void;
  watchGuideLoading?: boolean;
  watchGuideIconSrc?: string;
  showWatchGuide?: boolean;
  /** Optional slot (e.g. Payor Contract analysis) rendered as an accordion above Recently Reviewed Documents. */
  payorContractAnalysis?: React.ReactElement<PayorContractAnalysisProps>;
}

export default function RiskAnalysis({
  facilityData,
  riskTabActive = false,
  focusUploadSection = null,
  onFocusUploadSectionConsumed,
  onWatchGuideClick,
  watchGuideLoading = false,
  watchGuideIconSrc,
  showWatchGuide = true,
  payorContractAnalysis,
}: RiskAnalysisProps) {
  const getFacilityData = () => {
    let resolvedFacility = facilityData;
    if (!resolvedFacility) {
      resolvedFacility = getSelectedFacilityFromSession();
    }
    return resolvedFacility || null;
  };

  const { connectToAffableBPM: handleConnectAffableBPM, isConnectingAffable } =
    useAffableConnect({ getFacilityData, workflowType: "Risk" });

  // Resolve facility once for render-time checks (e.g., Affable connection status)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resolvedFacility = useMemo(() => getFacilityData(), [facilityData]);
  const isAffableConnected = Boolean(resolvedFacility?.is_affable_connected);

  // Recently Reviewed Documents (analysis-history) state for Risk Analysis
  const [riskHistory, setRiskHistory] = useState<any[]>([]);
  const [riskHistoryLoading, setRiskHistoryLoading] = useState(false);
  const [riskHistoryRefreshCounter, setRiskHistoryRefreshCounter] = useState(0);
  const [riskHistorySelectedAnalysis, setRiskHistorySelectedAnalysis] =
    useState<RiskAnalysisResponse | null>(null);
  const [riskHistorySelectedType, setRiskHistorySelectedType] = useState<
    "survey" | "incident" | "employee" | null
  >(null);
  const [riskHistorySelectedId, setRiskHistorySelectedId] = useState<
    number | null
  >(null);
  // Per-type expand/collapse state for Recently Reviewed section
  const [riskHistoryExpandedTypes, setRiskHistoryExpandedTypes] = useState<
    Record<string, boolean>
  >({
    "Incident report": true,
    "Survey report": true,
    "Employee agreements": true,
  });
  // Per-type pagination
  const [riskHistoryPageIncident, setRiskHistoryPageIncident] = useState(1);
  const [riskHistoryRowsPerPageIncident, setRiskHistoryRowsPerPageIncident] =
    useState(10);
  const [riskHistoryTotalIncident, setRiskHistoryTotalIncident] = useState(0);
  const [riskHistoryPageSurvey, setRiskHistoryPageSurvey] = useState(1);
  const [riskHistoryRowsPerPageSurvey, setRiskHistoryRowsPerPageSurvey] =
    useState(10);
  const [riskHistoryTotalSurvey, setRiskHistoryTotalSurvey] = useState(0);
  const [riskHistoryPageEmployee, setRiskHistoryPageEmployee] = useState(1);
  const [riskHistoryRowsPerPageEmployee, setRiskHistoryRowsPerPageEmployee] =
    useState(10);
  const [riskHistoryTotalEmployee, setRiskHistoryTotalEmployee] = useState(0);

  const [tabExpandedPanel, setTabExpandedPanel] = useState<string | false>(false);
  const riskRecentlyReviewedAutoOpenedRef = useRef(false);

  const handleTabAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setTabExpandedPanel(isExpanded ? panel : false);
    };
  const skipEmployeeAgreementHydrateRef = useRef(false);

  const [canAccessEmployeeAgreements, setCanAccessEmployeeAgreements] =
    useState(false);

  useEffect(() => {
    const sync = () =>
      setCanAccessEmployeeAgreements(readEmployeeAgreementAccessFromStorage());
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const riskPageAuditLoggedRef = React.useRef<string | null>(null);

  // Log page access when the Risk Analysis tab is visible
  useEffect(() => {
    if (!riskTabActive) {
      riskPageAuditLoggedRef.current = null;
      return;
    }
    const facilityId = resolvedFacility?.facility_id;
    if (!facilityId) return;
    const key = String(facilityId);
    if (riskPageAuditLoggedRef.current === key) return;
    riskPageAuditLoggedRef.current = key;

    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = resolvedFacility;

      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: "/api/v1/risk-analysis/",
        method: "GET",
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: "Risk Analysis",
          action: "VIEW",
        },
        action_type: "VIEW",
        action_status: "SUCCESS",
      });
    };

    void logPageAccess().catch((err) => {
      console.warn("Risk Analysis audit log (page view) failed:", err);
    });
  }, [riskTabActive, resolvedFacility]);

  // Fetch analysis history for Incident / Survey reports
  useEffect(() => {
    let cancelled = false;
    const facilityId = resolvedFacility?.facility_id;
    const userIdStr = getCurrentUserId();
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    if (!facilityId || !userId) return;

    const fetchHistory = async () => {
      setRiskHistoryLoading(true);
      try {
        const documentTypes = (
          canAccessEmployeeAgreements
            ? (["Incident report", "Survey report", "Employee agreements"] as const)
            : (["Incident report", "Survey report"] as const)
        );

        const allFirstPage =
          riskHistoryPageIncident === 1 &&
          riskHistoryPageSurvey === 1 &&
          (!canAccessEmployeeAgreements || riskHistoryPageEmployee === 1);
        const batchPageSize = canAccessEmployeeAgreements
          ? Math.max(
              riskHistoryRowsPerPageIncident,
              riskHistoryRowsPerPageSurvey,
              riskHistoryRowsPerPageEmployee,
            )
          : Math.max(
              riskHistoryRowsPerPageIncident,
              riskHistoryRowsPerPageSurvey,
            );

        type HistoryRow = Awaited<
          ReturnType<typeof continuousComplianceService.getAnalysisHistory>
        >;
        let results: { type: string; result: HistoryRow }[];

        if (allFirstPage) {
          const batch =
            await continuousComplianceService.getAnalysisHistoryBatchFirstPages({
              user_id: userId,
              facility_id: facilityId,
              types: [...documentTypes],
              page_size: batchPageSize,
            });
          if (batch.success && batch.result?.by_type) {
            const by = batch.result.by_type;
            results = documentTypes.map((type) => {
              const bucket = by[type] ?? {
                data: [],
                total: 0,
                page: 1,
                page_size: batchPageSize,
              };
              return {
                type,
                result: {
                  data: Array.isArray(bucket.data) ? bucket.data : [],
                  total: typeof bucket.total === "number" ? bucket.total : 0,
                  page: typeof bucket.page === "number" ? bucket.page : 1,
                  page_size:
                    typeof bucket.page_size === "number"
                      ? bucket.page_size
                      : batchPageSize,
                },
              };
            });
          } else {
            const historyPromises = documentTypes.map((type) => {
              let page = 1;
              let page_size = 5;
              if (type === "Incident report") {
                page = riskHistoryPageIncident;
                page_size = riskHistoryRowsPerPageIncident;
              } else if (type === "Survey report") {
                page = riskHistoryPageSurvey;
                page_size = riskHistoryRowsPerPageSurvey;
              } else if (type === "Employee agreements") {
                page = riskHistoryPageEmployee;
                page_size = riskHistoryRowsPerPageEmployee;
              }

              return continuousComplianceService
                .getAnalysisHistory({
                  user_id: userId,
                  facility_id: facilityId,
                  type,
                  page,
                  page_size,
                })
                .then((result) => ({ type, result }));
            });
            results = await Promise.all(historyPromises);
          }
        } else {
          const historyPromises = documentTypes.map((type) => {
            let page = 1;
            let page_size = 5;
            if (type === "Incident report") {
              page = riskHistoryPageIncident;
              page_size = riskHistoryRowsPerPageIncident;
            } else if (type === "Survey report") {
              page = riskHistoryPageSurvey;
              page_size = riskHistoryRowsPerPageSurvey;
            } else if (type === "Employee agreements") {
              page = riskHistoryPageEmployee;
              page_size = riskHistoryRowsPerPageEmployee;
            }

            return continuousComplianceService
              .getAnalysisHistory({
                user_id: userId,
                facility_id: facilityId,
                type,
                page,
                page_size,
              })
              .then((result) => ({ type, result }));
          });
          results = await Promise.all(historyPromises);
        }

        if (cancelled) return;

        const mergedData: any[] = [];
        results.forEach(({ type, result }) => {
          if (Array.isArray(result.data)) {
            mergedData.push(...result.data);
          }
          if (type === "Incident report") {
            setRiskHistoryTotalIncident(result.total || 0);
          } else if (type === "Survey report") {
            setRiskHistoryTotalSurvey(result.total || 0);
          } else if (type === "Employee agreements") {
            setRiskHistoryTotalEmployee(result.total || 0);
          }
        });

        setRiskHistory(mergedData);
      } catch (error) {
        console.error("Failed to fetch risk analysis history:", error);
      } finally {
        if (!cancelled) {
          setRiskHistoryLoading(false);
        }
      }
    };

    void fetchHistory();

    return () => {
      cancelled = true;
    };
  }, [
    resolvedFacility?.facility_id,
    riskHistoryPageIncident,
    riskHistoryRowsPerPageIncident,
    riskHistoryPageSurvey,
    riskHistoryRowsPerPageSurvey,
    riskHistoryPageEmployee,
    riskHistoryRowsPerPageEmployee,
    canAccessEmployeeAgreements,
    riskHistoryRefreshCounter,
  ]);

  useEffect(() => {
    if (riskHistoryLoading) {
      return;
    }
    if (riskHistory.length === 0) {
      setTabExpandedPanel((prev) =>
        prev === "recentlyReviewed" ? false : prev,
      );
      riskRecentlyReviewedAutoOpenedRef.current = false;
      return;
    }
    if (!riskRecentlyReviewedAutoOpenedRef.current) {
      riskRecentlyReviewedAutoOpenedRef.current = true;
      setTabExpandedPanel((prev) =>
        prev === false ? "recentlyReviewed" : prev,
      );
    }
  }, [riskHistoryLoading, riskHistory.length]);

  const [surveyFile, setSurveyFile] = useState<File | null>(null);
  const [incidentFile, setIncidentFile] = useState<File | null>(null);
  const [dragOverSurvey, setDragOverSurvey] = useState(false);
  const [dragOverIncident, setDragOverIncident] = useState(false);
  const [surveyAnalysis, setSurveyAnalysis] =
    useState<RiskAnalysisResponse | null>(null);
  const [incidentAnalysis, setIncidentAnalysis] =
    useState<RiskAnalysisResponse | null>(null);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [loadingIncident, setLoadingIncident] = useState(false);
  const [errorSurvey, setErrorSurvey] = useState<string | null>(null);
  const [errorIncident, setErrorIncident] = useState<string | null>(null);
  const [surveyUploadDate, setSurveyUploadDate] = useState<string | null>(null);
  const [incidentUploadDate, setIncidentUploadDate] = useState<string | null>(
    null,
  );
  const [showSurveyReport, setShowSurveyReport] = useState(false);
  const [showIncidentReport, setShowIncidentReport] = useState(false);
  useEffect(() => {
    if (!focusUploadSection) return;
    setTabExpandedPanel(focusUploadSection);
    requestAnimationFrame(() => {
      document
        .getElementById(`${focusUploadSection}-header`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    onFocusUploadSectionConsumed?.();
  }, [focusUploadSection, onFocusUploadSectionConsumed]);

  const [employeeAgreementFiles, setEmployeeAgreementFiles] = useState<
    File[]
  >([]);
  const [dragOverEmployeeAgreement, setDragOverEmployeeAgreement] =
    useState(false);
  const [loadingEmployeeAgreement, setLoadingEmployeeAgreement] =
    useState(false);
  const [errorEmployeeAgreement, setErrorEmployeeAgreement] = useState<
    string | null
  >(null);
  const [employeeAgreementResult, setEmployeeAgreementResult] =
    useState<EmployeeAgreementAnalysisResponse | null>(null);
  const [employeeAgreementType, setEmployeeAgreementType] =
    useState<string>("physician");
  const [employeeAgreementErrorFileResults, setEmployeeAgreementErrorFileResults] =
    useState<EmployeeAgreementFileResult[] | null>(null);
  /** Per-file extraction rows streamed before the full response (SSE). */
  const [employeeAgreementPartialFileResults, setEmployeeAgreementPartialFileResults] =
    useState<EmployeeAgreementFileResult[] | null>(null);
  const [employeeAgreementExtractingFilenames, setEmployeeAgreementExtractingFilenames] =
    useState<Set<string>>(() => new Set());
  const [employeeAgreementPdfHintByFilename, setEmployeeAgreementPdfHintByFilename] =
    useState<Record<string, boolean>>({});
  const [employeeAgreementFilePageProgress, setEmployeeAgreementFilePageProgress] =
    useState<Record<string, { page: number; total: number }>>({});
  const employeeAgreementPageProgRef = useRef<{
    pending: Record<string, { page: number; total: number }>;
    timer: ReturnType<typeof setTimeout> | null;
    lastEmit: number;
  }>({ pending: {}, timer: null, lastEmit: 0 });

  const scheduleEmployeeAgreementPageProgress = useCallback(
    (ev: { filename: string; page: number; totalPages: number }) => {
      const refBox = employeeAgreementPageProgRef.current;
      refBox.pending[ev.filename] = { page: ev.page, total: ev.totalPages };
      const throttleMs = 400;
      const now = Date.now();
      const flush = () => {
        if (!Object.keys(refBox.pending).length) return;
        const snap = { ...refBox.pending };
        refBox.pending = {};
        refBox.lastEmit = Date.now();
        setEmployeeAgreementFilePageProgress((prev) => ({ ...prev, ...snap }));
      };
      if (now - refBox.lastEmit >= throttleMs) {
        if (refBox.timer) {
          clearTimeout(refBox.timer);
          refBox.timer = null;
        }
        flush();
        return;
      }
      if (!refBox.timer) {
        refBox.timer = setTimeout(() => {
          refBox.timer = null;
          flush();
        }, Math.max(40, throttleMs - (now - refBox.lastEmit)));
      }
    },
    [],
  );

  const showEmployeeAgreementTabbedOutput =
    loadingEmployeeAgreement ||
    Boolean(
      employeeAgreementResult?.success && employeeAgreementResult.analysis,
    ) ||
    Boolean(
      retainExtractionFileResults(
        employeeAgreementErrorFileResults,
        employeeAgreementPartialFileResults,
      ),
    );

  const employeeAgreementStatusFileResults = useMemo(() => {
    if (loadingEmployeeAgreement && employeeAgreementFiles.length > 0) {
      return buildMergedExtractionFileResults(
        employeeAgreementFiles,
        employeeAgreementExtractingFilenames,
        employeeAgreementPartialFileResults,
        {
          pdfHintByFilename: employeeAgreementPdfHintByFilename,
          pageByFilename: employeeAgreementFilePageProgress,
        },
      );
    }
    if (loadingEmployeeAgreement) return null;
    return (
      employeeAgreementResult?.metadata?.file_results ??
      retainExtractionFileResults(
        employeeAgreementErrorFileResults,
        employeeAgreementPartialFileResults,
      )
    );
  }, [
    loadingEmployeeAgreement,
    employeeAgreementFiles,
    employeeAgreementExtractingFilenames,
    employeeAgreementPartialFileResults,
    employeeAgreementPdfHintByFilename,
    employeeAgreementFilePageProgress,
    employeeAgreementResult?.metadata?.file_results,
    employeeAgreementErrorFileResults,
  ]);

  const clearEmployeeAgreementAnalysis = useCallback(() => {
    skipEmployeeAgreementHydrateRef.current = true;
    setEmployeeAgreementFiles([]);
    setEmployeeAgreementResult(null);
    setErrorEmployeeAgreement(null);
    setEmployeeAgreementErrorFileResults(null);
    setEmployeeAgreementPartialFileResults(null);
    setEmployeeAgreementExtractingFilenames(new Set());
    setEmployeeAgreementPdfHintByFilename({});
    setEmployeeAgreementFilePageProgress({});
    employeeAgreementPageProgRef.current = {
      pending: {},
      timer: null,
      lastEmit: 0,
    };
  }, []);

  useEffect(() => {
    if (riskHistoryLoading || !canAccessEmployeeAgreements) return;
    if (loadingEmployeeAgreement) return;
    if (employeeAgreementResult !== null) return;
    if (skipEmployeeAgreementHydrateRef.current) return;

    const rows = riskHistory.filter(
      (item: { type?: string; data_obj?: { analysis?: unknown } }) =>
        item?.type === "Employee agreements" && item?.data_obj?.analysis,
    );
    if (!rows.length) return;

    type EmpRow = {
      created_at?: string;
      data_obj?: {
        analysis?: EmployeeAgreementAnalysisResponse["analysis"];
        metadata?: EmployeeAgreementAnalysisResponse["metadata"];
      };
    };
    const empRows = rows as EmpRow[];
    let latest = empRows[0];
    for (let i = 1; i < empRows.length; i += 1) {
      const row = empRows[i];
      const t = row.created_at ? new Date(row.created_at).getTime() : 0;
      const bt = latest.created_at
        ? new Date(latest.created_at).getTime()
        : 0;
      if (t >= bt) latest = row;
    }

    const analysis = latest?.data_obj?.analysis;
    if (!analysis) return;

    const meta = latest.data_obj?.metadata || {};
    setEmployeeAgreementResult({
      success: true,
      analysis,
      metadata: meta,
    });
    setEmployeeAgreementFiles([]);
    const et = meta.employee_type;
    if (typeof et === "string" && et.trim()) {
      setEmployeeAgreementType(et.trim().toLowerCase());
    }
  }, [
    riskHistory,
    riskHistoryLoading,
    canAccessEmployeeAgreements,
    loadingEmployeeAgreement,
    employeeAgreementResult,
  ]);

  const notifyRiskHistorySave = useCallback(
    (
      outcome: Awaited<
        ReturnType<typeof continuousComplianceService.saveAnalysisHistory>
      >,
      label: string,
    ) => {
      if (outcome.ok) return;
      if (outcome.skipped) {
        toast.warning(outcome.errorMessage);
      } else {
        toast.error(
          `${label} could not be saved to Recently Reviewed: ${outcome.errorMessage}`,
        );
      }
    },
    [],
  );

  // State for SOM search results for survey tags
  const [tagDetails, setTagDetails] = useState<Record<string, any>>({});
  const [loadingTags, setLoadingTags] = useState<Record<string, boolean>>({});

  // AffableFLOW integration states
  const [isSyncingAffableRecords, setIsSyncingAffableRecords] = useState(false);
  const [currentWorkflowType, setCurrentWorkflowType] =
    useState<string>("Risk");
  const [affableSyncError, setAffableSyncError] = useState<string | null>(null);
  const [affableDocuments, setAffableDocuments] = useState<
    AffableDocumentItem[]
  >([]);
  const [affableRecords, setAffableRecords] = useState<AffableRecordItem[]>([]);
  const [selectedAffableDocumentId, setSelectedAffableDocumentId] = useState<
    number | null
  >(null);
  const [selectedAffableFolderId, setSelectedAffableFolderId] = useState<
    number | null
  >(null);
  const [affableRecordCount, setAffableRecordCount] = useState(0);
  const [selectedAffableFileKey, setSelectedAffableFileKey] = useState<
    string | null
  >(null);
  const [loadingAffableFile, setLoadingAffableFile] = useState(false);
  const [errorAffableFile, setErrorAffableFile] = useState<string | null>(null);
  const [isAffablePanelClosed, setIsAffablePanelClosed] = useState(false);
  const [affableFileAnalysis, setAffableFileAnalysis] =
    useState<RiskAnalysisResponse | null>(null);
  const [affableFile, setAffableFile] = useState<File | null>(null);
  const [affableFileUploadDate, setAffableFileUploadDate] = useState<
    string | null
  >(null);
  const [showAffableFileReport, setShowAffableFileReport] = useState(false);
  const [affableSourceAccordion, setAffableSourceAccordion] = useState<
    "survey" | "incident" | null
  >(null);
  const [affableDocsPage, setAffableDocsPage] = useState(1);
  const [affableRowsPerPage, setAffableRowsPerPage] = useState(10);
  const [affableTableColumns, setAffableTableColumns] = useState<string[]>([]);
  const [affableUploadContext, setAffableUploadContext] = useState<{
    documentId: number;
    folderId: number;
    orgName: string;
    deptId?: number;
    orgId?: number;
  } | null>(null);
  const [isUploadingAffableResult, setIsUploadingAffableResult] =
    useState(false);
  const [, setAffableResultUploadError] = useState<
    string | null
  >(null);
  const [, setAffableResultUploadSuccess] = useState<
    string | null
  >(null);

  // Clear all analysis state when facility changes
  const prevFacilityIdRef = useRef<number | undefined>(
    resolvedFacility?.facility_id,
  );
  useEffect(() => {
    const currentId = resolvedFacility?.facility_id;
    if (
      prevFacilityIdRef.current !== undefined &&
      prevFacilityIdRef.current !== currentId
    ) {
      // Facility changed — reset uploaded files, analysis results, and reports
      setSurveyFile(null);
      setSurveyAnalysis(null);
      setSurveyUploadDate(null);
      setErrorSurvey(null);
      setShowSurveyReport(false);

      setIncidentFile(null);
      setIncidentAnalysis(null);
      setIncidentUploadDate(null);
      setErrorIncident(null);
      setShowIncidentReport(false);

      setEmployeeAgreementFiles([]);
      setEmployeeAgreementResult(null);
      setEmployeeAgreementErrorFileResults(null);
      setErrorEmployeeAgreement(null);
      setDragOverEmployeeAgreement(false);

      // Reset SOM tag details
      setTagDetails({});
      setLoadingTags({});

      // Reset AffableBPM state
      setAffableDocuments([]);
      setAffableRecords([]);
      setAffableRecordCount(0);
      setAffableSyncError(null);
      setAffableFile(null);
      setAffableFileUploadDate(null);
      setAffableFileAnalysis(null);
      setErrorAffableFile(null);
      setShowAffableFileReport(false);
      setAffableResultUploadError(null);
      setAffableResultUploadSuccess(null);

      // Reset history selection
      setRiskHistorySelectedAnalysis(null);
      setRiskHistorySelectedType(null);
      setRiskHistorySelectedId(null);

      riskRecentlyReviewedAutoOpenedRef.current = false;
      skipEmployeeAgreementHydrateRef.current = false;
      setTabExpandedPanel((prev) =>
        prev === "recentlyReviewed" ? false : prev,
      );
    }
    prevFacilityIdRef.current = currentId;
  }, [resolvedFacility?.facility_id]);

  // useEffect hooks for managing Affable document/folder selection
  useEffect(() => {
    if (!affableDocuments.length) {
      setSelectedAffableDocumentId(null);
      setSelectedAffableFolderId(null);
      return;
    }

    setSelectedAffableDocumentId((prevDocId) => {
      const nextDoc =
        prevDocId !== null &&
        affableDocuments.some((document) => document.id === prevDocId)
          ? affableDocuments.find((document) => document.id === prevDocId)
          : affableDocuments[0];

      setSelectedAffableFolderId(
        nextDoc && nextDoc.folders?.length ? nextDoc.folders[0].id : null,
      );

      return nextDoc ? nextDoc.id : null;
    });
  }, [affableDocuments]);

  useEffect(() => {
    if (!selectedAffableDocumentId) {
      setSelectedAffableFolderId(null);
      return;
    }

    const currentDoc = affableDocuments.find(
      (document) => document.id === selectedAffableDocumentId,
    );
    if (!currentDoc) {
      setSelectedAffableFolderId(null);
      return;
    }

    setSelectedAffableFolderId((prevFolderId) => {
      const stillExists =
        prevFolderId !== null &&
        currentDoc.folders?.some((folder: any) => folder.id === prevFolderId);

      const nextFolderId = stillExists
        ? prevFolderId
        : currentDoc.folders?.length
          ? currentDoc.folders[0].id
          : null;

      return nextFolderId;
    });
  }, [affableDocuments, selectedAffableDocumentId]);

  // Clear any selected file when document changes
  useEffect(() => {
    setSelectedAffableFileKey(null);
  }, [selectedAffableDocumentId]);

  // No modal for guidelines; inline accordion used instead

  const handleDeleteFile = (type: "survey" | "incident") => {
    if (type === "survey") {
      setSurveyFile(null);
      setSurveyAnalysis(null);
      setSurveyUploadDate(null);
      setErrorSurvey(null);
      setShowSurveyReport(false);
    } else {
      setIncidentFile(null);
      setIncidentAnalysis(null);
      setIncidentUploadDate(null);
      setErrorIncident(null);
      setShowIncidentReport(false);
    }
  };

  const handleFileUpload = async (file: File, type: "survey" | "incident") => {
    console.log("🔄 handleFileUpload called - Type:", type, "File:", file.name);

    if (type === "survey") {
      console.log("📤 Survey upload started");
      const sizeError = validateRiskAnalysisUploadSize(file);
      if (sizeError) {
        setSurveyFile(file);
        setSurveyUploadDate(formatClientUploadTimestamp(new Date()));
        setErrorSurvey(sizeError);
        return;
      }

      setSurveyFile(file);
      setSurveyUploadDate(formatClientUploadTimestamp(new Date()));
      setLoadingSurvey(true);
      setErrorSurvey(null);
      setSurveyAnalysis(null);

      try {
        // Get facility ID
        const currentFacilityData = getFacilityData();
        const facilityId = currentFacilityData?.facility_id?.toString();
        if (!facilityId) {
          throw new Error(
            "Facility ID is required for survey report analysis.",
          );
        }

        console.log(
          "📡 Calling uploadSurveyDocument with facilityId:",
          facilityId,
        );

        // Call uploadSurveyDocument (survey analysis endpoint)
        const surveyResponse = await surveyReportService.uploadSurveyDocument({
          file,
          facilityId,
          documentType: "survey_report",
        });

        console.log("✅ Upload successful:", surveyResponse);

        // Save analysis history for Survey report, storing the survey payload
        {
          const userIdStr = getCurrentUserId();
          const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
          const facilityNumericId =
            Number(currentFacilityData?.facility_id) || 0;

          if (userId && facilityNumericId) {
            const surveyHistoryPayload: any = {
              ...(surveyResponse as any),
              metadata: {
                ...(surveyResponse as any)?.metadata,
                filename: file.name,
                file_size: file.size,
                document_type: "survey_report",
              },
            };

            const outcome = await continuousComplianceService.saveAnalysisHistory(
              {
                user_id: userId,
                facility_id: facilityNumericId,
                type: "Survey report",
                data_obj: surveyHistoryPayload,
              },
            );
            notifyRiskHistorySave(outcome, "Survey report");
            if (outcome.ok) {
              setRiskHistoryRefreshCounter((c) => c + 1);
            }
          } else {
            toast.warning(
              "Survey analysis completed but was not saved to Recently Reviewed (missing user or facility).",
            );
          }
        }

        // For UI, map the survey response into RiskAnalysisResponse shape
        const surveyData: any = surveyResponse;
        const full = surveyData.full_analysis || {};
        const responseTags =
          surveyData.deficiency_descriptions || full.deficiency_tags || [];
        const facilityType =
          surveyData.facility_type ||
          full.facility_type?.detected_type ||
          currentFacilityData?.facility_type_slug ||
          currentFacilityData?.facility_type ||
          "Unknown";

        const mappedAnalysis: RiskAnalysisResponse = {
          success: true,
          message: "Survey analysis completed",
          analysis: {
            document_type: full.document_type || "healthcare_facility_survey",
            analysis_date:
              full.analysis_date ||
              surveyData.survey_date ||
              new Date().toISOString().slice(0, 10),
            facility_info: {
              industry: "Healthcare",
              facility_type: facilityType,
            },
            incident_summary: {
              narrative:
                full.survey_summary?.key_findings ||
                "Survey report uploaded for facility",
              incident_type: "Survey",
              clinical_classification: "N/A",
              date:
                surveyData.survey_date ||
                full.survey_metadata?.survey_date ||
                new Date().toISOString().slice(0, 10),
              location: full.survey_metadata?.facility_name || "",
              description: "",
              parties_involved: [],
              injuries_damages: "",
              environmental_factors: "",
              reporting_timeliness: "",
              documentation_completeness: "",
            },
            compliance_deficiencies: [],
            potential_penalties: {
              penalty_table: [],
              penalty_charts: {
                estimated_penalty_exposure: {
                  min: "0",
                  max: "0",
                  most_likely: "0",
                },
                estimated_corrective_investment: {
                  immediate: "0",
                  long_term: "0",
                  total: "0",
                },
              },
            },
            plan_of_correction: {
              action_table: [],
              documentation_improvements: [],
              training_requirements: [],
              system_improvements: [],
              corrective_costs: {
                immediate_costs: "0",
                long_term_costs: "0",
                total_estimated_cost: "0",
              },
              monitoring_tracking: [],
            },
            risk_score: {
              overall_score: 0,
              score_breakdown: {
                documentation: 0,
                compliance: 0,
                safety: 0,
                financial_risk: 0,
              },
              risk_level: "LOW",
              issues_count: Array.isArray(responseTags)
                ? responseTags.length
                : 0,
              actions_count: 0,
              justification: "Survey report uploaded",
            },
            executive_summary: full.survey_summary?.key_findings
              ? `Survey summary: ${full.survey_summary.key_findings}`
              : `Survey report uploaded with ${
                  Array.isArray(responseTags) ? responseTags.length : 0
                } deficiency tag(s)`,
            recommendations: { recommendation_table: [] },
          },
          metadata: {
            filename: file.name,
            file_size: file.size,
            document_type: "survey_report",
            analysis_timestamp: new Date().toISOString(),
          },
        };

        (mappedAnalysis as any).analysis.survey_metadata =
          full.survey_metadata || {
            surveying_body:
              full.survey_metadata?.surveying_body ||
              "State Health Department (on behalf of CMS)",
            survey_date:
              surveyData.survey_date ||
              full.survey_metadata?.survey_date ||
              new Date().toISOString().slice(0, 10),
            facility_name: full.survey_metadata?.facility_name || "",
            survey_type: full.survey_metadata?.survey_type || "Standard Survey",
          };

        (mappedAnalysis as any).analysis.deficiency_tags = (
          full.deficiency_tags || []
        ).map((item: any) => ({
          tag: item.tag || "",
          description: item.description || "",
          severity: item.severity || "MEDIUM",
          category: item.category || "",
          page_reference: item.page_reference || "",
        }));

        if (surveyData.llmPromptsBySection) {
          mappedAnalysis.llmPromptsBySection = surveyData.llmPromptsBySection;
        }
        if (surveyData.llmInvokeWallSecondsBySection) {
          mappedAnalysis.llmInvokeWallSecondsBySection =
            surveyData.llmInvokeWallSecondsBySection;
        }

        setSurveyAnalysis(mappedAnalysis);

        // Automatically call SOM search for each deficiency tag
        const deficiencyTags =
          (mappedAnalysis as any)?.analysis?.deficiency_tags || [];
        if (Array.isArray(deficiencyTags) && deficiencyTags.length > 0) {
          console.log(
            "🔍 Auto-calling SOM search for deficiency tags:",
            deficiencyTags.map((d: any) => d.tag),
          );

          // Extract tags and call SOM search for each
          const tags = deficiencyTags.map((d: any) => d.tag).filter(Boolean);
          if (tags.length > 0) {
            try {
              // Call SOM search for each tag
              const somPromises = tags.map(async (tag: string) => {
                try {
                  const somResponse = await riskAnalysisService.somSearch({
                    query: tag,
                    category:
                      "VectorStores_usa_healthcare_manual_som107ap_g_rhc",
                    customer_id: "1",
                    org_name: "test",
                    user_persona: "survey_analyst",
                  });

                  // Store the SOM data for this tag
                  setTagDetails((prev) => ({
                    ...prev,
                    [tag]: somResponse,
                  }));

                  console.log(`✅ SOM search completed for tag: ${tag}`);
                  return { tag, data: somResponse };
                } catch (error) {
                  console.error(`❌ SOM search failed for tag ${tag}:`, error);
                  // Check if it's an authentication error
                  if (
                    error instanceof Error &&
                    error.message.includes("Not authenticated")
                  ) {
                    // Redirect to login page
                    window.location.href = "/login";
                    return { tag, data: null };
                  }
                  return { tag, data: null };
                }
              });

              // Wait for all SOM searches to complete
              const somResults = await Promise.all(somPromises);
              console.log(
                "🎯 All SOM searches completed:",
                somResults.map((r) => r.tag),
              );
            } catch (error) {
              console.error("❌ Error in automatic SOM search:", error);
              // Check if it's an authentication error
              if (
                error instanceof Error &&
                error.message.includes("Not authenticated")
              ) {
                // Redirect to login page
                window.location.href = "/login";
              }
            }
          }
        }

        //setActiveReport("survey");
      } catch (error) {
        console.error("Survey analysis failed:", error);

        // Check if it's an authentication error
        if (
          error instanceof Error &&
          error.message.includes("Not authenticated")
        ) {
          setErrorSurvey("Session expired. Please sign in again.");
          // Redirect to login page after a short delay
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        } else {
          setErrorSurvey(
            error instanceof Error ? error.message : "Analysis failed",
          );
        }
      } finally {
        setLoadingSurvey(false);
      }
    } else {
      const sizeError = validateRiskAnalysisUploadSize(file);
      if (sizeError) {
        setIncidentFile(file);
        setIncidentUploadDate(formatClientUploadTimestamp(new Date()));
        setErrorIncident(sizeError);
        return;
      }

      setIncidentFile(file);
      setIncidentUploadDate(formatClientUploadTimestamp(new Date()));
      setLoadingIncident(true);
      setErrorIncident(null);
      setIncidentAnalysis(null);

      try {
        const currentFacilityData = getFacilityData();
        const facilityId = currentFacilityData?.facility_id?.toString();
        if (!facilityId) {
          throw new Error(
            "Facility ID is required for incident report analysis.",
          );
        }

        const analysis = await riskAnalysisService.analyzeDocument({
          file,
          document_type: "incident_report",
          industry: "healthcare",
          analysis_type: "comprehensive",
          facility_id: facilityId,
        });

        // Save analysis history for Incident report
        {
          const userIdStr = getCurrentUserId();
          const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
          const facilityNumericId =
            Number(currentFacilityData?.facility_id) || 0;

          if (userId && facilityNumericId) {
            const outcome = await continuousComplianceService.saveAnalysisHistory(
              {
                user_id: userId,
                facility_id: facilityNumericId,
                type: "Incident report",
                data_obj: {
                  analysis: (analysis as any).analysis || analysis,
                  metadata: (analysis as any).metadata || {
                    filename: file.name,
                    file_size: file.size,
                    document_type: "incident_report",
                  },
                },
              },
            );
            notifyRiskHistorySave(outcome, "Incident report");
            if (outcome.ok) {
              setRiskHistoryRefreshCounter((c) => c + 1);
            }
          } else {
            toast.warning(
              "Incident analysis completed but was not saved to Recently Reviewed (missing user or facility).",
            );
          }
        }

        setIncidentAnalysis(analysis);
        //setActiveReport("incident");
      } catch (error) {
        console.error("Incident analysis failed:", error);

        // Check if it's an authentication error
        if (
          error instanceof Error &&
          error.message.includes("Not authenticated")
        ) {
          setErrorIncident("Session expired. Please sign in again.");
          // Redirect to login page after a short delay
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        } else {
          setErrorIncident(
            error instanceof Error ? error.message : "Analysis failed",
          );
        }
      } finally {
        setLoadingIncident(false);
      }
    }
  };

  const handleEmployeeAgreementUpload = async (files: File[]) => {
    const picked = filterEmployeeAgreementFiles(files);
    if (!picked.length) {
      setErrorEmployeeAgreement(
        "No supported files (.pdf, .docx, .doc, .txt) in the selection.",
      );
      return;
    }
    skipEmployeeAgreementHydrateRef.current = false;
    const sorted = sortMultifileAnalysisUploadsByName(picked);
    setEmployeeAgreementFiles(sorted);
    setLoadingEmployeeAgreement(true);
    setErrorEmployeeAgreement(null);
    setEmployeeAgreementErrorFileResults(null);
    setEmployeeAgreementPartialFileResults([]);
    setEmployeeAgreementExtractingFilenames(new Set());
    setEmployeeAgreementPdfHintByFilename({});
    setEmployeeAgreementFilePageProgress({});
    employeeAgreementPageProgRef.current = {
      pending: {},
      timer: null,
      lastEmit: 0,
    };
    setEmployeeAgreementResult(null);
    try {
      const currentFacilityData = getFacilityData();
      const facilityId = currentFacilityData?.facility_id?.toString();
      if (!facilityId) {
        throw new Error("Please select a facility before analysis.");
      }
      const streamResult = await riskAnalysisService.analyzeEmployeeAgreementStream(
        sorted,
        facilityId,
        employeeAgreementType,
        {
          onFileExtracting: (fn, meta) => {
            setEmployeeAgreementExtractingFilenames((prev) => new Set(prev).add(fn));
            setEmployeeAgreementPdfHintByFilename((prev) => ({
              ...prev,
              [fn]: Boolean(
                meta?.pdf ?? fn.toLowerCase().endsWith(".pdf"),
              ),
            }));
            setEmployeeAgreementFilePageProgress((prev) => {
              const next = { ...prev };
              delete next[fn];
              return next;
            });
          },
          onFilePageProgress: (ev) => {
            scheduleEmployeeAgreementPageProgress(ev);
          },
          onFileResult: (fr) => {
            setEmployeeAgreementExtractingFilenames((prev) => {
              const next = new Set(prev);
              next.delete(fr.filename);
              return next;
            });
            setEmployeeAgreementPartialFileResults((prev) => {
              const base = prev ?? [];
              const idx = base.findIndex((x) => x.filename === fr.filename);
              if (idx >= 0) {
                const next = [...base];
                next[idx] = fr;
                return next;
              }
              return [...base, fr];
            });
          },
          onComplete: (result) => {
            setEmployeeAgreementExtractingFilenames(new Set());
            setEmployeeAgreementResult(result);
            setEmployeeAgreementErrorFileResults(null);
            setEmployeeAgreementPartialFileResults(null);
          },
          onError: (message, fileResults) => {
            setEmployeeAgreementExtractingFilenames(new Set());
            setErrorEmployeeAgreement(message);
            if (fileResults && fileResults.length) {
              setEmployeeAgreementErrorFileResults(fileResults);
              setEmployeeAgreementPartialFileResults(null);
            }
          },
        },
      );
      if (streamResult) {
        const userIdStr = getCurrentUserId();
        const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
        const facilityNumericId = Number(currentFacilityData?.facility_id) || 0;
        if (userId && facilityNumericId) {
          const totalBytes = sorted.reduce((s, f) => s + f.size, 0);
          const outcome = await continuousComplianceService.saveAnalysisHistory({
            user_id: userId,
            facility_id: facilityNumericId,
            type: "Employee agreements",
            data_obj: {
              analysis: streamResult.analysis,
              metadata: {
                ...metadataForEmployeeAgreementHistory(streamResult.metadata),
                filename: sorted.map((f) => f.name).join("; "),
                filenames: sorted.map((f) => f.name),
                file_count: sorted.length,
                file_size: totalBytes,
              },
            },
          });
          notifyRiskHistorySave(outcome, "Employee agreement analysis");
          if (outcome.ok) {
            setRiskHistoryRefreshCounter((c) => c + 1);
          }
        } else {
          toast.warning(
            "Employee agreement analysis completed but was not saved to Recently Reviewed (missing user or facility).",
          );
        }
      }
    } catch (err) {
      setEmployeeAgreementExtractingFilenames(new Set());
      if (err instanceof EmployeeAgreementRequestError) {
        setErrorEmployeeAgreement(err.message);
        if (err.fileResults && err.fileResults.length) {
          setEmployeeAgreementErrorFileResults(err.fileResults);
          setEmployeeAgreementPartialFileResults(null);
        }
      } else {
        setErrorEmployeeAgreement(
          err instanceof Error ? err.message : "Analysis failed",
        );
      }
    } finally {
      setEmployeeAgreementExtractingFilenames(new Set());
      setLoadingEmployeeAgreement(false);
    }
  };

  const handleDropEmployeeAgreement = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const raw = Array.from(e.dataTransfer.files || []);
    if (raw.length > 0) {
      void handleEmployeeAgreementUpload(raw);
    }
    setDragOverEmployeeAgreement(false);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    type: "survey" | "incident",
  ) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0], type);
    }
    if (type === "survey") {
      setDragOverSurvey(false);
    } else {
      setDragOverIncident(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement>,
    type: "survey" | "incident",
  ) => {
    e.preventDefault();
    if (type === "survey") {
      setDragOverSurvey(true);
    } else {
      setDragOverIncident(true);
    }
  };

  const handleDragLeave = (
    e: React.DragEvent<HTMLDivElement>,
    type: "survey" | "incident",
  ) => {
    e.preventDefault();
    if (type === "survey") {
      setDragOverSurvey(false);
    } else {
      setDragOverIncident(false);
    }
  };

  const handleAffableRecordsSync = async (
    workflowType: string = "Risk",
    pageNumber = 0,
    rowsPerPage?: number,
  ) => {
    if (isSyncingAffableRecords) return;

    const facilityName = (resolvedFacility as any)?.org_name;
    if (!facilityName) {
      setAffableSyncError(
        "Facility name unavailable. Please select a facility first.",
      );
      toast.error("Facility name unavailable. Please select a facility first.");
      return;
    }

    const orgName = (resolvedFacility as any)?.org_name || facilityName;
    const perPage = rowsPerPage || affableRowsPerPage;

    setIsSyncingAffableRecords(true);
    setAffableSyncError(null);
    setIsAffablePanelClosed(false);
    setAffableDocuments([]);
    setAffableRecords([]);
    setAffableTableColumns([]);
    setAffableRecordCount(0);
    setSelectedAffableDocumentId(null);
    setSelectedAffableFolderId(null);
    setSelectedAffableFileKey(null);
    setAffableFileAnalysis(null);
    setAffableFile(null);
    setAffableFileUploadDate(null);
    setShowAffableFileReport(false);

    setCurrentWorkflowType(workflowType);
    // Set the source accordion type based on workflow type
    setAffableSourceAccordion(
      workflowType === "Survey" ? "survey" : "incident",
    );
    try {
      // Use the provided workflow type (Risk or Survey)
      const mapping =
        await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
          orgName,
          type: workflowType,
        });

      let resolvedDeptId: number | undefined;
      if (
        mapping?.success &&
        Array.isArray(mapping.data) &&
        mapping.data.length > 0
      ) {
        const first = mapping.data[0];
        if (
          typeof first?.workflowId === "number" &&
          Number.isFinite(first.workflowId)
        ) {
          resolvedDeptId = first.workflowId;
        }
      }

      const response =
        await affablebpmIntegrationService.getRecordAndFilesDetails({
          orgName,
          deptId: resolvedDeptId,
          perPage,
          pageNumber,
        });

      // Surface backend error messages
      const rawResponse = (response || {}) as RawAffableResponse;
      if (
        typeof rawResponse.Status === "string" &&
        rawResponse.Status.toLowerCase() === "failed"
      ) {
        const backendMessage =
          rawResponse.ErrorMsg ||
          rawResponse.Message ||
          "Failed to fetch records from AffableBPM.";

        setAffableDocuments([]);
        setAffableRecords([]);
        setAffableRecordCount(0);
        setAffableTableColumns([]);
        setIsAffablePanelClosed(false);
        setAffableSyncError(backendMessage);
        //toast.error(backendMessage);
        return;
      }

      // Normalize the API response
      const { documents, records, totalCount } =
        normalizeAffableResponse(response);

      // Extract table columns from the first record, excluding internal IDs
      // Always show only the first dynamic column (after excluding fixed columns)
      const allColumns = records.length > 0 ? Object.keys(records[0]) : [];
      const filteredColumns = allColumns.filter(
        (col) => col !== "DocumentID" && col !== "DeptID" && col !== "RowNum",
      );
      // Take only the first dynamic column
      const columns = filteredColumns.length > 0 ? [filteredColumns[0]] : [];

      setAffableDocuments(documents);
      setAffableRecords(records);
      setAffableRecordCount(totalCount);
      setAffableTableColumns(columns);
      setIsAffablePanelClosed(false); // Show panel when sync succeeds

      if (!documents.length) {
        setAffableSyncError(
          "No folders returned from AffableBPM for this facility.",
        );
      }
    } catch (error) {
      console.error("Failed to sync AffableFLOW documents:", error);
      const errorMsg =
        error instanceof Error
          ? error.message || "Failed to sync with AffableFLOW."
          : "Failed to sync with AffableFLOW.";
      setAffableSyncError(errorMsg);
      toast.error(errorMsg);
      setAffableDocuments([]);
      setAffableRecords([]);
      setAffableRecordCount(0);
      setAffableTableColumns([]);
    } finally {
      setIsSyncingAffableRecords(false);
    }
  };

  // Helper functions for Affable file selection
  const getAffableFileKey = (folderId: number, imageId: number) =>
    `${folderId}-${imageId}`;

  const isAffableFileSelected = (folderId: number, imageId: number) =>
    selectedAffableFileKey === getAffableFileKey(folderId, imageId);

  const selectAffableFile = (folderId: number, imageId: number) => {
    const key = getAffableFileKey(folderId, imageId);
    setSelectedAffableFileKey((prev) => (prev === key ? null : key));
  };

  /**
   * Converts a base64 string to a File object
   */
  const base64ToFile = (
    base64: string,
    filename: string,
    mimeType: string,
  ): File => {
    // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
    const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create Blob and then File
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  };

  /**
   * Runs risk analysis for a given file from AffableBPM
   */
  const analyzeAffableFileForRisk = async (
    file: File,
    sourceType: "survey" | "incident" = "incident",
  ) => {
    setAffableFile(file);
    setAffableFileUploadDate(formatClientUploadTimestamp(new Date()));
    setShowAffableFileReport(false);
    setAffableFileAnalysis(null);
    setErrorAffableFile(null);

    try {
      const currentFacilityData = getFacilityData();
      const facilityId = currentFacilityData?.facility_id?.toString();
      if (!facilityId) {
        throw new Error("Facility ID is required for AffableFLOW analysis.");
      }

      let analysis: RiskAnalysisResponse;

      // Debug logging
      console.log("🔍 analyzeAffableFileForRisk - sourceType:", sourceType);
      console.log("🔍 affableSourceAccordion state:", affableSourceAccordion);

      // Call appropriate API based on source accordion type
      if (sourceType === "survey") {
        // Call survey analysis endpoint
        console.log(
          "📊 Calling SURVEY analysis API: /api/v1/risk-analysis/analyze-survey/async",
        );
        console.log("📊 Facility ID:", facilityId);
        analysis = await riskAnalysisService.analyzeSurvey(file, facilityId);
      } else {
        // Call incident report analysis endpoint (default behavior)
        console.log(
          "📊 Calling INCIDENT analysis API: /api/v1/risk-analysis/analyze-document/async",
        );
        analysis = await riskAnalysisService.analyzeDocument({
          file,
          document_type: "incident_report",
          industry: "Healthcare",
          analysis_type: "risk_analysis",
          facility_id: facilityId,
        });
      }

      // Save analysis history for AffableFLOW-based reports
      {
        const userIdStr = getCurrentUserId();
        const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
        const facilityNumericId = Number(currentFacilityData?.facility_id) || 0;

        if (userId && facilityNumericId) {
          const typeLabel =
            sourceType === "survey" ? "Survey report" : "Incident report";
          const outcome = await continuousComplianceService.saveAnalysisHistory({
            user_id: userId,
            facility_id: facilityNumericId,
            type: typeLabel,
            data_obj: {
              analysis: (analysis as any).analysis || analysis,
              metadata: (analysis as any).metadata || {
                filename: file.name,
                file_size: file.size,
                document_type:
                  sourceType === "survey" ? "survey_report" : "incident_report",
              },
            },
          });
          notifyRiskHistorySave(outcome, typeLabel);
          if (outcome.ok) {
            setRiskHistoryRefreshCounter((c) => c + 1);
          }
        } else {
          toast.warning(
            "Analysis completed but was not saved to Recently Reviewed (missing user or facility).",
          );
        }
      }

      setAffableFileAnalysis(analysis);
      setShowAffableFileReport(false);

      // If survey type, automatically call SOM search for deficiency tags
      if (sourceType === "survey") {
        const deficiencyTags =
          (analysis as any)?.analysis?.deficiency_tags || [];
        if (Array.isArray(deficiencyTags) && deficiencyTags.length > 0) {
          console.log(
            "🔍 Auto-calling SOM search for AffableFLOW survey deficiency tags:",
            deficiencyTags.map((d: any) => d.tag),
          );

          // Extract tags and call SOM search for each
          const tags = deficiencyTags.map((d: any) => d.tag).filter(Boolean);
          if (tags.length > 0) {
            try {
              // Call SOM search for each tag
              const somPromises = tags.map(async (tag: string) => {
                try {
                  const somResponse = await riskAnalysisService.somSearch({
                    query: tag,
                    category:
                      "VectorStores_usa_healthcare_manual_som107ap_g_rhc",
                    customer_id: "1",
                    org_name: "test",
                    user_persona: "survey_analyst",
                  });

                  // Store the SOM data for this tag
                  setTagDetails((prev) => ({
                    ...prev,
                    [tag]: somResponse,
                  }));

                  console.log(`✅ SOM search completed for tag: ${tag}`);
                  return { tag, data: somResponse };
                } catch (error) {
                  console.error(`❌ SOM search failed for tag ${tag}:`, error);
                  // Check if it's an authentication error
                  if (
                    error instanceof Error &&
                    error.message.includes("Not authenticated")
                  ) {
                    // Redirect to login page
                    window.location.href = "/login";
                    return { tag, data: null };
                  }
                  return { tag, data: null };
                }
              });

              // Wait for all SOM searches to complete
              await Promise.all(somPromises);
              console.log(
                "🎯 All SOM searches completed for AffableFLOW survey",
              );
            } catch (somError) {
              console.error("Error during SOM searches:", somError);
              // Don't fail the whole upload if SOM search fails
            }
          }
        }
      }
    } catch (analysisError) {
      console.error("AffableBPM file analysis failed:", analysisError);
      setErrorAffableFile(
        analysisError instanceof Error
          ? analysisError.message
          : "Analysis failed",
      );
    }
  };

  /**
   * Handles uploading a selected AffableBPM file to Risk Analysis
   */
  const handleUploadSelectedAffableFile = async () => {
    if (!selectedAffableFileKey || !selectedAffableFolder) {
      setErrorAffableFile("No file selected");
      return;
    }

    if (!selectedAffableDocument) {
      setErrorAffableFile("No document selected");
      return;
    }

    // Extract folderId and imageId from the selected file key
    const [folderIdStr, imageIdStr] = selectedAffableFileKey.split("-");
    const imageId = Number(imageIdStr);
    const folderId = Number(folderIdStr);

    if (!imageId || isNaN(imageId)) {
      setErrorAffableFile("Invalid file ID");
      return;
    }

    // Find the selected image to get its name and type
    const selectedImage = selectedAffableFolder.images.find(
      (img: any) => img.id === imageId,
    );

    if (!selectedImage) {
      setErrorAffableFile("Selected file not found");
      return;
    }

    setLoadingAffableFile(true);
    setErrorAffableFile(null);

    try {
      const orgNameFromFacility = (resolvedFacility as any)?.org_name;
      const orgName = (orgNameFromFacility || "").trim();
      if (!orgName) {
        throw new Error(
          "Facility name unavailable. Please select a facility first.",
        );
      }

      const fileResponse: ExternalGetFileByFileIdResponse =
        await affablebpmIntegrationService.getFileByFileId({
          orgName,
          fileId: imageId,
          deptId: 101,
          orgId: 0,
        });

      if (fileResponse.Status !== "Success" || !fileResponse.File) {
        throw new Error(
          fileResponse.Status || "Failed to fetch file from AffableBPM",
        );
      }

      const fileData = fileResponse.File;

      if (!fileData.Base64 || fileData.Base64.trim() === "") {
        throw new Error("File data is empty");
      }

      const imageType = fileData.ImageType || selectedImage.type || ".pdf";
      const mimeTypeMap: Record<string, string> = {
        ".pdf": "application/pdf",
        ".doc": "application/msword",
        ".docx":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".html": "text/html",
        ".htm": "text/html",
        ".txt": "text/plain",
        ".xls": "application/vnd.ms-excel",
        ".xlsx":
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      const mimeType =
        mimeTypeMap[imageType.toLowerCase()] || "application/octet-stream";

      let baseName = fileData.ImageName || selectedImage.name;
      if (baseName.toLowerCase().endsWith(imageType.toLowerCase())) {
        baseName = baseName.slice(0, -imageType.length);
      }
      const filename = `${baseName}${imageType}`;

      const file = base64ToFile(fileData.Base64, filename, mimeType);

      // Set upload context for later use with Upload to AffableFLOW button
      setAffableUploadContext({
        documentId: selectedAffableDocument.id,
        folderId: folderId,
        orgName: orgName,
        deptId: fileData.DeptID,
        orgId: 0,
      });

      // Pass the source accordion type to determine which API to call
      await analyzeAffableFileForRisk(
        file,
        affableSourceAccordion || "incident",
      );
    } catch (error) {
      console.error("Failed to upload AffableBPM file:", error);
      setErrorAffableFile(
        error instanceof Error ? error.message : "Failed to upload file",
      );
      toast.error(
        error instanceof Error ? error.message : "Failed to upload file",
      );
    } finally {
      setLoadingAffableFile(false);
    }
  };

  const handleCloseAffablePanel = () => {
    setIsAffablePanelClosed(true);
    // Also clear AffableBPM file and analysis to close the accordion
    setAffableFile(null);
    setAffableFileAnalysis(null);
    setAffableFileUploadDate(null);
    setShowAffableFileReport(false);
    setErrorAffableFile(null);
    setAffableSourceAccordion(null);
  };

  const handleAffableResultUploadButtonClick = async () => {
    setAffableResultUploadError(null);
    setAffableResultUploadSuccess(null);

    if (!affableUploadContext) {
      setAffableResultUploadError(
        "No AffableBPM destination detected. Please analyze a document from AffableBPM first.",
      );
      return;
    }

    if (!affableFileAnalysis) {
      setAffableResultUploadError(
        "No analysis available to upload. Please run the analysis first.",
      );
      return;
    }

    try {
      setIsUploadingAffableResult(true);

      // Derive base name from original Affable file, if available
      const originalName = affableFile?.name || "RiskAnalysisReport";
      const baseName = originalName.replace(/\.[^/.]+$/, "");

      // Build timestamp suffix: YYYYMMDD_HHmmss
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate(),
      )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(
        now.getSeconds(),
      )}`;

      const uploadFileName = `${baseName}_Analysis_${timestamp}.pdf`;

      // Determine document type from analysis metadata
      const documentType =
        affableFileAnalysis.metadata?.document_type === "survey_document"
          ? "survey"
          : "incident";
      console.log("📄 Generating PDF for type:", documentType);

      // Generate the analysis PDF as a Blob (using DownloadReport logic)
      const pdfContent = generatePDFContent(affableFileAnalysis, documentType);

      // Create PDF using jsPDF directly
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "letter",
      });

      // Set up PDF styling
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 25;
      let yPosition = margin;

      // Add content to PDF (simplified version)
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Risk Analysis Report", pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 30;

      // Add sections
      if (pdfContent && pdfContent.sections) {
        pdfContent.sections.forEach((section: any) => {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.setFontSize(14);
          pdf.setFont("helvetica", "bold");
          pdf.text(section.title || "Section", margin, yPosition);
          yPosition += 20;

          pdf.setFontSize(10);
          pdf.setFont("helvetica", "normal");
          const contentLines = pdf.splitTextToSize(
            section.content || "",
            pageWidth - 2 * margin,
          );

          contentLines.forEach((line: string) => {
            if (yPosition > pageHeight - 40) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 12;
          });

          yPosition += 10;
        });
      }

      const pdfBlob = pdf.output("blob");
      const pdfFile = new File([pdfBlob], uploadFileName, {
        type: "application/pdf",
      });

      // Upload generated PDF to AffableBPM using ExternalFileUpload
      const uploadResult = await affablebpmIntegrationService.uploadFile({
        orgName: affableUploadContext.orgName,
        documentId: affableUploadContext.documentId,
        folderId: affableUploadContext.folderId,
        deptId: affableUploadContext.deptId,
        orgId: 0,
        file: pdfFile,
        fileName: pdfFile.name,
      });

      setAffableResultUploadSuccess(
        "Analysis PDF generated and uploaded to AffableFLOW successfully.",
      );
      toast.success(
        uploadResult?.Message ||
          "Analysis PDF generated and uploaded to AffableFLOW successfully.",
      );

      // Refresh AffableBPM document library so the new PDF appears in the list
      await handleAffableRecordsSync(currentWorkflowType, affableDocsPage - 1); // Convert 1-indexed page to 0-indexed
    } catch (error) {
      console.error("Failed to upload analysis PDF to AffableFLOW:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to upload analysis PDF to AffableFLOW.";
      setAffableResultUploadError(message);
      toast.error(message);
    } finally {
      setIsUploadingAffableResult(false);
    }
  };

  // useMemo hooks for Affable document and folder selection
  const selectedAffableDocument = useMemo(() => {
    if (!affableDocuments.length) return null;
    if (selectedAffableDocumentId === null) return affableDocuments[0];
    return (
      affableDocuments.find(
        (document) => document.id === selectedAffableDocumentId,
      ) || affableDocuments[0]
    );
  }, [affableDocuments, selectedAffableDocumentId]);

  const selectedAffableFolder = useMemo(() => {
    if (!selectedAffableDocument) return null;
    if (selectedAffableFolderId === null)
      return selectedAffableDocument.folders?.[0] || null;
    return (
      selectedAffableDocument.folders?.find(
        (folder: any) => folder.id === selectedAffableFolderId,
      ) ||
      selectedAffableDocument.folders?.[0] ||
      null
    );
  }, [selectedAffableDocument, selectedAffableFolderId]);

  const totalAffableDocPages = useMemo(
    () => Math.max(1, Math.ceil(affableRecordCount / affableRowsPerPage)),
    [affableRecordCount, affableRowsPerPage],
  );

  const shouldShowAffablePanel =
    isAffableConnected &&
    !isAffablePanelClosed &&
    (isSyncingAffableRecords ||
      affableDocuments.length > 0 ||
      Boolean(affableSyncError));

  const getFacilityName = () => {
    return (resolvedFacility as any)?.org_name || "";
  };

  const getDescription = (type: "survey" | "incident") => {
    if (type === "survey") {
      return "We offer a full policy review to ensure current compliance standards based on your facility's regulations, surveyor feedback and expertise.";
    } else {
      return "We offer a full policy review to ensure current compliance standards based on your facility's regulations, surveyor feedback and expertise.";
    }
  };

  const getSupportedFileTypesText = (type: "survey" | "incident") => {
    // Per requirement:
    // - Risk Analysis - Incident Report : PDF
    // (Survey upload can continue to allow the broader set already configured.)
    if (type === "incident") {
      return "Supported file type: PDF only";
    }
    return "Supported file type: PDF only";
  };

  const getLoadingState = (type: "survey" | "incident") => {
    return type === "survey" ? loadingSurvey : loadingIncident;
  };

  const getError = (type: "survey" | "incident") => {
    return type === "survey" ? errorSurvey : errorIncident;
  };

  const getAnalysis = (type: "survey" | "incident") => {
    return type === "survey" ? surveyAnalysis : incidentAnalysis;
  };

  const getUploadDate = (type: "survey" | "incident") => {
    return type === "survey" ? surveyUploadDate : incidentUploadDate;
  };

  const toggleReport = (type: "survey" | "incident") => {
    if (type === "survey") {
      setShowSurveyReport(!showSurveyReport);
      if (!showSurveyReport) {
        setShowIncidentReport(false);
      }
    } else {
      setShowIncidentReport(!showIncidentReport);
      if (!showIncidentReport) {
        setShowSurveyReport(false);
      }
    }
  };

  const getShowReport = (type: "survey" | "incident") => {
    return type === "survey" ? showSurveyReport : showIncidentReport;
  };

  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<null | HTMLElement>(null);

  const generateAndDownloadPDF = async (
    content: any,
    documentType: "survey" | "incident",
    analysisData?: RiskAnalysisResponse,
  ) => {
    console.log(`📄 Generating PDF for ${documentType} analysis`);

    // Use passed analysis data or fall back to state
    const dataToUse =
      analysisData ||
      (documentType === "survey" ? surveyAnalysis : incidentAnalysis);

    if (!dataToUse) {
      alert("No analysis data available to download.");
      return;
    }

    console.log("📊 Analysis data structure:", dataToUse);
    console.log("📊 Analysis content:", dataToUse.analysis);

    try {
      const pdf = new jsPDF();

      // Set up PDF styling to match medical report format
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 25;
      let yPosition = margin;

      // Color definitions - professional medical colors
      const headerBlue = [41, 128, 185]; // #2980b9 - Professional blue
      const darkBlue = [52, 73, 94]; // #34495e - Dark blue-gray
      const textColor = [44, 62, 80]; // #2c3e50 - Dark gray
      const lightGray = [236, 240, 241]; // #ecf0f1 - Light background
      const successColor = [39, 174, 96]; // #27ae60 - Green
      const warningColor = [243, 156, 18]; // #f39c12 - Orange
      const dangerColor = [231, 76, 60]; // #e74c3c - Red

      // Helper: load UniComply logo from public assets
      const loadLogo = (): Promise<HTMLImageElement | null> => {
        return new Promise((resolve) => {
          try {
            const img = new Image();
            // Use public asset path
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            const base = (window as any)?.location?.origin || "";
            img.src = `${base}/unicomply.png`;
          } catch {
            resolve(null);
          }
        });
      };

      const logoImg = await loadLogo();

      // Helper functions for medical report formatting
      const addMedicalHeader = () => {
        // UniComply.ai logo on the right + wordmark (match ContinuousCompliance)
        if (logoImg) {
          try {
            const logoWidth = 10;
            const logoHeight = 10;
            // Slightly lower the circle icon
            const logoY = 10;

            // Measure wordmark and compute placement with right padding
            const textFontSize = 12; // larger wordmark
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(textFontSize);
            const uniW =
              (pdf.getStringUnitWidth("Uni") * pdf.getFontSize()) /
              pdf.internal.scaleFactor;
            pdf.setFont("helvetica", "normal");
            const complyW =
              (pdf.getStringUnitWidth("Comply.ai") * pdf.getFontSize()) /
              pdf.internal.scaleFactor;
            const wordWidth = uniW + complyW;
            const rightPadding = 4; // nudge further right
            const gapBetween = 2; // tighter gap
            const logoX = Math.max(
              margin,
              pageWidth -
                margin -
                logoWidth -
                wordWidth -
                gapBetween -
                rightPadding,
            );
            pdf.addImage(logoImg, "PNG", logoX, logoY, logoWidth, logoHeight);

            // Vertically center the wordmark relative to the circle
            const ascentCorrection = textFontSize * 0.18; // nudge baseline slightly up
            const wordY = logoY + logoHeight / 2 + ascentCorrection;
            const wordX = logoX + logoWidth + gapBetween;

            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(textFontSize);
            pdf.setTextColor(6, 91, 112);
            pdf.text("Uni", wordX, wordY);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(102, 102, 102);
            pdf.text("Comply.ai", wordX + uniW, wordY);
          } catch {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(12);
            pdf.setTextColor(6, 91, 112);
            pdf.text("UniComply.ai", pageWidth - margin - 60, 20);
          }
        } else {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(12);
          pdf.setTextColor(6, 91, 112);
          pdf.text("UniComply.ai", pageWidth - margin - 60, 20);
        }

        // Title on the left (dynamic by file type) - matching Figma design
        const title =
          documentType === "incident"
            ? "INCIDENT RISK ANALYSIS"
            : `Survey Reports - ${new Date().toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })}`;
        pdf.setFontSize(18); // Reduced from 24 to make title smaller
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(6, 91, 112); // #065b70 - matching Figma
        const titleBaselineY = 20;
        pdf.text(title, margin, titleBaselineY);

        // Remove subtitle - Figma design doesn't have "Healthcare Facility Risk Assessment"

        // Divider line below header – #BFE5EC at 1px - matching Figma
        pdf.setDrawColor(191, 229, 236); // #BFE5EC
        pdf.setLineWidth(0.5);
        pdf.line(margin, 30, pageWidth - margin, 30);

        return 40;
      };

      // Helpers to render Figma-like chips
      const addPillValue = (
        x: number,
        baselineY: number,
        value: string,
        maxWidth?: number,
      ) => {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        const padX = 4;
        const boxH = 8;
        const textWidth = (t: string) =>
          pdf.getStringUnitWidth(t) *
          (pdf.getFontSize() / pdf.internal.scaleFactor);
        const ellipsis = "…";
        let textVal = String(value || "");
        if (maxWidth && textWidth(textVal) + padX * 2 + 2 > maxWidth) {
          let lo = 0,
            hi = textVal.length;
          while (lo < hi) {
            const mid = Math.floor((lo + hi) / 2);
            const candidate = textVal.slice(0, mid) + ellipsis;
            if (textWidth(candidate) + padX * 2 + 2 <= maxWidth) lo = mid + 1;
            else hi = mid;
          }
          textVal = textVal.slice(0, Math.max(0, lo - 1)) + ellipsis;
        }
        const textW = textWidth(textVal);
        const boxW = Math.min(
          maxWidth || textW + padX * 2 + 2,
          textW + padX * 2 + 2,
        );
        const boxY = baselineY - boxH + 3; // vertical balance around baseline
        // Gray chip background #F0F0F0
        pdf.setFillColor(240, 240, 240);
        pdf.roundedRect(x - 2, boxY, boxW, boxH, 2, 2, "F");
        // Teal text
        pdf.setTextColor(6, 91, 112);
        const centeredX = x - 2 + (boxW - textW) / 2;
        pdf.text(textVal, centeredX, baselineY);
      };

      const addReportMetadata = (yPos: number) => {
        // Card outline like Continuous Compliance (teal border, rounded)
        const cardW = pageWidth - 2 * margin;
        const cardH = 40;
        pdf.setDrawColor(153, 212, 225);
        pdf.setLineWidth(0.6);
        pdf.roundedRect(margin, yPos, cardW, cardH, 4, 4);

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(6, 91, 112);

        // Use affableFile if available (when file comes from AffableFLOW)
        const uploadedFile =
          affableFile ||
          (documentType === "survey" ? surveyFile : incidentFile);
        // Get filename from metadata first, then fall back to file name
        const fileName =
          dataToUse.metadata?.filename ||
          (uploadedFile ? uploadedFile.name : "Unknown File");
        const reportDate = new Date().toLocaleDateString();

        const row1Y = yPos + 12;
        const row2Y = yPos + 23;
        const row3Y = yPos + 33;

        pdf.text("Report Date:", margin + 8, row1Y);
        pdf.text("Document:", margin + 8, row2Y);
        pdf.text("Analysis Type:", margin + 8, row3Y);

        const vX = margin + 55; // align values to consistent column
        const maxValueWidth = cardW - (vX - margin) - 12;
        addPillValue(vX, row1Y, reportDate, maxValueWidth);
        addPillValue(vX, row2Y, fileName, maxValueWidth);
        addPillValue(
          vX,
          row3Y,
          documentType === "incident"
            ? "Incident Report Analysis"
            : "Survey Report",
          maxValueWidth,
        );

        return yPos + cardH + 10;
      };

      const addSectionHeader = (title: string, yPos: number) => {
        yPos = checkNewPage(yPos, 16);
        // Title (no background) and divider above like Continuous Compliance
        pdf.setDrawColor(206, 228, 232);
        pdf.setLineWidth(0.6);
        pdf.line(
          margin,
          Math.max(margin, yPos - 4),
          pageWidth - margin,
          Math.max(margin, yPos - 4),
        );
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(6, 91, 112);
        pdf.text(String(title).toUpperCase(), margin + 5, yPos + 6);
        return yPos + 18;
      };

      const addFieldRow = (
        label: string,
        value: any,
        yPos: number,
        indent = 0,
        isLink: boolean = false,
      ) => {
        yPos = checkNewPage(yPos, 8);

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        // Labels teal like Continuous Compliance
        pdf.setTextColor(6, 91, 112);
        const labelX = margin + 5 + indent;
        const labelText = String(label);
        pdf.text(labelText + ":", labelX, yPos);

        // Compute dynamic start X for value to match Continuous Compliance spacing
        const labelWidth =
          (pdf.getStringUnitWidth(labelText + ":") * pdf.getFontSize()) /
          pdf.internal.scaleFactor;
        const minValueX = margin + 60 + indent; // align values to same column
        const valueX = Math.max(minValueX, labelX + labelWidth + 6);

        if (value) {
          pdf.setFont("helvetica", "normal");
          const cleanValue = String(value).replace(/[^\x20-\x7E\n]/g, "");
          const maxWidth = pageWidth - valueX - (margin - 5);
          const lines = pdf.splitTextToSize(cleanValue, Math.max(40, maxWidth));
          let currentY = yPos;
          if (isLink && typeof value === "string") {
            pdf.setTextColor(33, 150, 243);
            lines.forEach((line: any, index: number) => {
              currentY = checkNewPage(currentY, 8);
              if (index === 0) {
                pdf.textWithLink(line, valueX, currentY, {
                  url: value as string,
                });
              } else {
                pdf.text(line, valueX, currentY);
              }
              currentY += 5;
            });
            return currentY + 3;
          }
          // Values teal
          pdf.setTextColor(6, 91, 112);
          lines.forEach((line: any) => {
            currentY = checkNewPage(currentY, 8);
            pdf.text(line, valueX, currentY);
            currentY += 5;
          });

          return currentY + 3;
        }

        return yPos + 8;
      };

      const addBulletPoint = (text: string, yPos: number, indent = 10) => {
        yPos = checkNewPage(yPos, 8);

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(textColor[0], textColor[1], textColor[2]);

        // Bullet point
        pdf.text("•", margin + indent, yPos);

        // Text
        const cleanText = String(text).replace(/[^\x20-\x7E\n]/g, "");
        const lines = pdf.splitTextToSize(
          cleanText,
          pageWidth - margin - indent - 15,
        );

        lines.forEach((line: any, index: number) => {
          pdf.text(line, margin + indent + 8, yPos + index * 5);
        });

        return yPos + lines.length * 5 + 2;
      };

      const addParagraph = (
        text: string,
        yPos: number,
        indent = 5,
        color?: { r: number; g: number; b: number },
      ) => {
        yPos = checkNewPage(yPos, 8);

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        if (color) {
          pdf.setTextColor(color.r, color.g, color.b);
        } else {
          pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
        }

        const cleanText = String(text).replace(/[^\x20-\x7E\n]/g, "");
        // Ensure wrapping respects BOTH left and right margins
        const maxWidth = pageWidth - 2 * margin - indent - 5;
        const lines = pdf.splitTextToSize(cleanText, Math.max(40, maxWidth));

        lines.forEach((line: any) => {
          yPos = checkNewPage(yPos, 8);
          pdf.text(line, margin + indent, yPos);
          yPos += 5;
        });

        return yPos + 2;
      };

      const checkNewPage = (currentY: number, requiredSpace: number) => {
        if (currentY + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          return margin;
        }
        return currentY;
      };

      // Specialized renderer for Survey Tag sections (e.g., J-0042) to match Figma
      const renderSurveyTagSection = (section: any, yPos: number) => {
        const raw = String(section.content || "");
        const lines = raw.split("\n");
        let description = "";
        let cfr = "";
        let version = "";
        let summary = "";
        const fullGuidelines: string[] = [];
        const procedures: string[] = [];
        let requirementText = "";
        let scope = "";
        const expectedControls: string[] = [];
        let jurisdiction = "";
        let instrument = "";
        let citation = "";
        let inFullGuidelines = false;
        let inProcedures = false;
        let inRequirement = false;
        let inExpected = false;
        let inAuthority = false;
        let inSummary = false;

        const pushIf = (arr: string[], s?: string) => {
          if (s && String(s).trim()) arr.push(String(s));
        };

        lines.forEach((l, idx) => {
          const line = String(l || "").trim();
          if (!line) return;
          if (line.toLowerCase().startsWith("cfr citation")) {
            cfr = line.replace(/cfr citation\s*:?/i, "").trim();
            // If value is on next line, look ahead
            if (!cfr) {
              let j = idx + 1;
              while (j < lines.length && !String(lines[j] || "").trim()) j += 1;
              if (j < lines.length) cfr = String(lines[j]).trim();
            }
            inFullGuidelines =
              inProcedures =
              inRequirement =
              inExpected =
              inAuthority =
              inSummary =
                false;
            return;
          }
          if (line.toLowerCase().startsWith("version")) {
            version = line.replace(/version\s*:?/i, "").trim();
            if (!version) {
              let j = idx + 1;
              while (j < lines.length && !String(lines[j] || "").trim()) j += 1;
              if (j < lines.length) version = String(lines[j]).trim();
            }
            inFullGuidelines =
              inProcedures =
              inRequirement =
              inExpected =
              inAuthority =
              inSummary =
                false;
            return;
          }
          if (/^interpretive guidelines$/i.test(line)) {
            inFullGuidelines = false;
            inProcedures = false;
            inRequirement = false;
            inExpected = false;
            inAuthority = false;
            inSummary = false;
            return;
          }
          if (/^summary\s*:?/i.test(line)) {
            inSummary = true;
            return;
          }
          if (/^full guidelines$/i.test(line)) {
            inFullGuidelines = true;
            inProcedures = inRequirement = inExpected = inAuthority = false;
            inSummary = false;
            return;
          }
          if (/^survey procedures$/i.test(line)) {
            inProcedures = true;
            inFullGuidelines = inRequirement = inExpected = inAuthority = false;
            inSummary = false;
            return;
          }
          if (/^requirement$/i.test(line)) {
            inRequirement = true;
            inFullGuidelines = inProcedures = inExpected = inAuthority = false;
            inSummary = false;
            return;
          }
          if (/^scope\s*:/i.test(line)) {
            scope = line.replace(/^scope\s*:/i, "").trim();
            inRequirement = false;
            inSummary = false;
            return;
          }
          if (/^expected controls$/i.test(line)) {
            inExpected = true;
            inFullGuidelines =
              inProcedures =
              inRequirement =
              inAuthority =
                false;
            inSummary = false;
            return;
          }
          if (/^authority$/i.test(line)) {
            inAuthority = true;
            inFullGuidelines =
              inProcedures =
              inRequirement =
              inExpected =
                false;
            inSummary = false;
            return;
          }

          if (inFullGuidelines) {
            pushIf(fullGuidelines, line);
            return;
          }
          if (inProcedures) {
            pushIf(procedures, line.replace(/^\d+\s*/, ""));
            return;
          }
          if (inSummary) {
            summary += (summary ? "\n" : "") + line;
            return;
          }
          if (inRequirement) {
            requirementText += (requirementText ? "\n" : "") + line;
            return;
          }
          if (inExpected) {
            pushIf(expectedControls, line.replace(/^\d+\s*/, ""));
            return;
          }

          if (inAuthority) {
            if (/^jurisdiction\s*:/i.test(line)) {
              jurisdiction = line.replace(/^jurisdiction\s*:/i, "").trim();
              return;
            }
            if (/^instrument\s*:/i.test(line)) {
              instrument = line.replace(/^instrument\s*:/i, "").trim();
              return;
            }
            if (/^citation\s*:/i.test(line)) {
              citation = line.replace(/^citation\s*:/i, "").trim();
              return;
            }
          }

          if (!description) description = line;
          else description += "\n" + line;
        });

        // Card with tag name, CFR Citation, and Version
        const cardW = pageWidth - 2 * margin;
        const innerPad = 24;
        yPos = checkNewPage(yPos, 60);
        const cardY = yPos;
        const labelX = margin + innerPad;
        const valueStartX = margin + innerPad + 70;

        // Calculate card height based on what will be shown
        let currentRow = cardY + 16;
        const rowHeight = 16;
        let rowCount = 1; // Tag name row
        if (cfr && cfr.trim() && cfr !== "N/A") rowCount++;
        if (version && version.trim() && version !== "N/A") rowCount++;
        const cardH = rowCount * rowHeight + 20;

        // Draw card
        pdf.setDrawColor(91, 191, 214);
        pdf.setLineWidth(0.35);
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(margin, cardY, cardW, cardH, 12, 12, "FD");

        // Tag name row
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(6, 91, 112);
        pdf.text(section.title, labelX, currentRow);
        currentRow += rowHeight;

        // CFR Citation row - only show if value exists
        if (cfr && cfr.trim() && cfr !== "N/A") {
          pdf.setFont("helvetica", "bold");
          pdf.text("CFR Citation :", labelX, currentRow);

          const cfrBoxW = cardW - (valueStartX - margin) - innerPad;
          const cfrBoxH = 12;
          pdf.setFillColor(250, 250, 250);
          pdf.setDrawColor(250, 250, 250);
          pdf.roundedRect(
            valueStartX,
            currentRow - 8,
            cfrBoxW,
            cfrBoxH,
            3,
            3,
            "FD",
          );
          pdf.text(cfr, valueStartX + 6, currentRow);
          currentRow += rowHeight;
        }

        // Version row - only show if value exists
        if (version && version.trim() && version !== "N/A") {
          pdf.setFont("helvetica", "bold");
          pdf.text("Version :", labelX, currentRow);

          const verBoxW = cardW - (valueStartX - margin) - innerPad;
          const verBoxH = 12;
          pdf.setFillColor(250, 250, 250);
          pdf.setDrawColor(250, 250, 250);
          pdf.roundedRect(
            valueStartX,
            currentRow - 8,
            verBoxW,
            verBoxH,
            3,
            3,
            "FD",
          );
          pdf.text(version, valueStartX + 6, currentRow);
        }

        yPos = cardY + cardH + 8;

        // Render "INTERPRETIVE GUIDELINES" header without the divider line above
        yPos = checkNewPage(yPos, 16);
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(6, 91, 112);
        pdf.text("INTERPRETIVE GUIDELINES", margin + 5, yPos + 6);
        yPos = yPos + 24; // add a bit more vertical gap before summary

        // Summary field - label on one line, card on next line
        {
          yPos = checkNewPage(yPos, 40);
          const summaryLabelW = 20;
          const rawSummary =
            summary && summary.trim() ? summary : "No Summary Available";

          // Label on its own line
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(12);
          pdf.setTextColor(6, 91, 112);
          pdf.text("Summary", margin + 8, yPos);
          pdf.setFont("helvetica", "bold");
          pdf.text(":", margin + 8 + summaryLabelW, yPos);

          // Move to next line for the box (minimal gap)
          yPos += 6;

          // Wrapped box sizing - full width on new line
          pdf.setFont("helvetica", "normal"); // Changed from bold to normal
          pdf.setFontSize(11);
          const padX = 6;
          const padY = 4;
          const boxX = margin + 8;
          const maxTextWidth = pageWidth - 2 * margin - 16;
          const wrapped = pdf.splitTextToSize(
            rawSummary,
            maxTextWidth - padX * 2,
          ) as string[];
          const lineHeight = 5;
          const boxW = maxTextWidth;
          const boxH = padY * 2 + wrapped.length * lineHeight;

          // Box style
          pdf.setDrawColor(153, 212, 225);
          pdf.setFillColor(252, 254, 255);
          pdf.setLineWidth(0.1);
          pdf.roundedRect(boxX, yPos, boxW, boxH, 2, 2, "FD");
          pdf.setTextColor(6, 91, 112);

          // Draw wrapped lines
          let ty = yPos + padY + lineHeight;
          wrapped.forEach((line: string) => {
            pdf.text(line, boxX + padX, ty);
            ty += lineHeight;
          });

          yPos += boxH + 8;
        }

        // Full Guidelines - title outside, content in box (matching Summary style)
        if (fullGuidelines.length) {
          const guidelineLabelW = 35;
          const guideText = fullGuidelines.join("\n");
          const padX = 6;
          const padY = 4;
          const boxX = margin + 8;
          const maxTextWidth = pageWidth - 2 * margin - 16;
          const lineHeight = 5;

          // Calculate content lines
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(11);
          const guideLines = pdf.splitTextToSize(
            guideText,
            maxTextWidth - padX * 2,
          ) as string[];

          // Check if we have space for label + at least some content (60 units minimum)
          const minRequiredSpace = 12 + 6 + 60; // label + gap + minimum content
          if (yPos + minRequiredSpace > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
          }

          // Label on its own line
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(12);
          pdf.setTextColor(6, 91, 112);
          pdf.text("Full Guidelines", margin + 8, yPos);
          pdf.setFont("helvetica", "bold");
          pdf.text(":", margin + 8 + guidelineLabelW, yPos);

          // Move to next line for the box (minimal gap)
          yPos += 6;

          const boxW = maxTextWidth;

          // Track lines for current page
          let currentPageLines: string[] = [];
          let currentPageStartY = yPos;

          const drawPageBox = (lines: string[], startY: number) => {
            if (lines.length === 0) return startY;

            const boxH = padY * 2 + lines.length * lineHeight;

            // Draw box
            pdf.setDrawColor(153, 212, 225);
            pdf.setFillColor(252, 254, 255);
            pdf.setLineWidth(0.1);
            pdf.roundedRect(boxX, startY, boxW, boxH, 2, 2, "FD");

            // Draw text on top
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(11);
            pdf.setTextColor(6, 91, 112);

            let textY = startY + padY + lineHeight;
            lines.forEach((line: string) => {
              pdf.text(line, boxX + padX, textY);
              textY += lineHeight;
            });

            return textY;
          };

          // Process all lines and handle page breaks
          guideLines.forEach((line: string, index: number) => {
            const nextLineY =
              currentPageStartY +
              padY +
              lineHeight +
              (currentPageLines.length + 1) * lineHeight;

            // Check if next line would exceed page
            if (
              nextLineY > pageHeight - margin - 10 &&
              currentPageLines.length > 0
            ) {
              // Draw box for current page
              drawPageBox(currentPageLines, currentPageStartY);

              // Start new page
              pdf.addPage();
              currentPageStartY = margin;
              currentPageLines = [line];
            } else {
              currentPageLines.push(line);
            }

            // If this is the last line, draw the final box
            if (index === guideLines.length - 1) {
              yPos = drawPageBox(currentPageLines, currentPageStartY);
            }
          });

          yPos += padY + 8;
        }

        // Remove divider before EXPECTED CONTROLS to keep sections contiguous

        if (procedures.length) {
          yPos = addSectionHeader("SURVEY PROCEDURES", yPos);

          const procPadX = 8;
          const procPadY = 12;
          const procBoxW = pageWidth - 2 * margin - 16;
          const procBoxX = margin + 8;
          const procTextW = procBoxW - 50; // Account for number column and padding
          const lineHeight = 5;

          // Prepare all procedure items with their text lines
          interface ProcedureItem {
            number: number;
            lines: string[];
            height: number;
          }

          const procedureItems: ProcedureItem[] = procedures.map((p, idx) => {
            const procText = (p as any).step || p;
            const procLines = pdf.splitTextToSize(
              String(procText),
              Math.max(40, procTextW),
            ) as string[];
            return {
              number: idx + 1,
              lines: procLines,
              height: procLines.length * lineHeight + 12,
            };
          });

          // Check if we have space for title + at least first procedure
          const minRequiredSpace =
            procedureItems.length > 0
              ? procedureItems[0].height + procPadY * 2 + 20
              : 60;
          if (yPos + minRequiredSpace > pageHeight - margin - 10) {
            pdf.addPage();
            yPos = margin;
          }

          // Helper function to draw procedures box for current page
          const drawProceduresBox = (
            items: ProcedureItem[],
            startY: number,
          ) => {
            if (items.length === 0) return startY;

            // Calculate total height for this page's items
            let boxHeight = procPadY * 2;
            items.forEach((item) => {
              boxHeight += item.height;
            });

            // Draw container with border only (no background)
            pdf.setDrawColor(153, 212, 225); // #99d4e1 - border color
            pdf.setLineWidth(0.5);
            pdf.roundedRect(procBoxX, startY, procBoxW, boxHeight, 6, 6, "S");

            // Draw procedure items
            let procY = startY + procPadY;
            items.forEach((item) => {
              // Number column
              pdf.setFont("helvetica", "normal");
              pdf.setFontSize(11);
              pdf.setTextColor(6, 91, 112);
              pdf.text(String(item.number), procBoxX + procPadX + 4, procY);

              // Procedure text lines
              item.lines.forEach((line: string, i: number) => {
                pdf.text(
                  line,
                  procBoxX + procPadX + 20,
                  procY + i * lineHeight,
                );
              });

              procY += item.height;
            });

            return startY + boxHeight;
          };

          // Track procedures for current page
          let currentPageItems: ProcedureItem[] = [];
          let currentPageStartY = yPos;

          // Process all procedures and handle page breaks
          procedureItems.forEach((item: ProcedureItem, index: number) => {
            // Calculate where next item would end
            const currentBoxHeight =
              procPadY * 2 +
              currentPageItems.reduce((sum, i) => sum + i.height, 0);
            const nextItemEndY =
              currentPageStartY + currentBoxHeight + item.height;

            // Check if next item would exceed page
            if (
              nextItemEndY > pageHeight - margin - 10 &&
              currentPageItems.length > 0
            ) {
              // Draw box for current page
              drawProceduresBox(
                currentPageItems,
                currentPageStartY,
              );

              // Start new page
              pdf.addPage();
              currentPageStartY = margin;
              currentPageItems = [item];
            } else {
              currentPageItems.push(item);
            }

            // If this is the last item, draw the final box
            if (index === procedureItems.length - 1) {
              yPos = drawProceduresBox(currentPageItems, currentPageStartY);
            }
          });

          yPos += 8;
        }

        if (requirementText) {
          const reqLabelW = 35;
          const padX = 6;
          const padY = 4;
          const boxX = margin + 8;
          const maxTextWidth = pageWidth - 2 * margin - 16;
          const lineHeight = 5;

          // Calculate content lines
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(11);
          const reqLines = pdf.splitTextToSize(
            String(requirementText),
            maxTextWidth - padX * 2,
          ) as string[];

          // Check if we have space for label + at least some content
          const scopeRowH = scope ? 20 : 0;
          const minRequiredSpace = 12 + 6 + 60 + scopeRowH;
          if (yPos + minRequiredSpace > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
          }

          // Label on its own line (heading outside card)
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(12);
          pdf.setTextColor(6, 91, 112);
          pdf.text("Requirement", margin + 8, yPos);
          pdf.setFont("helvetica", "bold");
          pdf.text(":", margin + 8 + reqLabelW, yPos);

          // Move to next line for the box
          yPos += 6;

          const boxW = maxTextWidth;
          const boxH =
            padY * 2 +
            reqLines.length * lineHeight +
            (scope ? scopeRowH + 8 : 0);

          // Draw box with border only (no background)
          pdf.setDrawColor(153, 212, 225);
          pdf.setLineWidth(0.1);
          pdf.roundedRect(boxX, yPos, boxW, boxH, 2, 2, "S");

          // Draw requirement text
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(11);
          pdf.setTextColor(6, 91, 112);

          let textY = yPos + padY + lineHeight;
          reqLines.forEach((line: string) => {
            pdf.text(line, boxX + padX, textY);
            textY += lineHeight;
          });

          // Scope row inside box if present
          if (scope) {
            textY += 12;
            const scopeLabelW = 60;
            pdf.setFont("helvetica", "bold");
            pdf.text("Scope", boxX + padX, textY);
            pdf.text(":", boxX + padX + scopeLabelW, textY);

            // Scope value box
            const scopeBoxX = boxX + padX + scopeLabelW + 10;
            const scopeBoxW = boxW - (scopeLabelW + 10 + padX * 2);
            const scopeBoxH = 12;

            pdf.setFillColor(250, 250, 250);
            pdf.setDrawColor(250, 250, 250);
            pdf.roundedRect(
              scopeBoxX,
              textY - 8,
              scopeBoxW,
              scopeBoxH,
              3,
              3,
              "FD",
            );
            pdf.setFontSize(11);
            pdf.text(String(scope), scopeBoxX + 6, textY);
          }

          // Advance cursor after box
          yPos += boxH + padY + 8;
        } else if (scope) {
          // If only scope present, render simply
          yPos = addSectionHeader("REQUIREMENT", yPos);
          yPos = addFieldRow("Scope", scope, yPos, 5);
        }

        if (expectedControls.length) {
          // Title matching Figma design (24px uppercase)
          yPos = addSectionHeader("EXPECTED CONTROLS", yPos);

          const panelPadX = 8;
          const panelPadY = 8;
          const numberColW = 14; // Width for number column (40px in Figma)
          const textMaxW =
            pageWidth - 2 * margin - panelPadX * 2 - numberColW - 8;
          const lineH = 6;

          let totalH = 0;
          expectedControls.forEach((c) => {
            const lines = pdf.splitTextToSize(
              String(c),
              Math.max(40, textMaxW),
            );
            totalH += lines.length * lineH + 12;
          });
          const panelH = panelPadY * 2 + totalH;
          yPos = checkNewPage(yPos, panelH + 6);

          // Fill-only rounded panel matching Figma (#f4feff background)
          pdf.setFillColor(244, 254, 255); // #f4feff
          pdf.roundedRect(
            margin,
            yPos,
            pageWidth - 2 * margin,
            panelH,
            6,
            6,
            "F",
          );

          let rowY = yPos + panelPadY + 6;
          expectedControls.forEach((c, idx) => {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(12); // 18px in Figma
            pdf.setTextColor(6, 91, 112);
            const lines = pdf.splitTextToSize(
              String(c),
              Math.max(40, textMaxW),
            );
            lines.forEach((line: string, i: number) => {
              pdf.text(line, margin + panelPadX + 4, rowY + i * lineH);
            });
            rowY += lines.length * lineH + 12;
          });

          yPos += panelH + 8;
        }

        // Authority Section - matching Summary/Requirement card style
        if (jurisdiction || instrument || citation || cfr) {
          const authLabelW = 35;
          const padX = 6;
          const padY = 4;
          const boxX = margin + 8;
          const maxTextWidth = pageWidth - 2 * margin - 16;
          const lineHeight = 14;

          // Calculate height based on fields present
          let fieldCount = 0;
          if (jurisdiction) fieldCount++;
          if (instrument) fieldCount++;
          if (citation || cfr) fieldCount++;

          const boxH = padY * 2 + fieldCount * lineHeight + 8;
          const minRequiredSpace = 12 + 6 + boxH;

          if (yPos + minRequiredSpace > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
          }

          // Label on its own line (heading outside card)
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(12);
          pdf.setTextColor(6, 91, 112);
          pdf.text("Authority", margin + 8, yPos);
          pdf.setFont("helvetica", "bold");
          pdf.text(":", margin + 8 + authLabelW, yPos);

          // Move to next line for the box
          yPos += 6;

          const boxW = maxTextWidth;

          // Draw box with border only (no background)
          pdf.setDrawColor(153, 212, 225);
          pdf.setLineWidth(0.1);
          pdf.roundedRect(boxX, yPos, boxW, boxH, 2, 2, "S");

          // Draw authority fields inside the card
          let textY = yPos + padY + lineHeight;
          const labelWidth = 50;

          if (jurisdiction) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(11);
            pdf.setTextColor(6, 91, 112);
            pdf.text("Jurisdiction", boxX + padX, textY);
            pdf.setFont("helvetica", "bold");
            pdf.text(":", boxX + padX + labelWidth, textY);
            pdf.setFont("helvetica", "normal");
            pdf.text(jurisdiction, boxX + padX + labelWidth + 8, textY);
            textY += lineHeight;
          }

          if (instrument) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(11);
            pdf.setTextColor(6, 91, 112);
            pdf.text("Instrument", boxX + padX, textY);
            pdf.setFont("helvetica", "bold");
            pdf.text(":", boxX + padX + labelWidth, textY);
            pdf.setFont("helvetica", "normal");
            pdf.text(instrument, boxX + padX + labelWidth + 8, textY);
            textY += lineHeight;
          }

          if (citation || cfr) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(11);
            pdf.setTextColor(6, 91, 112);
            pdf.text("Citation", boxX + padX, textY);
            pdf.setFont("helvetica", "bold");
            pdf.text(":", boxX + padX + labelWidth, textY);
            pdf.setFont("helvetica", "normal");
            pdf.text(citation || cfr, boxX + padX + labelWidth + 8, textY);
          }

          yPos += boxH + padY + 8;
        }

        return yPos;
      };

      const formatIncidentSummary = (content: any, yPosition: number) => {
        const lines = content.split("\n");

        lines.forEach((line: string) => {
          if (line.trim()) {
            yPosition = checkNewPage(yPosition, 15);

            if (line.includes(":")) {
              const parts = line.split(":");
              if (parts.length >= 2) {
                const label = parts[0].trim();
                const value = parts.slice(1).join(":").trim();
                yPosition = addFieldRow(label, value, yPosition);
              }
            } else {
              yPosition = addBulletPoint(line, yPosition, 5);
              yPosition += 3;
            }
          }
        });

        return yPosition + 5;
      };

      // Add medical report header
      yPosition = addMedicalHeader();

      // Add metadata section for both incident and survey PDFs
      yPosition = addReportMetadata(yPosition);
      // Tighten spacing before first section header
      yPosition += 2;

      // Risk score details are appended immediately after the Executive Summary section below

      // Process sections from content
      if (content.sections && Array.isArray(content.sections)) {
        content.sections.forEach((section: any) => {
          console.log(`📄 Adding section: ${section.title}`);

          const isJCodeSection =
            documentType === "survey" && /^j-?\d+/i.test(section.title);
          if (!isJCodeSection) {
            yPosition = addSectionHeader(section.title, yPosition);
          }

          if (section.content) {
            yPosition = checkNewPage(yPosition, 40);

            // Use enhanced formatting based on section type
            if (section.title.toLowerCase().includes("executive summary")) {
              // Render executive summary first, then risk score block to match Continuous Compliance
              const lines = section.content.split("\n");
              lines.forEach((line: string) => {
                if (line.trim()) {
                  if (line.includes(":")) {
                    const parts = line.split(":");
                    if (parts.length >= 2) {
                      yPosition = addFieldRow(
                        parts[0].trim(),
                        parts.slice(1).join(":").trim(),
                        yPosition,
                      );
                    }
                  } else {
                    // Executive summary paragraph text in teal (#065B70)
                    yPosition = addParagraph(line.trim(), yPosition, 5, {
                      r: 6,
                      g: 91,
                      b: 112,
                    });
                  }
                }
              });
              // Append risk score details right after executive summary
              const rs = dataToUse.analysis.risk_score;
              if (rs) {
                yPosition += 6;
                yPosition = addSectionHeader("Risk Score", yPosition);
                yPosition = addFieldRow(
                  "Overall Score",
                  `${rs?.overall_score ?? "-"} (${rs?.risk_level ?? "N/A"})`,
                  yPosition,
                  5,
                );
                yPosition = addFieldRow(
                  "Issues Count",
                  rs?.issues_count ?? "-",
                  yPosition,
                  5,
                );
                yPosition = addFieldRow(
                  "Actions Count",
                  rs?.actions_count ?? "-",
                  yPosition,
                  5,
                );
                if (rs?.score_breakdown) {
                  yPosition = addFieldRow("Score Breakdown", "", yPosition, 5);
                  yPosition = addFieldRow(
                    "Documentation",
                    rs?.score_breakdown?.documentation ?? "-",
                    yPosition,
                    10,
                  );
                  yPosition = addFieldRow(
                    "Compliance",
                    rs?.score_breakdown?.compliance ?? "-",
                    yPosition,
                    10,
                  );
                  yPosition = addFieldRow(
                    "Safety",
                    rs?.score_breakdown?.safety ?? "-",
                    yPosition,
                    10,
                  );
                  yPosition = addFieldRow(
                    "Financial Risk",
                    rs?.score_breakdown?.financial_risk ?? "-",
                    yPosition,
                    10,
                  );
                }
                if (rs?.justification) {
                  yPosition = addFieldRow(
                    "Justification",
                    rs.justification,
                    yPosition,
                    5,
                  );
                  // Reduce spacing before divider
                  yPosition += 4;
                  pdf.setDrawColor(lightGray[0], lightGray[1], lightGray[2]);
                  pdf.setLineWidth(0.5);
                  pdf.line(margin, yPosition, pageWidth - margin, yPosition);
                  yPosition += 2;
                }
              }
            } else if (
              section.title.toLowerCase().includes("incident summary")
            ) {
              yPosition = formatIncidentSummary(section.content, yPosition);
            } else if (
              documentType === "survey" &&
              /^j-?\d+/i.test(section.title)
            ) {
              yPosition = renderSurveyTagSection(section, yPosition);
            } else if (section.title.toLowerCase().includes("deficienc")) {
              // Structured rendering identical to Continuous Compliance
              const defs: any[] =
                (analysisData as any).analysis?.compliance_deficiencies || [];
              if (Array.isArray(defs) && defs.length > 0) {
                defs.forEach((def: any, idx: number) => {
                  yPosition = addFieldRow(
                    `#${idx + 1} ${def?.category || "Category"}`,
                    "",
                    yPosition,
                  );
                  yPosition = addFieldRow(
                    "Risk Level",
                    def?.risk_level || "N/A",
                    yPosition,
                    5,
                  );
                  yPosition = addFieldRow(
                    "Requirement",
                    def?.requirement,
                    yPosition,
                    5,
                  );
                  yPosition = addFieldRow(
                    "Source",
                    def?.regulatory_source || "N/A",
                    yPosition,
                    5,
                  );
                  if (def?.specific_regulation_citation) {
                    yPosition = addFieldRow(
                      "Citation",
                      def?.specific_regulation_citation,
                      yPosition,
                      5,
                    );
                  }
                  if (def?.official_url) {
                    yPosition = addFieldRow(
                      "Official URL",
                      def?.official_url,
                      yPosition,
                      5,
                      true,
                    );
                  }
                  yPosition = addFieldRow(
                    "Deficiency",
                    def?.deficiency_description,
                    yPosition,
                    5,
                  );
                  const risks = (def?.associated_risks || []).join(", ");
                  if (risks)
                    yPosition = addFieldRow(
                      "Associated Risks",
                      risks,
                      yPosition,
                      5,
                    );
                  yPosition += 8;
                });
              }
            } else if (
              section.title.toLowerCase().includes("plan of correction")
            ) {
              // Render Plan of Correction with the same structure as Continuous Compliance
              const poc =
                (analysisData as any).analysis?.plan_of_correction || {};

              const actions = poc?.action_table || [];
              if (Array.isArray(actions) && actions.length) {
                actions.forEach((action: any, idx: number) => {
                  yPosition = addFieldRow(
                    `Action ${idx + 1}`,
                    action?.action,
                    yPosition,
                  );
                  yPosition = addFieldRow(
                    "Priority",
                    normalizePriority(action?.priority),
                    yPosition,
                    5,
                  );
                  if (action?.timeline)
                    yPosition = addFieldRow(
                      "Timeline",
                      action.timeline,
                      yPosition,
                      5,
                    );
                  if (action?.responsible)
                    yPosition = addFieldRow(
                      "Responsible",
                      action.responsible,
                      yPosition,
                      5,
                    );
                  yPosition += 2;
                });
              }

              const docImprovements = poc?.documentation_improvements || [];
              if (Array.isArray(docImprovements) && docImprovements.length) {
                yPosition = addFieldRow(
                  "Documentation Improvements",
                  "",
                  yPosition,
                );
                docImprovements.forEach((doc: any, idx: number) => {
                  yPosition = addFieldRow(
                    `Improvement ${idx + 1}`,
                    doc?.improvement,
                    yPosition,
                    5,
                  );
                  if (
                    Array.isArray(doc?.implementation_steps) &&
                    doc.implementation_steps.length
                  ) {
                    yPosition = addFieldRow(
                      "Implementation Steps",
                      doc.implementation_steps.join(", "),
                      yPosition,
                      10,
                    );
                  }
                  if (doc?.timeline)
                    yPosition = addFieldRow(
                      "Timeline",
                      doc.timeline,
                      yPosition,
                      10,
                    );
                  yPosition += 2;
                });
              }

              const systemImprovements = poc?.system_improvements || [];
              if (
                Array.isArray(systemImprovements) &&
                systemImprovements.length
              ) {
                yPosition = addFieldRow("System Improvements", "", yPosition);
                systemImprovements.forEach((sys: any, idx: number) => {
                  yPosition = addFieldRow(
                    `System ${idx + 1}`,
                    sys?.system,
                    yPosition,
                    5,
                  );
                  if (sys?.improvement)
                    yPosition = addFieldRow(
                      "Improvement",
                      sys.improvement,
                      yPosition,
                      10,
                    );
                  if (sys?.implementation_cost)
                    yPosition = addFieldRow(
                      "Implementation Cost",
                      sys.implementation_cost,
                      yPosition,
                      10,
                    );
                  if (sys?.timeline)
                    yPosition = addFieldRow(
                      "Timeline",
                      sys.timeline,
                      yPosition,
                      10,
                    );
                  yPosition += 2;
                });
              }

              const monitoringTracking = poc?.monitoring_tracking || [];
              if (
                Array.isArray(monitoringTracking) &&
                monitoringTracking.length
              ) {
                yPosition = addFieldRow("Monitoring & Tracking", "", yPosition);
                monitoringTracking.forEach((m: any, idx: number) => {
                  yPosition = addFieldRow(
                    `Metric ${idx + 1}`,
                    m?.metric,
                    yPosition,
                    5,
                  );
                  if (m?.frequency)
                    yPosition = addFieldRow(
                      "Frequency",
                      m.frequency,
                      yPosition,
                      10,
                    );
                  if (m?.responsible_party)
                    yPosition = addFieldRow(
                      "Responsible",
                      m.responsible_party,
                      yPosition,
                      10,
                    );
                  if (m?.reporting_method)
                    yPosition = addFieldRow(
                      "Reporting Method",
                      m.reporting_method,
                      yPosition,
                      10,
                    );
                  yPosition += 2;
                });
              }

              const trainingReqs = poc?.training_requirements || [];
              if (Array.isArray(trainingReqs) && trainingReqs.length) {
                trainingReqs.forEach((t: any) => {
                  yPosition = addFieldRow(
                    "Training",
                    t?.training_topic,
                    yPosition,
                  );
                  if (t?.target_audience)
                    yPosition = addFieldRow(
                      "Target Audience",
                      t.target_audience,
                      yPosition,
                      5,
                    );
                  if (t?.frequency)
                    yPosition = addFieldRow(
                      "Frequency",
                      t.frequency,
                      yPosition,
                      5,
                    );
                  if (t?.duration)
                    yPosition = addFieldRow(
                      "Duration",
                      t.duration,
                      yPosition,
                      5,
                    );
                  yPosition += 2;
                });
              }

              const costs = poc?.corrective_costs;
              if (costs) {
                const immediate = costs?.immediate_costs ?? "N/A";
                const longTerm = costs?.long_term_costs ?? "N/A";
                const total =
                  costs?.total_estimated_cost ?? costs?.total ?? "N/A";
                yPosition = addFieldRow(
                  "Estimated Corrective Costs",
                  `Immediate: ${immediate} | Long-term: ${longTerm} | Total: ${total}`,
                  yPosition,
                );
              }
            } else if (section.title.toLowerCase().includes("recommendation")) {
              // Render Recommendations with the same structured layout as Continuous Compliance
              const recs = extractRecommendationsFromData(
                dataToUse.analysis as any,
              );
              recs.forEach((r: any, idx: number) => {
                yPosition = addFieldRow(
                  `Recommendation ${idx + 1}`,
                  r?.recommendation || r?.title || "",
                  yPosition,
                );
                yPosition = addFieldRow(
                  "Priority",
                  normalizePriority(r?.priority),
                  yPosition,
                  5,
                );
                if (r?.category)
                  yPosition = addFieldRow(
                    "Category",
                    r?.category,
                    yPosition,
                    5,
                  );
                if (r?.rationale)
                  yPosition = addFieldRow(
                    "Rationale",
                    r?.rationale,
                    yPosition,
                    5,
                  );
                if (r?.estimated_cost || r?.cost)
                  yPosition = addFieldRow(
                    "Estimated Cost",
                    r?.estimated_cost || r?.cost,
                    yPosition,
                    5,
                  );
                if (r?.expected_outcome || r?.outcome)
                  yPosition = addFieldRow(
                    "Expected Outcome",
                    r?.expected_outcome || r?.outcome,
                    yPosition,
                    5,
                  );
                if (r?.implementation_timeline)
                  yPosition = addFieldRow(
                    "Timeline",
                    r?.implementation_timeline,
                    yPosition,
                    5,
                  );

                // Dynamic extra keys similar to Continuous Compliance
                const knownKeys = new Set([
                  "recommendation",
                  "title",
                  "priority",
                  "category",
                  "rationale",
                  "estimated_cost",
                  "cost",
                  "expected_outcome",
                  "outcome",
                  "implementation_timeline",
                ]);
                Object.entries(r || {}).forEach(([k, v]) => {
                  if (
                    !knownKeys.has(k) &&
                    v !== undefined &&
                    v !== null &&
                    String(v) !== ""
                  ) {
                    yPosition = addFieldRow(
                      prettifyLabel(k),
                      stringifyValue(v),
                      yPosition,
                      5,
                    );
                  }
                });
                yPosition += 2;
              });
            } else {
              // Default formatting for other sections
              const lines = section.content.split("\n");
              lines.forEach((line: string) => {
                if (line.trim()) {
                  if (line.includes(":")) {
                    const parts = line.split(":");
                    if (parts.length >= 2) {
                      yPosition = addFieldRow(
                        parts[0].trim(),
                        parts.slice(1).join(":").trim(),
                        yPosition,
                      );
                    }
                  } else {
                    yPosition = addBulletPoint(line.trim(), yPosition);
                  }
                }
              });
            }
            yPosition += 10;
          }
        });
      }

      // Add medical report footer to all pages
      const totalPages = pdf.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);

        // Footer line
        pdf.setDrawColor(lightGray[0], lightGray[1], lightGray[2]);
        pdf.setLineWidth(0.5);
        pdf.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

        // Footer branding (logo + wordmark on left)
        pdf.setFontSize(9);
        pdf.setTextColor(darkBlue[0], darkBlue[1], darkBlue[2]);
        pdf.setFont("helvetica", "normal");
        const pageLabel = `Page ${i} of ${totalPages}`;
        const generatedDateLabel = `Generated: ${new Date().toLocaleDateString()}`;

        // Left: Logo + wordmark + report type; fallback to text if asset missing
        try {
          if (logoImg) {
            const iconW = 8;
            const iconH = 8;
            const iconX = margin;
            const baseY = pageHeight - 14; // baseline
            const iconY = baseY - iconH / 2 - 1;
            pdf.addImage(logoImg, "PNG", iconX, iconY, iconW, iconH);
            const textFontSize = 9;
            const ascentCorrection = textFontSize * 0.18;
            const wordY = iconY + iconH / 2 + ascentCorrection;
            const wordX = iconX + iconW + 2;
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(textFontSize);
            pdf.setTextColor(6, 91, 112);
            pdf.text("Uni", wordX, wordY);
            const uniW =
              (pdf.getStringUnitWidth("Uni") * pdf.getFontSize()) /
              pdf.internal.scaleFactor;
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(102, 102, 102);
            pdf.text("Comply.ai", wordX + uniW, wordY);

            // Append report type after brand
            const footerType =
              documentType === "incident"
                ? " - Incident Risk Analysis"
                : " - Survey Report";
            const brandWidth =
              ((pdf.getStringUnitWidth("Uni") +
                pdf.getStringUnitWidth("Comply.ai")) *
                pdf.getFontSize()) /
                pdf.internal.scaleFactor +
              2;
            pdf.setTextColor(darkBlue[0], darkBlue[1], darkBlue[2]);
            pdf.text(footerType, wordX + brandWidth, wordY);
          } else {
            const footerTitle =
              documentType === "incident"
                ? "UniComply.ai - Incident Risk Analysis"
                : "UniComply.ai - Survey Report";
            pdf.text(footerTitle, margin, pageHeight - 14);
          }
        } catch {
          const footerTitle =
            documentType === "incident"
              ? "UniComply.ai - Incident Risk Analysis"
              : "UniComply.ai - Survey Report";
          pdf.text(footerTitle, margin, pageHeight - 14);
        }

        // Center: Page X of N (centered)
        const pageLabelWidth =
          (pdf.getStringUnitWidth(pageLabel) * pdf.getFontSize()) /
          pdf.internal.scaleFactor;
        pdf.text(
          pageLabel,
          pageWidth / 2 - pageLabelWidth / 2,
          pageHeight - 14,
        );

        // Right: Generated date (right-aligned)
        const genWidth =
          (pdf.getStringUnitWidth(generatedDateLabel) * pdf.getFontSize()) /
          pdf.internal.scaleFactor;
        pdf.text(
          generatedDateLabel,
          pageWidth - margin - genWidth,
          pageHeight - 14,
        );

        // Add confidentiality notice on each page
        pdf.setFontSize(7);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          "Generated using AI. Subject to human verification.",
          margin,
          pageHeight - 8,
        );
      }

      // Save the PDF with dynamic incident type naming
      // Use affableFile if available (when file comes from AffableFLOW)
      const uploadedFile =
        affableFile || (documentType === "survey" ? surveyFile : incidentFile);
      const incidentType =
        dataToUse.analysis?.incident_summary?.incident_type || "Risk_Analysis";
      const cleanIncidentType = incidentType.replace(/[^a-zA-Z0-9]/g, "_");

      const fileName = uploadedFile
        ? `${cleanIncidentType}_Analysis_${uploadedFile.name.replace(
            /\.[^/.]+$/,
            "",
          )}_${new Date().toISOString().split("T")[0]}.pdf`
        : `${cleanIncidentType}_Analysis_${
            new Date().toISOString().split("T")[0]
          }.pdf`;
      pdf.save(fileName);

      console.log(`✅ Medical report PDF downloaded: ${fileName}`);
    } catch (error) {
      console.error("❌ PDF generation error:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const DownloadReport = (
    type: "survey" | "incident",
    analysis: RiskAnalysisResponse,
  ) => {
    console.log("📄 Generating PDF for download:", type, analysis);
    const pdfContent = generatePDFContent(analysis, type);
    generateAndDownloadPDF(pdfContent, type, analysis);
  };

  const DownloadDocxReport = (
    type: "survey" | "incident",
    analysis: RiskAnalysisResponse,
  ) => {
    generateRiskAnalysisDocx(analysis, type);
  };

  async function generateRiskAnalysisDocx(
    analysis: RiskAnalysisResponse,
    documentType: "survey" | "incident",
  ) {
    const data: any = (analysis as any)?.analysis || {};
    const meta: any = (analysis as any)?.metadata;

    const titleMap: Record<typeof documentType, string> = {
      survey: "SURVEY RISK ANALYSIS",
      incident: "INCIDENT RISK ANALYSIS",
    };

    const sections: any[] = [];

    // Title
    sections.push(
      new Paragraph({
        text: titleMap[documentType],
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
      new Paragraph({
        text: "Healthcare Facility Risk Assessment",
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      }),
    );

    // Metadata
    sections.push(
      new Paragraph({
        text: "Report Information",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 200 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Report Date: ", bold: true }),
          new TextRun(new Date().toLocaleDateString()),
        ],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Document: ", bold: true }),
          new TextRun(meta?.filename || "Unknown File"),
        ],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Analysis Type: ", bold: true }),
          new TextRun(
            documentType === "survey"
              ? "Survey Risk Analysis"
              : "Incident Risk Analysis",
          ),
        ],
        spacing: { after: 300 },
      }),
    );

    // Risk Score - Removed as per user request
    // if (data?.risk_score) {
    //   const rs = data.risk_score;
    //   sections.push(
    //     new Paragraph({
    //       text: "Risk Score",
    //       heading: HeadingLevel.HEADING_2,
    //       spacing: { before: 200, after: 200 },
    //     }),
    //     new Paragraph({
    //       children: [
    //         new TextRun({ text: "Overall Score: ", bold: true }),
    //         new TextRun(
    //           `${rs?.overall_score ?? "-"} (${rs?.risk_level ?? "N/A"})`
    //         ),
    //       ],
    //       spacing: { after: 300 },
    //     })
    //   );
    // }

    // Compliance Deficiencies
    if (
      Array.isArray(data?.compliance_deficiencies) &&
      data.compliance_deficiencies.length > 0
    ) {
      sections.push(
        new Paragraph({
          text: "Compliance Deficiencies",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 200 },
        }),
      );
      data.compliance_deficiencies.forEach((def: any, idx: number) => {
        sections.push(
          new Paragraph({
            children: [
              new TextRun({ text: `#${idx + 1} `, bold: true }),
              new TextRun({
                text: def?.category || def?.requirement || "Compliance Issue",
                bold: true,
              }),
              new TextRun({ text: ":" }),
            ],
            spacing: { before: 100, after: 50 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "Risk Level: ", bold: true }),
              new TextRun(def?.risk_level || "N/A"),
            ],
            spacing: { after: 50 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: "Requirement: ", bold: true }),
              new TextRun(def?.requirement || "N/A"),
            ],
            spacing: { after: 200 },
          }),
        );
      });
    }

    // Incident-specific sections only
    if (documentType === "incident") {
      // Potential Fines & Penalties
      if (data?.potential_penalties) {
        const penaltyTable = data.potential_penalties?.penalty_table || [];
        if (Array.isArray(penaltyTable) && penaltyTable.length > 0) {
          sections.push(
            new Paragraph({
              text: "Potential Fines & Penalties",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 200 },
            }),
          );
          penaltyTable.forEach((penalty: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Penalty ${idx + 1}`, bold: true }),
                ],
                spacing: { before: 100, after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Regulator: ", bold: true }),
                  new TextRun(penalty?.regulator || "N/A"),
                ],
                spacing: { after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Fine Range: ", bold: true }),
                  new TextRun(penalty?.fine_range || "N/A"),
                ],
                spacing: { after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Estimated Exposure: ", bold: true }),
                  new TextRun(penalty?.estimated_exposure || "N/A"),
                ],
                spacing: { after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Notes: ", bold: true }),
                  new TextRun(penalty?.notes || "N/A"),
                ],
                spacing: { after: 200 },
              }),
            );
          });
        }
      }

      // Plan of Correction
      if (data?.plan_of_correction) {
        const poc = data.plan_of_correction;

        sections.push(
          new Paragraph({
            text: "Plan of Correction",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 200 },
          }),
        );

        // Immediate Actions
        if (Array.isArray(poc?.action_table) && poc.action_table.length > 0) {
          sections.push(
            new Paragraph({
              text: "Immediate Actions",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 100, after: 100 },
            }),
          );
          poc.action_table.forEach((action: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Action ${idx + 1}: `, bold: true }),
                  new TextRun(
                    action?.action ||
                      action?.title ||
                      action?.description ||
                      "Corrective Action",
                  ),
                ],
                spacing: { before: 100, after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Priority: ", bold: true }),
                  new TextRun(action?.priority || "N/A"),
                ],
                spacing: { after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Timeline: ", bold: true }),
                  new TextRun(action?.timeline || "N/A"),
                ],
                spacing: { after: 50 },
              }),
            );
            if (action?.responsible) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Responsible: ", bold: true }),
                    new TextRun(action.responsible),
                  ],
                  spacing: { after: 200 },
                }),
              );
            } else {
              sections.push(
                new Paragraph({
                  text: "",
                  spacing: { after: 200 },
                }),
              );
            }
          });
        }

        // Documentation Improvements
        if (
          Array.isArray(poc?.documentation_improvements) &&
          poc.documentation_improvements.length > 0
        ) {
          sections.push(
            new Paragraph({
              text: "Documentation Improvements",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            }),
          );
          poc.documentation_improvements.forEach((doc: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Improvement ${idx + 1}: `, bold: true }),
                  new TextRun(doc?.improvement || "Improvement"),
                ],
                spacing: { before: 100, after: 50 },
              }),
            );
            if (
              Array.isArray(doc?.implementation_steps) &&
              doc.implementation_steps.length > 0
            ) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Implementation Steps: ", bold: true }),
                    new TextRun(doc.implementation_steps.join(", ")),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (doc?.timeline) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Timeline: ", bold: true }),
                    new TextRun(doc.timeline),
                  ],
                  spacing: { after: 200 },
                }),
              );
            } else {
              sections.push(
                new Paragraph({
                  text: "",
                  spacing: { after: 200 },
                }),
              );
            }
          });
        }

        // System Improvements
        if (
          Array.isArray(poc?.system_improvements) &&
          poc.system_improvements.length > 0
        ) {
          sections.push(
            new Paragraph({
              text: "System Improvements",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            }),
          );
          poc.system_improvements.forEach((sys: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `System ${idx + 1}: `, bold: true }),
                  new TextRun(sys?.system || "System"),
                ],
                spacing: { before: 100, after: 50 },
              }),
            );
            if (sys?.improvement) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Improvement: ", bold: true }),
                    new TextRun(sys.improvement),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (sys?.implementation_cost) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Implementation Cost: ", bold: true }),
                    new TextRun(sys.implementation_cost),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (sys?.timeline) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Timeline: ", bold: true }),
                    new TextRun(sys.timeline),
                  ],
                  spacing: { after: 200 },
                }),
              );
            } else {
              sections.push(
                new Paragraph({
                  text: "",
                  spacing: { after: 200 },
                }),
              );
            }
          });
        }

        // Monitoring & Tracking
        if (
          Array.isArray(poc?.monitoring_tracking) &&
          poc.monitoring_tracking.length > 0
        ) {
          sections.push(
            new Paragraph({
              text: "Monitoring & Tracking",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            }),
          );
          poc.monitoring_tracking.forEach((m: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Metric ${idx + 1}: `, bold: true }),
                  new TextRun(m?.metric || "Metric"),
                ],
                spacing: { before: 100, after: 50 },
              }),
            );
            if (m?.frequency) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Frequency: ", bold: true }),
                    new TextRun(m.frequency),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (m?.responsible_party) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Responsible: ", bold: true }),
                    new TextRun(m.responsible_party),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (m?.reporting_method) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Reporting Method: ", bold: true }),
                    new TextRun(m.reporting_method),
                  ],
                  spacing: { after: 200 },
                }),
              );
            } else {
              sections.push(
                new Paragraph({
                  text: "",
                  spacing: { after: 200 },
                }),
              );
            }
          });
        }

        // Training Requirements
        if (
          Array.isArray(poc?.training_requirements) &&
          poc.training_requirements.length > 0
        ) {
          sections.push(
            new Paragraph({
              text: "Training Requirements",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            }),
          );
          poc.training_requirements.forEach((t: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Training ${idx + 1}: `, bold: true }),
                  new TextRun(t?.training_topic || "Training Topic"),
                ],
                spacing: { before: 100, after: 50 },
              }),
            );
            if (t?.target_audience) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Target Audience: ", bold: true }),
                    new TextRun(t.target_audience),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (t?.frequency) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Frequency: ", bold: true }),
                    new TextRun(t.frequency),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            if (t?.duration) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Duration: ", bold: true }),
                    new TextRun(t.duration),
                  ],
                  spacing: { after: 200 },
                }),
              );
            } else {
              sections.push(
                new Paragraph({
                  text: "",
                  spacing: { after: 200 },
                }),
              );
            }
          });
        }

        // Estimated Corrective Costs
        if (poc?.corrective_costs) {
          const costs = poc.corrective_costs;
          const immediate = costs?.immediate_costs ?? "N/A";
          const longTerm = costs?.long_term_costs ?? "N/A";
          const total = costs?.total_estimated_cost ?? costs?.total ?? "N/A";

          sections.push(
            new Paragraph({
              text: "Estimated Corrective Costs",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Immediate Costs: ", bold: true }),
                new TextRun(immediate),
              ],
              spacing: { after: 50 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Long-term Costs: ", bold: true }),
                new TextRun(longTerm),
              ],
              spacing: { after: 50 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Total Estimated Cost: ", bold: true }),
                new TextRun(total),
              ],
              spacing: { after: 200 },
            }),
          );
        }
      }

      // Recommendations
      if (data?.recommendations?.recommendation_table?.length > 0) {
        sections.push(
          new Paragraph({
            text: "Recommendations",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 200 },
          }),
        );
        data.recommendations.recommendation_table.forEach(
          (rec: any, idx: number) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Recommendation ${idx + 1}: `,
                    bold: true,
                  }),
                  new TextRun(rec?.recommendation || "Recommendation"),
                ],
                spacing: { before: 100, after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Priority: ", bold: true }),
                  new TextRun(rec?.priority || "N/A"),
                ],
                spacing: { after: 50 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Rationale: ", bold: true }),
                  new TextRun(rec?.rationale || "No rationale provided"),
                ],
                spacing: { after: 50 },
              }),
            );
            if (rec?.estimated_cost) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Estimated Cost: ", bold: true }),
                    new TextRun(rec.estimated_cost),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "Expected Outcome: ", bold: true }),
                  new TextRun(rec?.expected_outcome || "No outcome specified"),
                ],
                spacing: { after: 200 },
              }),
            );
          },
        );
      }
    } // End of incident-specific sections

    // Survey-specific sections (for survey document type)
    if (documentType === "survey") {
      const surveyTags = data?.deficiency_tags || [];
      if (Array.isArray(surveyTags) && surveyTags.length > 0) {
        surveyTags.forEach((def: any) => {
          const tag = def?.tag || "";
          const somData = (tagDetails as any)?.[tag];
          const parsed: any = somData
            ? parseSomResponse(somData, data?.survey_summary)
            : null;

          // Add tag name before CFR Citation and Version
          if (tag) {
            sections.push(
              new Paragraph({
                text: tag,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 200 },
              }),
            );
          }

          // CFR Citation - only if present and not 'N/A'
          const cfrCitation =
            parsed?.cfrCitation || def?.specific_regulation_citation;
          if (
            cfrCitation &&
            cfrCitation !== "N/A" &&
            cfrCitation.trim() !== ""
          ) {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "CFR Citation: ", bold: true }),
                  new TextRun(cfrCitation),
                ],
                spacing: { before: 100, after: 100 },
              }),
            );
          }

          // Version - only if present and not 'N/A'
          const version = parsed?.version;
          if (version && version !== "N/A" && version.trim() !== "") {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "Version: ", bold: true }),
                  new TextRun(version),
                ],
                spacing: { after: 100 },
              }),
            );
          }

          // Interpretive Guidelines
          if (parsed?.summary || parsed?.interpretiveGuidelines) {
            sections.push(
              new Paragraph({
                text: "INTERPRETIVE GUIDELINES",
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              }),
            );

            if (parsed?.summary) {
              sections.push(
                new Paragraph({
                  children: [new TextRun({ text: "Summary: ", bold: true })],
                  spacing: { after: 50 },
                }),
                new Paragraph({
                  text: parsed.summary,
                  spacing: { after: 100 },
                }),
              );
            }

            if (parsed?.interpretiveGuidelines) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Full Guidelines: ", bold: true }),
                  ],
                  spacing: { before: 100, after: 50 },
                }),
                new Paragraph({
                  text: parsed.interpretiveGuidelines,
                  spacing: { after: 200 },
                }),
              );
            }
          }

          // Survey Procedures
          if (
            Array.isArray(parsed?.procedures) &&
            parsed.procedures.length > 0
          ) {
            sections.push(
              new Paragraph({
                text: "SURVEY PROCEDURES",
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              }),
            );

            parsed.procedures.forEach((proc: any, idx: number) => {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: `${idx + 1}. `, bold: true }),
                    new TextRun(proc.step || proc),
                  ],
                  spacing: { after: 50 },
                }),
              );
            });
            sections.push(new Paragraph({ text: "", spacing: { after: 100 } }));
          }

          // Requirement
          if (parsed?.description) {
            sections.push(
              new Paragraph({
                text: "REQUIREMENT",
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              }),
              new Paragraph({
                text: parsed.description,
                spacing: { after: 100 },
              }),
            );
          }

          // Scope
          const scopeText =
            parsed?.scope || def?.scope || data?.survey_summary?.scope;
          if (scopeText) {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "Scope: ", bold: true }),
                  new TextRun(scopeText),
                ],
                spacing: { after: 200 },
              }),
            );
          }

          // Expected Controls
          if (
            Array.isArray(parsed?.expectedControls) &&
            parsed.expectedControls.length > 0
          ) {
            sections.push(
              new Paragraph({
                text: "Expected Controls",
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              }),
            );

            parsed.expectedControls.forEach((control: string, idx: number) => {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: `${idx + 1}. `, bold: true }),
                    new TextRun(control),
                  ],
                  spacing: { after: 50 },
                }),
              );
            });
            sections.push(new Paragraph({ text: "", spacing: { after: 100 } }));
          }

          // Authority
          if (
            parsed?.jurisdiction ||
            parsed?.instrument ||
            parsed?.cfrCitation ||
            def?.regulatory_source ||
            def?.specific_regulation_citation
          ) {
            sections.push(
              new Paragraph({
                text: "Authority",
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 100 },
              }),
            );

            if (parsed?.jurisdiction || def?.regulatory_source) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Jurisdiction: ", bold: true }),
                    new TextRun(parsed?.jurisdiction || def?.regulatory_source),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }

            if (parsed?.instrument) {
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Instrument: ", bold: true }),
                    new TextRun(parsed.instrument),
                  ],
                  spacing: { after: 50 },
                }),
              );
            }

            if (parsed?.cfrCitation || def?.specific_regulation_citation) {
              const citation =
                parsed?.cfrCitation || def?.specific_regulation_citation;
              sections.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: "Citation: ", bold: true }),
                    new TextRun(citation),
                  ],
                  spacing: { after: 200 },
                }),
              );
            }
          }

          // Add spacing between tags
          sections.push(
            new Paragraph({
              text: "",
              spacing: { after: 400 },
            }),
          );
        });
      }
    }

    // Create footer
    const footerText =
      documentType === "incident"
        ? "UniComply.ai - Incident Risk Analysis"
        : "UniComply.ai - Survey Report";

    const generatedDate = new Date().toLocaleDateString();

    const footer = new Footer({
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          tabStops: [
            {
              type: TabStopType.LEFT,
              position: 0,
            },
            {
              type: TabStopType.CENTER,
              position: TabStopPosition.MAX / 2,
            },
            {
              type: TabStopType.RIGHT,
              position: TabStopPosition.MAX,
            },
          ],
          children: [
            new TextRun({
              text: footerText,
              size: 18,
              color: "065B70",
            }),
            new TextRun({
              text: "\t",
            }),
            new TextRun({
              text: "Page ",
              size: 18,
            }),
            new TextRun({
              children: [PageNumber.CURRENT],
              size: 18,
            }),
            new TextRun({
              text: " of ",
              size: 18,
            }),
            new TextRun({
              children: [PageNumber.TOTAL_PAGES],
              size: 18,
            }),
            new TextRun({
              text: "\t",
            }),
            new TextRun({
              text: `Generated: ${generatedDate}`,
              size: 18,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.LEFT,
          spacing: { before: 100 },
          children: [
            new TextRun({
              text: "Generated using AI. Subject to human verification.",
              size: 14,
              color: "808080",
            }),
          ],
        }),
      ],
    });

    // Create document
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: sections,
          footers: {
            default: footer,
          },
        },
      ],
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    // Use affableFile if available (when file comes from AffableFLOW)
    const uploadedFile =
      affableFile || (documentType === "survey" ? surveyFile : incidentFile);
    const fileName = uploadedFile
      ? `RiskAnalysis_${documentType}_${uploadedFile.name.replace(
          /\.[^/.]+$/,
          "",
        )}_${new Date().toISOString().split("T")[0]}.docx`
      : `RiskAnalysis_${documentType}_${
          new Date().toISOString().split("T")[0]
        }.docx`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function generatePDFContent(
    analysis: RiskAnalysisResponse,
    documentType: "survey" | "incident",
  ) {
    console.log("📊 Generating PDF content from analysis data:", analysis);
    const sections = [];
    const analysisData = analysis.analysis;
    if (documentType === "incident") {
      // Executive Summary (match Continuous Compliance styled PDF: narrative text only)
      // if (analysisData.executive_summary) {
      //   sections.push({
      //     title: "Executive Summary",
      //     content:
      //       analysisData.executive_summary || "No executive summary available",
      //   });
      // }

      // Incident Summary Details
      // if (analysisData.incident_summary) {
      //   const inc = analysisData.incident_summary;
      //   let partiesLine = "Not specified";
      //   if (Array.isArray(inc.parties_involved)) {
      //     partiesLine = inc.parties_involved
      //       .map((p: any) => `"${String(p)}"`)
      //       .join(",  ");
      //   } else if (inc.parties_involved) {
      //     partiesLine = String(inc.parties_involved);
      //   }
      //   sections.push({
      //     title: "Incident Summary",
      //     content:
      //       `Narrative: ${inc.narrative || "Not specified"}\n` +
      //       `Type: ${inc.incident_type || "Not specified"}\n` +
      //       `Clinical Classification: ${
      //         inc.clinical_classification || "Not specified"
      //       }\n` +
      //       `Date: ${inc.date || "N/A"}\n` +
      //       `Location: ${inc.location || "N/A"}\n` +
      //       `Description: ${inc.description || "No description available"}\n` +
      //       `Parties Involved: ${partiesLine}\n` +
      //       `Injuries/Damages: ${inc.injuries_damages || "Not specified"}\n` +
      //       `Environmental Factors: ${
      //         inc.environmental_factors || "Not specified"
      //       }\n` +
      //       `Reporting Timeliness: ${
      //         inc.reporting_timeliness || "Not specified"
      //       }\n` +
      //       `Documentation Completeness: ${
      //         inc.documentation_completeness || "Not specified"
      //       }`,
      //   });
      // }

      // Compliance Deficiencies
      if (
        analysisData.compliance_deficiencies &&
        analysisData.compliance_deficiencies.length > 0
      ) {
        let deficienciesText = "";
        analysisData.compliance_deficiencies.forEach((def, index) => {
          const priority = def.risk_level || def.risk_level || "MEDIUM";
          deficienciesText += `${
            def.requirement || `Deficiency ${index + 1}`
          }\n`;
          deficienciesText += `Risk Level: ${priority}\n`;
          deficienciesText += `Category: ${def.category || "Not specified"}\n`;
          if (def.regulatory_source) {
            deficienciesText += `Regulatory Source: ${def.regulatory_source}\n`;
          }
          if (def.specific_regulation_citation) {
            deficienciesText += `Regulation Citation: ${def.specific_regulation_citation}\n`;
          }
          if (def.official_url) {
            deficienciesText += `Official URL: ${def.official_url}\n`;
          }
          deficienciesText += `Description: ${
            def.deficiency_description || "No description"
          }\n`;
          if (def.associated_risks && def.associated_risks.length > 0) {
            deficienciesText += `Associated Risks:\n`;
            def.associated_risks.forEach((risk) => {
              deficienciesText += `• ${risk}\n`;
            });
          } else if (def.associated_risks && def.associated_risks.length > 0) {
            deficienciesText += `Associated Risks:\n`;
            def.associated_risks.forEach((risk) => {
              deficienciesText += `• ${risk}\n`;
            });
          }
          deficienciesText += "\n";
        });

        sections.push({
          title: "Compliance Deficiencies",
          content: deficienciesText,
        });
      }

      // Potential Fines & Penalties (omit penalty_charts per requirement)
      if (analysisData.potential_penalties) {
        let penaltiesText = "";

        const table = analysisData.potential_penalties?.penalty_table || [];
        table.forEach((p: any) => {
          penaltiesText += `Regulator: ${p?.regulator ?? "N/A"}\n`;
          penaltiesText += `Fine Range: ${p?.fine_range ?? "N/A"}\n`;
          penaltiesText += `Estimated Exposure: ${
            p?.estimated_exposure ?? "N/A"
          }\n`;
          penaltiesText += `Notes: ${p?.notes ?? "N/A"}\n`;
          penaltiesText += "\n";
        });

        if (penaltiesText) {
          sections.push({
            title: "Potential Fines & Penalties",
            content: penaltiesText,
          });
        }
      }

      // Plan of Correction
      if (
        analysisData.plan_of_correction &&
        analysisData.plan_of_correction.action_table
      ) {
        let correctionsText = "Immediate Actions\n\n";
        analysisData.plan_of_correction.action_table.forEach(
          (correction, index) => {
            const priority = correction.priority || "MEDIUM";
            correctionsText += `${
              correction.action || `Action ${index + 1}`
            }\n`;
            correctionsText += `Priority: ${priority}\n`;
            if (correction.timeline) {
              correctionsText += `Timeline: ${correction.timeline}\n`;
            }
            if (correction.responsible) {
              correctionsText += `Responsible: ${correction.responsible}\n`;
            }
            // if (correction.description) {
            //     correctionsText += `Details: ${correction.description}\n`;
            // }
            correctionsText += "\n";
          },
        );

        sections.push({
          title: "Plan of Correction",
          content: correctionsText,
        });
      }

      // // Recommendations
      if (
        analysisData.recommendations &&
        analysisData.recommendations.recommendation_table
      ) {
        let recommendationsText = "";
        analysisData.recommendations.recommendation_table.forEach(
          (rec, index) => {
            const priority = rec.priority || "MEDIUM";
            recommendationsText += `${
              rec.recommendation || `Recommendation ${index + 1}`
            }\n`;
            recommendationsText += `Priority: ${priority}\n`;
            recommendationsText += `Rationale: ${
              rec.rationale || "No rationale provided"
            }\n`;
            if (rec.estimated_cost) {
              recommendationsText += `Estimated Cost: ${rec.estimated_cost}\n`;
            }
            recommendationsText += `Expected Outcome: ${
              rec.expected_outcome || "No outcome specified"
            }\n\n`;
          },
        );

        sections.push({
          title: "Recommendations",
          content: recommendationsText,
        });
      }
    } else {
      // SURVEY: Build sections mirroring the Survey UI per deficiency tag
      const surveyTags = (analysisData as any)?.deficiency_tags || [];
      if (Array.isArray(surveyTags) && surveyTags.length > 0) {
        surveyTags.forEach((def: any) => {
          const tag = def?.tag || "";
          const somData = (tagDetails as any)?.[tag];
          const parsed: any = somData
            ? parseSomResponse(somData, (analysisData as any)?.survey_summary)
            : null;

          const sectionTitle = tag || def?.category || "Survey Tag";
          const contentLines: string[] = [];

          // Top requirement/description block
          // if (parsed?.description || def?.description) {
          //   contentLines.push(String(parsed?.description || def?.description));
          // }

          // CFR / Version - only show if present and not 'N/A'
          const cfrCitation =
            parsed?.cfrCitation || def?.specific_regulation_citation;
          if (
            cfrCitation &&
            cfrCitation !== "N/A" &&
            cfrCitation.trim() !== ""
          ) {
            contentLines.push(`CFR Citation : ${cfrCitation}`);
          }
          const version = parsed?.version;
          if (version && version !== "N/A" && version.trim() !== "") {
            contentLines.push(`Version : ${version}`);
          }

          // Interpretive Guidelines: Summary + (optional) Full Guidelines
          contentLines.push("INTERPRETIVE GUIDELINES");
          if (parsed?.summary) {
            contentLines.push("Summary:");
            contentLines.push(parsed.summary);
          }
          if (parsed?.interpretiveGuidelines) {
            contentLines.push("Full Guidelines");
            contentLines.push(String(parsed.interpretiveGuidelines));
          }

          // Survey Procedures
          if (Array.isArray(parsed?.procedures) && parsed.procedures.length) {
            contentLines.push("SURVEY PROCEDURES");
            parsed.procedures.forEach((p: any, idx: number) => {
              // Keep procedure text on single line by replacing internal newlines with spaces
              const procText = (p.step || p).replace(/\n/g, " ");
              contentLines.push(`${idx + 1}\n${procText}`);
            });
          }

          // Requirement + Scope
          if (parsed?.description) {
            contentLines.push("REQUIREMENT");
            contentLines.push(String(parsed.description));
          }
          const scopeText =
            parsed?.scope ||
            def?.scope ||
            (analysisData as any)?.survey_summary?.scope;
          if (scopeText) contentLines.push(`Scope : ${scopeText}`);

          // Expected Controls
          if (
            Array.isArray(parsed?.expectedControls) &&
            parsed.expectedControls.length
          ) {
            contentLines.push("Expected Controls");
            parsed.expectedControls.forEach((c: any, idx: number) =>
              contentLines.push(`${idx + 1}\n${String(c)}`),
            );
          }

          // Authority block
          if (
            parsed?.jurisdiction ||
            parsed?.instrument ||
            parsed?.cfrCitation ||
            def?.regulatory_source ||
            def?.specific_regulation_citation
          ) {
            contentLines.push("Authority");
            if (parsed?.jurisdiction)
              contentLines.push(`Jurisdiction : ${parsed.jurisdiction}`);
            if (!parsed?.jurisdiction && def?.regulatory_source)
              contentLines.push(`Jurisdiction : ${def.regulatory_source}`);
            if (parsed?.instrument)
              contentLines.push(`Instrument : ${parsed.instrument}`);
            if (parsed?.cfrCitation)
              contentLines.push(`Citation : ${parsed.cfrCitation}`);
            else if (def?.specific_regulation_citation)
              contentLines.push(
                `Citation : ${def.specific_regulation_citation}`,
              );
          }

          sections.push({
            title: sectionTitle,
            content: contentLines.join("\n"),
          });
        });
      }

      // Fallback: if no deficiency_tags or no sections were added, use compliance_deficiencies
      if (
        sections.length === 0 &&
        Array.isArray((analysisData as any)?.compliance_deficiencies)
      ) {
        (analysisData as any).compliance_deficiencies.forEach(
          (def: any, idx: number) => {
            const sectionTitle =
              def?.specific_regulation_citation ||
              def?.category ||
              `Deficiency ${idx + 1}`;
            const lines: string[] = [];
            if (def?.requirement) lines.push(String(def.requirement));
            if (def?.specific_regulation_citation)
              lines.push(`CFR Citation : ${def.specific_regulation_citation}`);
            if (def?.version) lines.push(`Version : ${def.version}`);
            lines.push("REQUIREMENT");
            if (def?.deficiency_description)
              lines.push(String(def.deficiency_description));
            {
              const scopeText =
                def?.scope || (analysisData as any)?.survey_summary?.scope;
              if (scopeText) lines.push(`Scope : ${scopeText}`);
            }
            if (def?.regulatory_source || def?.specific_regulation_citation) {
              lines.push("Authority");
              if (def?.regulatory_source)
                lines.push(`Jurisdiction : ${def.regulatory_source}`);
              if (def?.instrument) lines.push(`Instrument : ${def.instrument}`);
              if (def?.specific_regulation_citation)
                lines.push(`Citation : ${def.specific_regulation_citation}`);
            }
            sections.push({ title: sectionTitle, content: lines.join("\n") });
          },
        );
      }

      // Do not include a top-level Executive Summary for survey PDFs
    }

    console.log("📊 Generated PDF sections:", sections);
    return { sections };
  }

  const renderReportDetails = (
    analysis: RiskAnalysisResponse,
    type: "survey" | "incident",
  ) => {
    const data: any = (analysis as any)?.analysis || {};
    return (
      <ReportDetailsContainer>
        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            marginBottom: "12px",
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <DownloadButton
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            endIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Download
          </DownloadButton>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                minWidth: "120px",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                DownloadReport(type, analysis);
                setDownloadMenuAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                padding: "10px 16px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.49976 9.00299C4.22361 9.00299 3.99976 9.22685 3.99976 9.50299V11.503C3.99976 11.7791 4.22361 12.003 4.49976 12.003C4.7759 12.003 4.99976 11.7791 4.99976 11.503V11.3366H5.33296C5.97736 11.3366 6.49976 10.8142 6.49976 10.1698C6.49976 9.52538 5.97736 9.00299 5.33296 9.00299H4.49976ZM5.33296 10.3366H4.99976V10.003H5.33296C5.42508 10.003 5.49976 10.0777 5.49976 10.1698C5.49976 10.2619 5.42508 10.3366 5.33296 10.3366ZM10.0005 9.50236C10.0008 9.22646 10.2246 9.00299 10.5005 9.00299H11.4988C11.7749 9.00299 11.9988 9.22685 11.9988 9.50299C11.9988 9.77913 11.7749 10.003 11.4988 10.003H10.9998L10.9994 10.3375H11.4988C11.7749 10.3375 11.9988 10.5613 11.9988 10.8375C11.9988 11.1136 11.7749 11.3375 11.4988 11.3375H11L11.0005 11.5017C11.0012 11.7779 10.7779 12.0023 10.5017 12.003C10.2256 12.0037 10.0012 11.7804 10.0005 11.5043L9.99878 10.8368L10.0005 9.50236ZM7.49829 9.00299C7.22215 9.00299 6.99829 9.22685 6.99829 9.50299V11.503C6.99829 11.7791 7.22215 12.003 7.49829 12.003H7.99974C8.82816 12.003 9.49973 11.3314 9.49974 10.503C9.49974 9.67457 8.82817 9.00299 7.99974 9.00299H7.49829ZM7.99829 11.003V10.003H7.99974C8.27588 10.003 8.49974 10.2269 8.49974 10.503C8.49974 10.7791 8.27588 11.003 7.99974 11.003H7.99829ZM3 3.50195V7.08413C2.4174 7.29005 2 7.84567 2 8.49878V12.502C2 13.3304 2.67157 14.002 3.5 14.002H12.5C13.3284 14.002 14 13.3304 14 12.502V8.49878C14 7.84567 13.5826 7.29005 13 7.08413V5.4133C13 5.0149 12.8415 4.63287 12.5595 4.35147L10.6439 2.44012C10.3627 2.15953 9.98167 2.00195 9.58442 2.00195H4.5C3.67157 2.00195 3 2.67353 3 3.50195ZM4.5 3.00195H9V4.49994C9 5.32837 9.67157 5.99994 10.5 5.99994H12V6.99878H4V3.50195C4 3.22581 4.22386 3.00195 4.5 3.00195ZM10 3.21029L11.7936 4.99994H10.5C10.2239 4.99994 10 4.77608 10 4.49994V3.21029ZM3.5 7.99878H12.5C12.7761 7.99878 13 8.22264 13 8.49878V12.502C13 12.7781 12.7761 13.002 12.5 13.002H3.5C3.22386 13.002 3 12.7781 3 12.502V8.49878C3 8.22264 3.22386 7.99878 3.5 7.99878Z"
                  fill="#262626"
                />
              </svg>
              PDF
            </MenuItem>
            <MenuItem
              onClick={() => {
                DownloadDocxReport(type, analysis);
                setDownloadMenuAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                padding: "10px 16px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 3C3 1.89543 3.89543 1 5 1H8.58579C8.98361 1 9.36514 1.15804 9.64645 1.43934L12.5607 4.35355C12.842 4.63486 13 5.01639 13 5.41421V13C13 14.1046 12.1046 15 11 15H5C3.89543 15 3 14.1046 3 13V3ZM5 2C4.44772 2 4 2.44772 4 3V13C4 13.5523 4.44772 14 5 14H11C11.5523 14 12 13.5523 12 13V6H9.5C8.67157 6 8 5.32843 8 4.5V2H5ZM9.5 5H11.7929L9 2.20711V4.5C9 4.77614 9.22386 5 9.5 5ZM5.5 8C5.22386 8 5 8.22386 5 8.5C5 8.77614 5.22386 9 5.5 9H10.5C10.7761 9 11 8.77614 11 8.5C11 8.22386 10.7761 8 10.5 8H5.5ZM5 10.5C5 10.2239 5.22386 10 5.5 10H10.5C10.7761 10 11 10.2239 11 10.5C11 10.7761 10.7761 11 10.5 11H5.5C5.22386 11 5 10.7761 5 10.5ZM5.5 12C5.22386 12 5 12.2239 5 12.5C5 12.7761 5.22386 13 5.5 13H10.5C10.7761 13 11 12.7761 11 12.5C11 12.2239 10.7761 12 10.5 12H5.5Z"
                  fill="#262626"
                />
              </svg>
              DOCX
            </MenuItem>
          </Menu>
          {affableUploadContext && (
            <UploadActionButton
              onClick={handleAffableResultUploadButtonClick}
              disabled={isUploadingAffableResult}
              sx={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              {isUploadingAffableResult ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#0a829d" }} />
                  Uploading...
                </>
              ) : (
                <>
                  {/* <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.33333 9.66667V5.276L5.47133 7.13867L4.52867 6.196L8 2.72467L11.4713 6.196L10.5287 7.13867L8.66667 5.276V9.66667H7.33333ZM4 12.3333H12V13.6667H4V12.3333Z"
                      fill="#065B70"
                    />
                  </svg> */}
                  <AffableConnectedIcon />
                  Upload to AffableFLOW
                </>
              )}
            </UploadActionButton>
          )}
        </Box>

        {/* Executive Summary Section - COMMENTED OUT */}
        {/* <ReportSection>
          <SectionTitle>EXECUTIVE SUMMARY</SectionTitle>

          <Box
            sx={{
              backgroundColor: "#fffbf0",
              border: "1px solid #f59e0b",
              borderRadius: "4px",
              padding: "8px",
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M8.68149 2.78544C9.24892 1.73813 10.752 1.73821 11.3193 2.78557L17.8198 14.7865C18.3612 15.786 17.6375 17.0009 16.5009 17.0009H3.4982C2.36147 17.0009 1.63783 15.7858 2.17934 14.7864L8.68149 2.78544ZM10.5 7.5C10.5 7.22386 10.2761 7 10 7C9.72386 7 9.5 7.22386 9.5 7.5V11.5C9.5 11.7761 9.72386 12 10 12C10.2761 12 10.5 11.7761 10.5 11.5V7.5ZM10.75 13.75C10.75 13.3358 10.4142 13 10 13C9.58579 13 9.25 13.3358 9.25 13.75C9.25 14.1642 9.58579 14.5 10 14.5C10.4142 14.5 10.75 14.1642 10.75 13.75Z"
                    fill="url(#paint0_linear_2570_14894)"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2570_14894"
                      x1="9.99954"
                      y1="2"
                      x2="9.99954"
                      y2="17.0009"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stop-color="#EF7A19" />
                      <stop offset="1" stop-color="#ED6C02" />
                    </linearGradient>
                  </defs>
                </svg>
              </Box>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#262626" }}
              >
                Gaps found
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "14px",
                color: "#262626",
                lineHeight: 1.5,
                marginBottom: "12px",
              }}
            >
              <strong>
                Risk Score: {data.risk_score?.overall_score || 0}/100 (
                {data.risk_score?.risk_level || "MEDIUM"})
              </strong>
              <br />
              <br />
              {data.executive_summary || "No executive summary available"}
            </Typography>

            {data.incident_summary && (
              <Typography
                sx={{ fontSize: "14px", color: "#262626", lineHeight: 1.5 }}
              >
                <strong>{data.incident_summary.incident_type}:</strong>{" "}
                {data.incident_summary.description}
                <br />
                <br />
                <strong>Date:</strong> {data.incident_summary.date}
                <br />
                <strong>Location:</strong> {data.incident_summary.location}
                <br />
                <strong>Documentation Status:</strong>{" "}
                {data.incident_summary.documentation_completeness}
              </Typography>
            )}
          </Box>
        </ReportSection> */}

        {/* Compliance Deficiencies Section */}
        {Array.isArray(data.compliance_deficiencies) &&
          data.compliance_deficiencies.length > 0 && (
            <StyledAccordion defaultExpanded={false}>
              <AccordionSummary
                expandIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#737373"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                sx={{
                  minHeight: "45px",
                  borderRadius: "4px",
                  border: "1px solid #9EE5F4",
                  backgroundColor: "#FFF",
                  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                  "&.Mui-expanded": {
                    minHeight: "45px",
                    border: "1px solid #9EE5F4",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "7px 0",
                    flex: "1 1 auto",
                    width: "100%",
                    minWidth: 0,
                    "&.Mui-expanded": {
                      margin: "7px 0",
                    },
                  },
                  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                    transform: "rotate(180deg)",
                  },
                }}
              >
                <AccordionTitle component="div" className="accordion-title">
                  <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Compliance Deficiencies</span>
                  </AccordionTitleLeading>
                  <AccordionCountBadge className="accordion-count-badge">
                    {data.compliance_deficiencies.length} items
                  </AccordionCountBadge>
                </AccordionTitle>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "16px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#065b70",
                    marginBottom: "16px",
                  }}
                >
                  {/* Based on CMS Conditions of Participation (CoPs), Joint
                  Commission, OSHA, and HIPAA (where relevant): */}
                </Typography>

                {data.compliance_deficiencies.map(
                  (deficiency: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "#f3fdff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: "1px solid #737373",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#262626",
                          }}
                        >
                          {deficiency.category} - Risk Level:{" "}
                          {deficiency.risk_level}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Requirement:</strong> {deficiency.requirement}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Source:</strong> {deficiency.regulatory_source}{" "}
                        - {deficiency.specific_regulation_citation}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Deficiency:</strong>{" "}
                        {deficiency.deficiency_description}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Associated Risks:</strong>{" "}
                        {deficiency.associated_risks?.join(", ")}
                      </Typography>
                    </Box>
                  ),
                )}
              </AccordionDetails>
            </StyledAccordion>
          )}

        {/* Potential Fines & Penalties Section */}
        {data.potential_penalties && (
          <StyledAccordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#737373"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                minHeight: "45px",
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "&.Mui-expanded": {
                  minHeight: "45px",
                  border: "1px solid #9EE5F4",
                  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "7px 0",
                  flex: "1 1 auto",
                  width: "100%",
                  minWidth: 0,
                  "&.Mui-expanded": {
                    margin: "7px 0",
                  },
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <AccordionTitle component="div" className="accordion-title">
                <AccordionTitleLeading>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                      fill="#065B70"
                    />
                  </svg>
                  <span>Potential Fines & Penalties if Not Corrected</span>
                </AccordionTitleLeading>
                <AccordionCountBadge className="accordion-count-badge">
                  {data.potential_penalties?.penalty_table?.length || 0} items
                </AccordionCountBadge>
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#f3fdff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  padding: "8px",
                }}
              >
                {data.potential_penalties?.penalty_table?.map(
                  (penalty: any, index: number) => (
                    <Box key={index} sx={{ marginBottom: "8px" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Regulator:</strong>{" "}
                        {penalty?.regulator ?? "N/A"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Fine Range:</strong>{" "}
                        {penalty?.fine_range ?? "N/A"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Estimated Exposure:</strong>{" "}
                        {penalty?.estimated_exposure ?? "N/A"}
                      </Typography>
                      {penalty?.notes ? (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#262626",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Notes:</strong> {penalty.notes}
                        </Typography>
                      ) : null}
                    </Box>
                  ),
                )}

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                    marginTop: "12px",
                    fontWeight: 600,
                  }}
                >
                  <strong>Estimated Financial Impact:</strong>
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                    marginBottom: "4px",
                  }}
                >
                  • Penalty Range:{" "}
                  {data.potential_penalties?.penalty_charts
                    ?.estimated_penalty_exposure?.min || "N/A"}{" "}
                  -{" "}
                  {data.potential_penalties?.penalty_charts
                    ?.estimated_penalty_exposure?.max || "N/A"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                  }}
                >
                  • Corrective Investment:{" "}
                  {data.potential_penalties?.penalty_charts
                    ?.estimated_corrective_investment?.total || "N/A"}
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        )}

        {/* Plan of Correction Section */}
        {data.plan_of_correction && (
          <StyledAccordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#737373"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                minHeight: "45px",
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "&.Mui-expanded": {
                  minHeight: "45px",
                  border: "1px solid #9EE5F4",
                  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "7px 0",
                  flex: "1 1 auto",
                  width: "100%",
                  minWidth: 0,
                  "&.Mui-expanded": {
                    margin: "7px 0",
                  },
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <AccordionTitle component="div" className="accordion-title">
                <AccordionTitleLeading>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                      fill="#065B70"
                    />
                  </svg>
                  <span>Plan of Correction (POC)</span>
                </AccordionTitleLeading>
                <AccordionCountBadge className="accordion-count-badge">
                  {data.plan_of_correction?.action_table?.length || 0} actions
                </AccordionCountBadge>
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#065b70",
                  marginBottom: "16px",
                }}
              >
                To bring the facility into compliance:
              </Typography>

              {/* Action Items */}
              {data.plan_of_correction?.action_table?.map(
                (action: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#f3fdff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      padding: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        borderBottom: "1px solid #737373",
                        paddingBottom: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#262626",
                        }}
                      >
                        Action {index + 1}
                      </Typography>
                    </Box>
                    {/* Priority as separate field */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Priority:</strong>{" "}
                      {normalizePriority(action.priority)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Action:</strong> {action.action}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Timeline:</strong> {action.timeline}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Responsible:</strong> {action.responsible}
                    </Typography>
                  </Box>
                ),
              )}

              {/* Documentation Improvements */}
              {data.plan_of_correction?.documentation_improvements?.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      marginTop: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    Documentation Improvements
                  </Typography>
                  {data.plan_of_correction.documentation_improvements.map(
                    (doc: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#065b70",
                            marginBottom: "4px",
                          }}
                        >
                          {doc.improvement}
                        </Typography>
                        {Array.isArray(doc.implementation_steps) &&
                          doc.implementation_steps.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#374151",
                                lineHeight: 1.5,
                                marginBottom: "4px",
                              }}
                            >
                              <strong>Implementation Steps:</strong>{" "}
                              {doc.implementation_steps.join(", ")}
                            </Typography>
                          )}
                        {doc.timeline && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                            }}
                          >
                            <strong>Timeline:</strong> {doc.timeline}
                          </Typography>
                        )}
                      </Box>
                    ),
                  )}
                </>
              ) : null}

              {/* System Improvements */}
              {data.plan_of_correction?.system_improvements?.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      marginTop: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    System Improvements
                  </Typography>
                  {data.plan_of_correction.system_improvements.map(
                    (sys: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>System:</strong> {sys.system}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Improvement:</strong> {sys.improvement}
                        </Typography>
                        {sys.implementation_cost && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                              marginBottom: "4px",
                            }}
                          >
                            <strong>Implementation Cost:</strong>{" "}
                            {sys.implementation_cost}
                          </Typography>
                        )}
                        {sys.timeline && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                            }}
                          >
                            <strong>Timeline:</strong> {sys.timeline}
                          </Typography>
                        )}
                      </Box>
                    ),
                  )}
                </>
              ) : null}

              {/* Monitoring & Tracking */}
              {data.plan_of_correction?.monitoring_tracking?.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      marginTop: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    Monitoring & Tracking
                  </Typography>
                  {data.plan_of_correction.monitoring_tracking.map(
                    (m: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Metric:</strong> {m.metric}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Frequency:</strong> {m.frequency}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Responsible:</strong> {m.responsible_party}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Reporting Method:</strong>{" "}
                          {m.reporting_method}
                        </Typography>
                      </Box>
                    ),
                  )}
                </>
              ) : null}

              {/* Additional Plan Details */}
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#065b70",
                  marginTop: "16px",
                  marginBottom: "12px",
                }}
              >
                Training Requirements:
              </Typography>
              {data.plan_of_correction?.training_requirements?.map(
                (training: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      padding: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#065b70",
                        marginBottom: "4px",
                      }}
                    >
                      {training.training_topic}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Target Audience:</strong>{" "}
                      {training.target_audience}
                      <br />
                      <strong>Frequency:</strong> {training.frequency}
                      <br />
                      <strong>Duration:</strong> {training.duration}
                    </Typography>
                  </Box>
                ),
              )}

              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#065b70",
                  marginTop: "16px",
                  marginBottom: "12px",
                }}
              >
                Estimated Corrective Costs:
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  padding: "8px",
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", color: "#374151", lineHeight: 1.5 }}
                >
                  <strong>Immediate Costs:</strong>{" "}
                  {data.plan_of_correction?.corrective_costs?.immediate_costs ||
                    "N/A"}
                  <br />
                  <strong>Long-term Costs:</strong>{" "}
                  {data.plan_of_correction?.corrective_costs?.long_term_costs ||
                    "N/A"}
                  <br />
                  <strong>Total Estimated Cost:</strong>{" "}
                  {data.plan_of_correction?.corrective_costs
                    ?.total_estimated_cost || "N/A"}
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        )}

        {/* Recommendations Section */}
        {(() => {
          const recs = extractRecommendationsFromData(data as any);
          if (recs.length === 0) return null;

          return (
            <StyledAccordion defaultExpanded={false}>
              <AccordionSummary
                expandIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#737373"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                sx={{
                  minHeight: "45px",
                  borderRadius: "4px",
                  border: "1px solid #9EE5F4",
                  backgroundColor: "#FFF",
                  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                  "&.Mui-expanded": {
                    minHeight: "45px",
                    border: "1px solid #9EE5F4",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "7px 0",
                    flex: "1 1 auto",
                    width: "100%",
                    minWidth: 0,
                    "&.Mui-expanded": {
                      margin: "7px 0",
                    },
                  },
                  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                    transform: "rotate(180deg)",
                  },
                }}
              >
                <AccordionTitle component="div" className="accordion-title">
                  <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Recommendations</span>
                  </AccordionTitleLeading>
                  <AccordionCountBadge className="accordion-count-badge">
                    {recs.length} items
                  </AccordionCountBadge>
                </AccordionTitle>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "16px",
                  backgroundColor: "#ffffff",
                }}
              >
                {recs.map((recommendation: any, index: number) => {
                  const knownKeys = new Set([
                    "recommendation",
                    "title",
                    "priority",
                    "category",
                    "rationale",
                    "estimated_cost",
                    "cost",
                    "expected_outcome",
                    "outcome",
                    "implementation_timeline",
                  ]);
                  const extraEntries = Object.entries(
                    recommendation || {},
                  ).filter(
                    ([k, v]) =>
                      !knownKeys.has(k) &&
                      v !== undefined &&
                      v !== null &&
                      String(v) !== "",
                  );
                  return (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "#f3fdff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: "1px solid #737373",
                          paddingBottom: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#262626",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor:
                                normalizePriority(recommendation.priority) ===
                                "HIGH"
                                  ? "#dc2626"
                                  : normalizePriority(
                                        recommendation.priority,
                                      ) === "MEDIUM"
                                    ? "#f59e0b"
                                    : normalizePriority(
                                          recommendation.priority,
                                        ) === "LOW"
                                      ? "#10b981"
                                      : "#6b7280",
                              color: "white",
                              fontSize: "10px",
                              fontWeight: "bold",
                              padding: "2px 6px",
                              borderRadius: "4px",
                            }}
                          >
                            {normalizePriority(recommendation.priority)}
                          </Box>
                          Recommendation {index + 1}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                          fontWeight: 600,
                        }}
                      >
                        {recommendation.recommendation ||
                          recommendation.title ||
                          "Recommendation"}
                      </Typography>

                      {/* Priority as a separate field */}
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#374151",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Priority:</strong>{" "}
                        {normalizePriority(recommendation.priority)}
                      </Typography>

                      {recommendation.category && (
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "8px",
                          }}
                        >
                          <strong>Category:</strong> {recommendation.category}
                        </Typography>
                      )}

                      {recommendation.rationale && (
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "8px",
                          }}
                        >
                          <strong>Rationale:</strong> {recommendation.rationale}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        {(recommendation.estimated_cost ||
                          recommendation.cost) && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                            }}
                          >
                            <strong>Estimated Cost:</strong>{" "}
                            {recommendation.estimated_cost ||
                              recommendation.cost}
                          </Typography>
                        )}

                        {(recommendation.expected_outcome ||
                          recommendation.outcome) && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                              // Force this field to a new line in the wrapping flex row
                              flex: "0 0 100%",
                            }}
                          >
                            <strong>Expected Outcome:</strong>{" "}
                            {recommendation.expected_outcome ||
                              recommendation.outcome}
                          </Typography>
                        )}

                        {recommendation.implementation_timeline && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                            }}
                          >
                            <strong>Timeline:</strong>{" "}
                            {recommendation.implementation_timeline}
                          </Typography>
                        )}
                      </Box>

                      {/* Dynamic extra fields */}
                      {extraEntries.length > 0 && (
                        <Box
                          sx={{
                            marginTop: "6px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                          }}
                        >
                          {extraEntries.map(([k, v]) => (
                            <Typography
                              key={k}
                              sx={{
                                fontSize: "13px",
                                color: "#374151",
                                lineHeight: 1.5,
                              }}
                            >
                              <strong>{prettifyLabel(k)}:</strong>{" "}
                              {stringifyValue(v)}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </AccordionDetails>
            </StyledAccordion>
          );
        })()}
      </ReportDetailsContainer>
    );
  };

  const renderRiskRecentlyReviewedDocuments = () => {
    // Map analysis-history type to internal doc type
    const typeToDocTypeMap: Record<string, "incident" | "survey"> = {
      "Incident report": "incident",
      "Survey report": "survey",
    };

    const getFileName = (historyItem: any) => {
      if (historyItem.type === "Employee agreements") {
        return formatEmployeeAgreementRecentlyReviewedTitle(historyItem);
      }

      const metadata = historyItem?.data_obj?.metadata;
      if (metadata?.filename) return metadata.filename;

      // For Survey report entries, prefer a clean, generic filename if no explicit filename is stored
      if (historyItem.type === "Survey report") {
        const surveyData: any = historyItem.data_obj || {};
        const full = surveyData.full_analysis || {};
        const surveyDate =
          surveyData.survey_date || full.survey_metadata?.survey_date || "";
        const baseName = "Survey-Report";
        return surveyDate ? `${baseName}-${surveyDate}.pdf` : `${baseName}.pdf`;
      }

      const date = historyItem?.created_at
        ? new Date(historyItem.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        : new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          });
      const typeName = historyItem.type?.replace(/\s+/g, "-") || "Document";
      return `${typeName}-${date}.pdf`;
    };

    const getFileSize = (historyItem: any) => {
      const metadata = historyItem?.data_obj?.metadata;
      if (historyItem.type === "Employee agreements") {
        const fc = Number(metadata?.file_count) || 0;
        if (fc > 1 && metadata?.file_size != null) {
          return `${fc} files · ${formatFileSize(Number(metadata.file_size))} combined`;
        }
        if (metadata?.file_size != null) {
          return formatFileSize(Number(metadata.file_size));
        }
        return "N/A";
      }
      if (metadata?.file_size) return formatFileSize(metadata.file_size);
      return "N/A";
    };

    const getFileExtension = (fileName: string) => {
      const ext = fileName.split(".").pop()?.toLowerCase() || "pdf";
      return ext;
    };

    const getRowFileExtension = (historyItem: any) => {
      if (historyItem.type === "Employee agreements") {
        const m = historyItem?.data_obj?.metadata;
        const first =
          (Array.isArray(m?.filenames) && m.filenames[0]) ||
          (typeof m?.filename === "string" && m.filename.split(";")[0]?.trim()) ||
          "";
        return first ? getFileExtension(String(first)) : "pdf";
      }
      return getFileExtension(getFileName(historyItem));
    };

    const handleViewAnalysis = (historyItem: any) => {
      if (historyItem.type === "Employee agreements") {
        const raw = historyItem?.data_obj?.analysis;
        if (!raw) return;
        setRiskHistorySelectedAnalysis({
          success: true,
          message: "Employee agreement",
          analysis: raw as any,
          metadata: historyItem?.data_obj?.metadata || {},
        } as RiskAnalysisResponse);
        setRiskHistorySelectedType("employee");
        setRiskHistorySelectedId(historyItem?.id ?? null);
        return;
      }

      const docType = typeToDocTypeMap[historyItem.type];
      if (!docType) return;

      // Survey report history stores the raw survey payload in data_obj
      if (historyItem.type === "Survey report") {
        // AffableFLOW survey history is saved as { data_obj: { analysis, metadata } }.
        // Handle this shape first so Survey details render from stored analysis.
        const storedSurveyAnalysis = historyItem?.data_obj?.analysis;
        if (storedSurveyAnalysis) {
          const analysis: RiskAnalysisResponse = {
            success: true,
            message: "Survey analysis loaded from history",
            analysis: storedSurveyAnalysis,
            metadata: historyItem?.data_obj?.metadata || {},
          };
          setRiskHistorySelectedAnalysis(analysis);
          setRiskHistorySelectedType("survey");
          setRiskHistorySelectedId(historyItem?.id ?? null);
          return;
        }

        const surveyData: any = historyItem.data_obj || {};
        const full = surveyData.full_analysis || {};
        const responseTags =
          surveyData.deficiency_descriptions || full.deficiency_tags || [];
        const facilityType =
          surveyData.facility_type ||
          full.facility_type?.detected_type ||
          "Unknown";

        const mappedAnalysis: RiskAnalysisResponse = {
          success: true,
          message: "Survey analysis loaded from history",
          analysis: {
            document_type: full.document_type || "healthcare_facility_survey",
            analysis_date:
              full.analysis_date ||
              surveyData.survey_date ||
              new Date().toISOString().slice(0, 10),
            facility_info: {
              industry: "Healthcare",
              facility_type: facilityType,
            },
            incident_summary: {
              narrative:
                full.survey_summary?.key_findings ||
                "Survey report loaded from history",
              incident_type: "Survey",
              clinical_classification: "N/A",
              date:
                surveyData.survey_date ||
                full.survey_metadata?.survey_date ||
                new Date().toISOString().slice(0, 10),
              location: full.survey_metadata?.facility_name || "",
              description: "",
              parties_involved: [],
              injuries_damages: "",
              environmental_factors: "",
              reporting_timeliness: "",
              documentation_completeness: "",
            },
            compliance_deficiencies: [],
            potential_penalties: {
              penalty_table: [],
              penalty_charts: {
                estimated_penalty_exposure: {
                  min: "0",
                  max: "0",
                  most_likely: "0",
                },
                estimated_corrective_investment: {
                  immediate: "0",
                  long_term: "0",
                  total: "0",
                },
              },
            },
            plan_of_correction: {
              action_table: [],
              documentation_improvements: [],
              training_requirements: [],
              system_improvements: [],
              corrective_costs: {
                immediate_costs: "0",
                long_term_costs: "0",
                total_estimated_cost: "0",
              },
              monitoring_tracking: [],
            },
            risk_score: {
              overall_score: 0,
              score_breakdown: {
                documentation: 0,
                compliance: 0,
                safety: 0,
                financial_risk: 0,
              },
              risk_level: "LOW",
              issues_count: Array.isArray(responseTags)
                ? responseTags.length
                : 0,
              actions_count: 0,
              justification: "Survey report loaded from history",
            },
            executive_summary: full.survey_summary?.key_findings
              ? `Survey summary: ${full.survey_summary.key_findings}`
              : `Survey report with ${
                  Array.isArray(responseTags) ? responseTags.length : 0
                } deficiency tag(s)`,
            recommendations: { recommendation_table: [] },
          },
          metadata: {
            filename: "Survey-Report.pdf",
            file_size: 0,
            document_type: "survey_report",
            analysis_timestamp: new Date().toISOString(),
          },
        };

        (mappedAnalysis as any).analysis.survey_metadata =
          full.survey_metadata || {
            surveying_body:
              full.survey_metadata?.surveying_body ||
              "State Health Department (on behalf of CMS)",
            survey_date:
              surveyData.survey_date ||
              full.survey_metadata?.survey_date ||
              new Date().toISOString().slice(0, 10),
            facility_name: full.survey_metadata?.facility_name || "",
            survey_type: full.survey_metadata?.survey_type || "Standard Survey",
          };

        (mappedAnalysis as any).analysis.deficiency_tags = (
          full.deficiency_tags || []
        ).map((item: any) => ({
          tag: item.tag || "",
          description: item.description || "",
          severity: item.severity || "MEDIUM",
          category: item.category || "",
          page_reference: item.page_reference || "",
        }));

        setRiskHistorySelectedAnalysis(mappedAnalysis);
        setRiskHistorySelectedType("survey");
        setRiskHistorySelectedId(historyItem?.id ?? null);
        return;
      }

      // Default path for incident / other entries that store analysis in data_obj.analysis
      const analysisData = historyItem?.data_obj?.analysis;
      if (!analysisData) return;

      const analysis: RiskAnalysisResponse = {
        success: true,
        message: "Analysis loaded from history",
        analysis: analysisData,
        metadata: historyItem?.data_obj?.metadata || {},
      };

      setRiskHistorySelectedAnalysis(analysis);
      setRiskHistorySelectedType(docType);
      setRiskHistorySelectedId(historyItem?.id ?? null);
    };

    if (riskHistoryLoading) {
      return (
        <StyledAccordion
          expanded={tabExpandedPanel === "recentlyReviewed"}
          onChange={handleTabAccordionChange("recentlyReviewed")}
        >
          <AccordionSummary>
            <AccordionTitle className="accordion-title">
              Recently Reviewed Documents
            </AccordionTitle>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#065B70" }} />
            </Box>
          </AccordionDetails>
        </StyledAccordion>
      );
    }

    return (
      <StyledAccordion
        expanded={tabExpandedPanel === "recentlyReviewed"}
        onChange={handleTabAccordionChange("recentlyReviewed")}
      >
        <AccordionSummary
          expandIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#262626"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          aria-controls="risk-recently-reviewed-content"
          id="risk-recently-reviewed-header"
          sx={{
            alignItems: "center",
            borderRadius: "4px",
            border: "1px solid #9EE5F4",
            backgroundColor: "#FFF",
            boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(180deg)",
            },
          }}
        >
          <AccordionIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15 12.5854C15.5826 12.7913 16 13.3469 16 14V14.5C16 15.6046 15.1046 16.5 14 16.5H6C4.89543 16.5 4 15.6046 4 14.5V9C4 8.34689 4.4174 7.79127 5 7.58535V6C5 5.17157 5.67157 4.5 6.5 4.5H11.5C12.3284 4.5 13 5.17157 13 6V12.5H14L13.9997 6.58525C14.5825 6.79109 15 7.34678 15 8V12.5854ZM12 11.7929V6C12 5.72386 11.7761 5.5 11.5 5.5H6.5C6.22386 5.5 6 5.72386 6 6V7.5H7.08579C7.48361 7.5 7.86514 7.65804 8.14645 7.93934L12 11.7929ZM5 9V14.5C5 15.0523 5.44772 15.5 6 15.5H14C14.5523 15.5 15 15.0523 15 14.5V14C15 13.7239 14.7761 13.5 14.5 13.5H12.9142C12.5164 13.5 12.1349 13.342 11.8536 13.0607L7.43934 8.64645C7.34557 8.55268 7.21839 8.5 7.08579 8.5H5.5C5.22386 8.5 5 8.72386 5 9Z"
                fill="#065B70"
              />
            </svg>
          </AccordionIcon>
          <AccordionTitle className="accordion-title">
            Recently Reviewed Documents
          </AccordionTitle>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: "#ffffff",
            padding: "12px",
            paddingTop: "16px !important",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {(() => {
            const documentTypes = [
              { type: "Incident report", label: "Incident Report" },
              { type: "Survey report", label: "Survey Report" },
              ...(canAccessEmployeeAgreements
                ? [
                    {
                      type: "Employee agreements",
                      label: "Employee Agreements",
                    },
                  ]
                : []),
            ];

            if (riskHistory.length === 0) {
              return (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#737373",
                    fontFamily: "Rethink Sans, sans-serif",
                    padding: "8px",
                    textAlign: "center",
                  }}
                >
                  No documents reviewed yet
                </Typography>
              );
            }

            const groupedHistory = documentTypes.map((docType) => ({
              ...docType,
              documents: riskHistory.filter(
                (item) => item.type === docType.type,
              ),
            }));

            const sections = groupedHistory.map((section, sectionIndex) => {
              const hasDocuments = section.documents.length > 0;

              let page = 1;
              let rowsPerPage = 5;
              let setPage: (v: number) => void = () => {};
              let setRowsPerPage: (v: number) => void = () => {};
              let totalItems = 0;

              if (section.type === "Incident report") {
                page = riskHistoryPageIncident;
                rowsPerPage = riskHistoryRowsPerPageIncident;
                setPage = setRiskHistoryPageIncident;
                setRowsPerPage = setRiskHistoryRowsPerPageIncident;
                totalItems = riskHistoryTotalIncident;
              } else if (section.type === "Survey report") {
                page = riskHistoryPageSurvey;
                rowsPerPage = riskHistoryRowsPerPageSurvey;
                setPage = setRiskHistoryPageSurvey;
                setRowsPerPage = setRiskHistoryRowsPerPageSurvey;
                totalItems = riskHistoryTotalSurvey;
              } else if (section.type === "Employee agreements") {
                page = riskHistoryPageEmployee;
                rowsPerPage = riskHistoryRowsPerPageEmployee;
                setPage = setRiskHistoryPageEmployee;
                setRowsPerPage = setRiskHistoryRowsPerPageEmployee;
                totalItems = riskHistoryTotalEmployee;
              }

              const totalPages =
                totalItems > 0 ? Math.ceil(totalItems / rowsPerPage) : 1;
              const currentPage = Math.min(page, totalPages);
              // Data is already server-side paginated, no client-side slicing needed
              const pageDocuments = section.documents;

              const isExpanded =
                riskHistoryExpandedTypes[section.type] !== undefined
                  ? riskHistoryExpandedTypes[section.type]
                  : true;

              return (
                <Box
                  key={section.type}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      borderRadius: isExpanded ? "4px 4px 0 0" : "4px",
                      backgroundColor: hasDocuments ? "#0A829D" : "#F5F5F5",
                      border: hasDocuments
                        ? "1px solid #0A829D"
                        : "1px solid #E0E0E0",
                      cursor: hasDocuments ? "pointer" : "default",
                      opacity: hasDocuments ? 1 : 0.6,
                    }}
                    onClick={() => {
                      if (!hasDocuments) return;
                      setRiskHistoryExpandedTypes((prev) => ({
                        ...prev,
                        [section.type]: !prev[section.type],
                      }));
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: hasDocuments ? "#FFFFFF" : "#737373",
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      {section.label}
                    </Typography>
                    {hasDocuments && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke={hasDocuments ? "#FFFFFF" : "#065B70"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </Box>

                  {hasDocuments && isExpanded && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        border: "1px solid #99d4e1",
                        borderTop: "none",
                        borderRadius: "0 0 8px 8px",
                        padding: "12px",
                      }}
                    >
                      {pageDocuments.map((historyItem, index) => {
                        const displayName = getFileName(historyItem);
                        const sizeLabel = getFileSize(historyItem);
                        const fileExt = getRowFileExtension(historyItem);
                        const { analyzedLabel, showUploaded } =
                          getHistoryRowAnalyzedAndUploadLabels(historyItem);
                        const hasAnalysis =
                          historyItem.type === "Survey report"
                            ? Boolean(historyItem.data_obj)
                            : Boolean(historyItem?.data_obj?.analysis);
                        const isSelected =
                          riskHistorySelectedId !== null &&
                          riskHistorySelectedId === historyItem.id;

                        return (
                          <React.Fragment
                            key={historyItem.id || `${sectionIndex}-${index}`}
                          >
                            <FileDisplayContainer>
                              <FileInfoContainer>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "24px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "16px",
                                      fontWeight: 700,
                                      color: "#065B70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {index + 1}
                                  </Typography>
                                </Box>
                                <FileIcon>
                                  <ComplianceHistoryFileGlyph />
                                </FileIcon>
                                <FileDetails>
                                  <FileName
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    {displayName}
                                  </FileName>
                                  <FileSize>
                                    {fileExt} | {sizeLabel}
                                    {analyzedLabel && (
                                      <>
                                        {" "}
                                        |{" "}
                                        <span
                                          style={{ textTransform: "none" }}
                                        >
                                          Analyzed: {analyzedLabel}
                                        </span>
                                      </>
                                    )}
                                    {showUploaded &&
                                      historyItem?.created_at && (
                                        <>
                                          {" "}
                                          |{" "}
                                          <span
                                            style={{ textTransform: "none" }}
                                          >
                                            Uploaded:{" "}
                                            {formatClientUploadTimestamp(
                                              new Date(historyItem.created_at),
                                            )}
                                          </span>
                                        </>
                                      )}
                                  </FileSize>
                                </FileDetails>
                              </FileInfoContainer>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                {hasAnalysis && (
                                  <>
                                    <DeleteButton
                                      aria-label="Delete recently reviewed document"
                                      onClick={async (event) => {
                                        event.stopPropagation();
                                        if (!historyItem.id) return;
                                        try {
                                          await continuousComplianceService.deleteAnalysisHistoryEntry(
                                            historyItem.id,
                                          );
                                          setRiskHistory((prev) =>
                                            prev.filter(
                                              (item) =>
                                                item.id !== historyItem.id,
                                            ),
                                          );
                                          if (
                                            riskHistorySelectedId ===
                                            historyItem.id
                                          ) {
                                            setRiskHistorySelectedAnalysis(
                                              null,
                                            );
                                            setRiskHistorySelectedType(null);
                                            setRiskHistorySelectedId(null);
                                          }
                                          toast.success(
                                            "Recently reviewed analysis removed successfully.",
                                          );
                                        } catch (error) {
                                          console.error(
                                            "Failed to delete recently reviewed analysis:",
                                            error,
                                          );
                                          toast.error(
                                            "Failed to delete recently reviewed analysis. Please try again.",
                                          );
                                        }
                                      }}
                                    >
                                      <DeleteFileIcon />
                                    </DeleteButton>

                                    <ViewReportButton
                                      onClick={() => {
                                        if (isSelected) {
                                          setRiskHistorySelectedAnalysis(null);
                                          setRiskHistorySelectedType(null);
                                          setRiskHistorySelectedId(null);
                                        } else {
                                          handleViewAnalysis(historyItem);
                                        }
                                      }}
                                    >
                                      {isSelected
                                        ? "Hide Report"
                                        : "View Analysis"}
                                    </ViewReportButton>
                                  </>
                                )}
                              </Box>
                            </FileDisplayContainer>

                            {isSelected &&
                              riskHistorySelectedAnalysis &&
                              (riskHistorySelectedType === "survey"
                                ? renderSurveyReportDetails(
                                    riskHistorySelectedAnalysis,
                                    "survey",
                                  )
                                : riskHistorySelectedType === "employee"
                                  ? (() => {
                                      const empPayload =
                                        riskHistorySelectedAnalysis as {
                                          analysis?: Record<string, unknown>;
                                          metadata?: EmployeeAgreementAnalysisResponse["metadata"];
                                        };
                                      const empAnalysis = empPayload?.analysis;
                                      return empAnalysis ? (
                                        <Box sx={{ width: "100%", mt: 1 }}>
                                          <EmployeeAgreementResults
                                            analysis={empAnalysis}
                                            loading={false}
                                            statusFiles={[]}
                                            statusFileResults={
                                              empPayload.metadata
                                                ?.file_results ?? null
                                            }
                                          />
                                        </Box>
                                      ) : null;
                                    })()
                                  : renderReportDetails(
                                      riskHistorySelectedAnalysis,
                                      "incident",
                                    ))}
                          </React.Fragment>
                        );
                      })}

                      {/* Section-level pagination row at bottom (per type) */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          mt: 1,
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#4b5563",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Rows per page
                          </Typography>
                          <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                              const value = Number(e.target.value) || 10;
                              setRowsPerPage(value);
                              setPage(1);
                            }}
                            size="small"
                            sx={{
                              fontSize: 12,
                              height: 28,
                              "& .MuiSelect-select": {
                                padding: "4px 8px",
                              },
                            }}
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                          </Select>
                        </Box>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, value) => setPage(value)}
                          size="small"
                          sx={{
                            "& .MuiPaginationItem-root": {
                              fontSize: 12,
                              height: 28,
                              minWidth: 28,
                              padding: "0 6px",
                            },
                            "& .MuiPaginationItem-root.Mui-selected": {
                              backgroundColor: "#0a829d",
                              color: "#ffffff",
                            },
                            "& .MuiPaginationItem-root.Mui-selected:hover": {
                              backgroundColor: "#0a829d",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            });

            return <>{sections}</>;
          })()}
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  // Fetch SOM search details for a specific tag
  const fetchTagDetails = async (tag: string) => {
    if (tagDetails[tag] || loadingTags[tag]) return; // Skip if already loaded or loading

    setLoadingTags((prev) => ({ ...prev, [tag]: true }));

    try {
      const result = await riskAnalysisService.somSearch({
        query: tag,
        category: "VectorStores_usa_healthcare_manual_som107ap_g_rhc",
        customer_id: "1",
        org_name: "test",
      });

      // Validate the response has some content
      if (result && (result.result || result.structured)) {
        setTagDetails((prev) => ({ ...prev, [tag]: result }));
      } else {
        console.warn(`SOM search returned empty result for tag ${tag}`);
        // Set a fallback response
        setTagDetails((prev) => ({
          ...prev,
          [tag]: {
            result: `No detailed information found for ${tag}. This may be a general compliance requirement.`,
            structured: null,
          },
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch details for tag ${tag}:`, error);
      // Set error state
      setTagDetails((prev) => ({
        ...prev,
        [tag]: {
          result: `Error loading details for ${tag}. Please try again later.`,
          structured: null,
          error: true,
        },
      }));
    } finally {
      setLoadingTags((prev) => ({ ...prev, [tag]: false }));
    }
  };

  // Fetch details when accordion expands
  const handleSurveyAccordionChange =
    (tag: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        fetchTagDetails(tag);
      }
    };

  const parseSomResponse = (somData: any, surveySummary: any) => {
    const resultText = somData?.result || "";
    const structured = somData?.structured || {};

    // Check if we have structured data or just raw text
    const hasStructuredData = structured && Object.keys(structured).length > 0;

    // Extract basic info from result text if structured data is incomplete
    const extractFromText = (text: string) => {
      // Look for J-tag pattern like "J-0042" or "J0042"
      const jTagMatch = text.match(/J-?(\d+)/);
      const jTag = jTagMatch ? `J-${jTagMatch[1]}` : "";

      // Look for CFR citation pattern like "§ 491.4"
      const cfrMatch = text.match(/§\s*(\d+\.\d+)/);
      const cfrCitation = cfrMatch ? `§ ${cfrMatch[1]}` : "";

      // Look for revision info
      const revMatch = text.match(/Rev\.\s*(\d+)/);
      const version = revMatch ? `Rev. ${revMatch[1]}` : "";

      return { jTag, cfrCitation, version };
    };

    const textInfo = extractFromText(resultText);

    // Parse the structured response format with fallbacks
    return {
      description:
        structured?.requirement?.text ||
        (structured?.interpretive_guidelines?.summary
          ? `${structured.interpretive_guidelines.summary}\n\n${resultText}`
          : resultText) ||
        "No description available",
      cfrCitation:
        structured?.authority?.citation || textInfo.cfrCitation || "N/A",
      version: structured?.version?.rev || textInfo.version || "N/A",
      procedures: Array.isArray(structured?.survey_procedures)
        ? structured.survey_procedures.map((procedure: any) =>
            typeof procedure === "string"
              ? { step: procedure, evidence_type: "text" }
              : procedure,
          )
        : [],
      requirements:
        structured?.interpretive_guidelines?.controls_expected || [],
      expectedControls: Array.isArray(
        structured?.interpretive_guidelines?.controls_expected,
      )
        ? structured.interpretive_guidelines.controls_expected.map(
            (control: any) =>
              control?.description ||
              (typeof control === "string" ? control : JSON.stringify(control)),
          )
        : [],
      summary:
        structured?.interpretive_guidelines?.summary ||
        (resultText.includes("Interpretative Guidelines")
          ? resultText
              .split("Interpretative Guidelines")[1]
              ?.split("\n")[0]
              ?.trim()
          : surveySummary?.key_findings) ||
        "No Summary Available",
      scope: structured?.scope || structured?.requirement?.scope || null,
      jurisdiction: structured?.authority?.jurisdiction || "",
      instrument:
        structured?.authority?.instrument || "CMS State Operations Manual",
      interpretiveGuidelines:
        structured?.interpretive_guidelines?.verbatim ||
        (resultText.includes("Interpretative Guidelines")
          ? resultText.split("Interpretative Guidelines")[1]?.substring(0, 1000)
          : ""),
      hasStructuredData, // Flag to control UI display
    };
  };

  // Render Survey Report Details (Figma Design)
  const renderSurveyReportDetails = (
    analysis: RiskAnalysisResponse,
    type: "survey",
  ) => {
    const data: any = (analysis as any)?.analysis || {};
    const deficiencyTags = data?.deficiency_tags || [];
    const surveySummary = data?.survey_summary || {};
    const surveyLlmDebug = getRiskSectionLlmDebug(
      analysis,
      RISK_LLM_SECTION.surveyReport,
    );

    return (
      <ReportDetailsContainer>
        {/* <ReportHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <rect
                  x="5"
                  y="3"
                  width="10"
                  height="14"
                  rx="1"
                  stroke="#065b70"
                  strokeWidth="1.5"
                />
                <path
                  d="M8 7H12M8 10H12M8 13H10"
                  stroke="#065b70"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </Box>
            <ReportHeaderInfo>
              <ReportTitle>Survey Reports</ReportTitle>
              <FacilityInfo>
                Facility:{" "}
                {facilityData?.site_name || "Evergreen Health Medical Center"} –{" "}
                {facilityData?.city || "Kirkland"},{" "}
                {facilityData?.state || "WA"}
              </FacilityInfo>
              <LastUpdated sx={{ textAlign: "left", alignSelf: "flex-start" }}>
                Last updated:{" "}
                {analysis.metadata.analysis_timestamp
                  ? new Date(
                      analysis.metadata.analysis_timestamp
                    ).toLocaleDateString("en-US", {
                      month: "long",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : getUploadDate(type)}
              </LastUpdated>
            </ReportHeaderInfo>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "8px",
            }}
          >
            <CloseButton onClick={() => toggleReport(type)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M9 3L3 9M3 3L9 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
            <DownloadButton
              onClick={() => DownloadReport(type, analysis)}
              sx={{
                backgroundColor: "#e6fafc",
                color: "#086e86",
                border: "1px solid #5bbfd6",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{ marginRight: 4 }}
              >
                <path
                  d="M8 12V3M8 12L5 9M8 12L11 9M3 13H13"
                  stroke="#086e86"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Download
            </DownloadButton>
          </Box>
        </ReportHeader> */}

        {/* Action Buttons – match Incident Report (Download with PDF/DOCX menu) */}
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            marginBottom: "12px",
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <DownloadButton
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            endIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Download
          </DownloadButton>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                minWidth: "120px",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                DownloadReport(type, analysis);
                setDownloadMenuAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                padding: "10px 16px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.49976 9.00299C4.22361 9.00299 3.99976 9.22685 3.99976 9.50299V11.503C3.99976 11.7791 4.22361 12.003 4.49976 12.003C4.7759 12.003 4.99976 11.7791 4.99976 11.503V11.3366H5.33296C5.97736 11.3366 6.49976 10.8142 6.49976 10.1698C6.49976 9.52538 5.97736 9.00299 5.33296 9.00299H4.49976ZM5.33296 10.3366H4.99976V10.003H5.33296C5.42508 10.003 5.49976 10.0777 5.49976 10.1698C5.49976 10.2619 5.42508 10.3366 5.33296 10.3366ZM10.0005 9.50236C10.0008 9.22646 10.2246 9.00299 10.5005 9.00299H11.4988C11.7749 9.00299 11.9988 9.22685 11.9988 9.50299C11.9988 9.77913 11.7749 10.003 11.4988 10.003H10.9998L10.9994 10.3375H11.4988C11.7749 10.3375 11.9988 10.5613 11.9988 10.8375C11.9988 11.1136 11.7749 11.3375 11.4988 11.3375H11L11.0005 11.5017C11.0012 11.7779 10.7779 12.0023 10.5017 12.003C10.2256 12.0037 10.0012 11.7804 10.0005 11.5043L9.99878 10.8368L10.0005 9.50236ZM7.49829 9.00299C7.22215 9.00299 6.99829 9.22685 6.99829 9.50299V11.503C6.99829 11.7791 7.22215 12.003 7.49829 12.003H7.99974C8.82816 12.003 9.49973 11.3314 9.49974 10.503C9.49974 9.67457 8.82817 9.00299 7.99974 9.00299H7.49829ZM7.99829 11.003V10.003H7.99974C8.27588 10.003 8.49974 10.2269 8.49974 10.503C8.49974 10.7791 8.27588 11.003 7.99974 11.003H7.99829ZM3 3.50195V7.08413C2.4174 7.29005 2 7.84567 2 8.49878V12.502C2 13.3304 2.67157 14.002 3.5 14.002H12.5C13.3284 14.002 14 13.3304 14 12.502V8.49878C14 7.84567 13.5826 7.29005 13 7.08413V5.4133C13 5.0149 12.8415 4.63287 12.5595 4.35147L10.6439 2.44012C10.3627 2.15953 9.98167 2.00195 9.58442 2.00195H4.5C3.67157 2.00195 3 2.67353 3 3.50195ZM4.5 3.00195H9V4.49994C9 5.32837 9.67157 5.99994 10.5 5.99994H12V6.99878H4V3.50195C4 3.22581 4.22386 3.00195 4.5 3.00195ZM10 3.21029L11.7936 4.99994H10.5C10.2239 4.99994 10 4.77608 10 4.49994V3.21029ZM3.5 7.99878H12.5C12.7761 7.99878 13 8.22264 13 8.49878V12.502C13 12.7781 12.7761 13.002 12.5 13.002H3.5C3.22386 13.002 3 12.7781 3 12.502V8.49878C3 8.22264 3.22386 7.99878 3.5 7.99878Z"
                  fill="#262626"
                />
              </svg>
              PDF
            </MenuItem>
            <MenuItem
              onClick={() => {
                DownloadDocxReport(type, analysis);
                setDownloadMenuAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                padding: "10px 16px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 3C3 1.89543 3.89543 1 5 1H8.58579C8.98361 1 9.36514 1.15804 9.64645 1.43934L12.5607 4.35355C12.842 4.63486 13 5.01639 13 5.41421V13C13 14.1046 12.1046 15 11 15H5C3.89543 15 3 14.1046 3 13V3ZM5 2C4.44772 2 4 2.44772 4 3V13C4 13.5523 4.44772 14 5 14H11C11.5523 14 12 13.5523 12 13V6H9.5C8.67157 6 8 5.32843 8 4.5V2H5ZM9.5 5H11.7929L9 2.20711V4.5C9 4.77614 9.22386 5 9.5 5ZM5.5 8C5.22386 8 5 8.22386 5 8.5C5 8.77614 5.22386 9 5.5 9H10.5C10.7761 9 11 8.77614 11 8.5C11 8.22386 10.7761 8 10.5 8H5.5ZM5 10.5C5 10.2239 5.22386 10 5.5 10H10.5C10.7761 10 11 10.2239 11 10.5C11 10.7761 10.7761 11 10.5 11H5.5C5.22386 11 5 10.7761 5 10.5ZM5.5 12C5.22386 12 5 12.2239 5 12.5C5 12.7761 5.22386 13 5.5 13H10.5C10.7761 13 11 12.7761 11 12.5C11 12.2239 10.7761 12 10.5 12H5.5Z"
                  fill="#262626"
                />
              </svg>
              Docx
            </MenuItem>
          </Menu>
          {affableUploadContext && (
            <UploadActionButton
              onClick={handleAffableResultUploadButtonClick}
              disabled={isUploadingAffableResult}
              sx={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              {isUploadingAffableResult ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#0a829d" }} />
                  Uploading...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.33333 9.66667V5.276L5.47133 7.13867L4.52867 6.196L8 2.72467L11.4713 6.196L10.5287 7.13867L8.66667 5.276V9.66667H7.33333ZM4 12.3333H12V13.6667H4V12.3333Z"
                      fill="#065B70"
                    />
                  </svg>
                  Upload to AffableFLOW
                </>
              )}
            </UploadActionButton>
          )}
        </Box>

        {/* Survey Cards - Based on deficiency_tags */}
        <Box sx={{ marginTop: "8px", width: "100%", maxWidth: "100%" }}>
          {deficiencyTags && deficiencyTags.length > 0 ? (
            deficiencyTags.map((deficiency: any, index: number) => {
              const tag = deficiency.tag;
              const somData = tagDetails[tag];
              const isLoading = loadingTags[tag];
              const parsedData = somData
                ? parseSomResponse(somData, surveySummary)
                : null;

              return (
                <SurveyCard
                  key={tag}
                  defaultExpanded={false}
                  onChange={handleSurveyAccordionChange(tag)}
                >
                  <SurveyCardSummary
                    expandIcon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="#262626"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                    sx={{
                      "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                        transform: "rotate(180deg)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "20px",
                        height: "20px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M10 6V10L12 12"
                          stroke="#065b70"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="10"
                          cy="10"
                          r="6"
                          stroke="#065b70"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </Box>
                    <SurveyCardTitle className="accordion-title" title={`${tag} | ${deficiency.description || deficiency.category || "Survey finding"}`}>
                      {tag} |{" "}
                      {deficiency.description ||
                        deficiency.category ||
                        "Survey finding"}
                    </SurveyCardTitle>
                    <Box
                      sx={{ flexShrink: 0, marginLeft: "auto" }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <GlobalAdminLlmPromptButton
                        dialogTitle={`${tag} — survey extraction LLM prompt`}
                        promptText={surveyLlmDebug.prompt}
                        wallTimeSec={surveyLlmDebug.wallSec}
                      />
                    </Box>
                  </SurveyCardSummary>
                  <SurveyCardDetails>
                    {isLoading ? (
                      <Box sx={{ padding: "24px", textAlign: "center" }}>
                        <CircularProgress size={24} sx={{ color: "#065b70" }} />
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#065b70",
                            marginTop: "8px",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          Loading details for {tag}...
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: "1px solid #e5e7eb",
                          backgroundColor: "#f3fdff",
                          borderRadius: "4px",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        {/* Main Description */}
                        {/* <Box sx={{ padding: "8px" }}>
                          <Box sx={{ padding: "0 16px", marginBottom: "8px" }}>
                            <SurveySectionTitle>{tag} :</SurveySectionTitle>
                          </Box>
                          <SurveyTextBox>
                            {parsedData?.description ||
                              deficiency.description ||
                              "No description available"}
                          </SurveyTextBox>
                        </Box> */}

                        {/* CFR Citation - only show if structured data available */}
                        {parsedData?.hasStructuredData &&
                          parsedData?.cfrCitation &&
                          parsedData.cfrCitation !== "N/A" && (
                            <SurveyInfoRow>
                              <SurveyInfoLabel>CFR Citation :</SurveyInfoLabel>
                              <SurveyInfoValue>
                                {parsedData.cfrCitation}
                              </SurveyInfoValue>
                            </SurveyInfoRow>
                          )}

                        {/* Version - only show if structured data available */}
                        {parsedData?.hasStructuredData &&
                          parsedData?.version &&
                          parsedData.version !== "N/A" && (
                            <SurveyInfoRow>
                              <SurveyInfoLabel>Version :</SurveyInfoLabel>
                              <SurveyInfoValue>
                                {parsedData.version}
                              </SurveyInfoValue>
                            </SurveyInfoRow>
                          )}

                        {/* Summary Section - only show if structured data available */}
                        {parsedData?.hasStructuredData && (
                          <Box sx={{ padding: "12px" }}>
                            <SurveySubSectionTitle>
                              Interpretive Guidelines
                            </SurveySubSectionTitle>
                            <Box sx={{ padding: "8px" }}>
                              <SurveySummaryRow>
                                <SurveySummaryLabel>
                                  Summary:
                                </SurveySummaryLabel>
                                <SurveySummaryValue>
                                  {parsedData?.summary ||
                                    "No Summary Available"}
                                </SurveySummaryValue>
                              </SurveySummaryRow>
                              <Box sx={{ marginTop: "12px" }}>
                                <GuidelinesAccordion>
                                  <GuidelinesAccordionSummary
                                    expandIcon={
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                      >
                                        <path
                                          d="M5 7.5L10 12.5L15 7.5"
                                          stroke="#262626"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    }
                                    sx={{
                                      "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded":
                                        {
                                          transform: "rotate(180deg)",
                                        },
                                    }}
                                  >
                                    <GuidelinesTitle className="guidelines-title">
                                      View Full Guidelines
                                    </GuidelinesTitle>
                                  </GuidelinesAccordionSummary>
                                  <AccordionDetails>
                                    <Box
                                      sx={{
                                        backgroundColor: "#fdffff",
                                        border: "1px solid #e5f3f6",
                                        borderLeft: "2px solid #35d7e4",
                                        borderRadius: "4px",
                                        padding: "12px 12px 8px 16px",
                                        color: "#065b70",
                                        fontSize: "14px",
                                        fontFamily: "Inter, sans-serif",
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {parsedData?.interpretiveGuidelines?.trim() ||
                                        "No detailed guidelines available"}
                                    </Box>
                                  </AccordionDetails>
                                </GuidelinesAccordion>
                              </Box>
                            </Box>
                          </Box>
                        )}

                        {/* Expected Controls Section */}
                        {parsedData?.expectedControls &&
                          parsedData.expectedControls.length > 0 && (
                            <Box sx={{ padding: "8px" }}>
                              <SurveySubSectionTitle>
                                Expected Controls
                              </SurveySubSectionTitle>
                              {parsedData.expectedControls.map(
                                (control: string, controlIdx: number) => (
                                  <Box
                                    key={controlIdx}
                                    sx={{
                                      backgroundColor: "#f4feff",
                                      padding: "8px 4px",
                                      borderRadius: "8px",
                                      marginBottom:
                                        controlIdx <
                                        parsedData.expectedControls.length - 1
                                          ? "8px"
                                          : 0,
                                    }}
                                  >
                                    <SurveyListItem sx={{ padding: "2px" }}>
                                      <SurveyListNumber>
                                        {controlIdx + 1}
                                      </SurveyListNumber>
                                      <SurveyListText>{control}</SurveyListText>
                                    </SurveyListItem>
                                  </Box>
                                ),
                              )}
                            </Box>
                          )}

                        {/* Survey Procedures Section */}
                        {parsedData?.procedures &&
                          parsedData.procedures.length > 0 && (
                            <Box sx={{ padding: "12px 12px 8px 12px" }}>
                              <SurveySubSectionTitle>
                                Survey Procedures
                              </SurveySubSectionTitle>
                              <SurveyListBox
                                sx={{ backgroundColor: "#f4feff" }}
                              >
                                {parsedData.procedures.map(
                                  (procedure: any, procIdx: number) => (
                                    <Box key={procIdx}>
                                      <SurveyListItem>
                                        <SurveyListNumber>
                                          {procIdx + 1}
                                        </SurveyListNumber>
                                        <SurveyListText>
                                          {procedure.step || procedure}
                                        </SurveyListText>
                                      </SurveyListItem>
                                      {/* {procedure.evidence_type && (
                                        <SurveyListItem
                                          sx={{ paddingLeft: "48px" }}
                                        >
                                          <SurveyInfoLabel>
                                            {" "}
                                            Evidence Type:
                                          </SurveyInfoLabel>
                                          <SurveyListText>
                                            {procedure.evidence_type}
                                          </SurveyListText>
                                        </SurveyListItem>
                                      )} */}
                                    </Box>
                                  ),
                                )}
                              </SurveyListBox>
                            </Box>
                          )}

                        {/* Requirement Section - only show if structured data available */}
                        {parsedData?.hasStructuredData &&
                          ((parsedData?.requirements &&
                            parsedData.requirements.length > 0) ||
                            parsedData?.description ||
                            parsedData?.scope) && (
                            <Box sx={{ padding: "12px 12px 8px 12px" }}>
                              <SurveySubSectionTitle sx={{ fontSize: "14px" }}>
                                Requirement
                              </SurveySubSectionTitle>
                              <Box
                                sx={{
                                  backgroundColor: "#f4feff",
                                  padding: "8px 4px",
                                  borderRadius: "8px",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                {/* Requirement Text */}
                                {parsedData?.description && (
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      fontFamily: "Inter, sans-serif",
                                      lineHeight: "20px",
                                      padding: "0 4px",
                                    }}
                                  >
                                    {parsedData.description}
                                  </Typography>
                                )}

                                {/* Scope */}
                                {parsedData?.scope && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: "4px",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        padding: "0 4px 0 0",
                                        width: "auto",
                                        minWidth: "100px",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          color: "#065b70",
                                          fontFamily: "Inter, sans-serif",
                                          lineHeight: "20px",
                                        }}
                                      >
                                        Scope :
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        backgroundColor: "#fafafa",
                                        border: "1px solid #fafafa",
                                        borderRadius: "4px",
                                        padding: "8px 12px",
                                        flex: 1,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          color: "#065b70",
                                          fontFamily: "Inter, sans-serif",
                                          lineHeight: "20px",
                                        }}
                                      >
                                        {parsedData.scope}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}

                        {/* Authority Section - only show if structured data available */}
                        {parsedData?.hasStructuredData &&
                          (parsedData?.jurisdiction ||
                            parsedData?.instrument ||
                            parsedData?.cfrCitation) && (
                            <Box sx={{ padding: "8px" }}>
                              <AuthoritySection>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                    padding: "0 0 8px 0",
                                  }}
                                >
                                  Authority
                                </Typography>
                                <Box
                                  sx={{
                                    backgroundColor: "#fffcf8",
                                    padding: "8px 4px",
                                    borderRadius: "2px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                  }}
                                >
                                  {parsedData?.jurisdiction && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                        padding: "2px 0",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          padding: "6px 0",
                                          width: "200px",
                                          display: "flex",
                                          gap: "2px",
                                          alignItems: "center",
                                          color: "#065b70",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontFamily: "Inter, sans-serif",
                                            lineHeight: "20px",
                                          }}
                                        >
                                          Jurisdiction
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "18px",
                                            fontWeight: 600,
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                            lineHeight: "1.5",
                                            letterSpacing: "-0.18px",
                                          }}
                                        >
                                          :
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          backgroundColor: "#fcfeff",
                                          border: "1px solid #fff4e5",
                                          borderRadius: "4px",
                                          padding: "4px 12px",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#065b70",
                                            fontFamily: "Inter, sans-serif",
                                            lineHeight: "20px",
                                          }}
                                        >
                                          {parsedData.jurisdiction}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  {parsedData?.instrument && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                        padding: "2px 0",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          padding: "6px 0",
                                          width: "200px",
                                          display: "flex",
                                          gap: "2px",
                                          alignItems: "center",
                                          color: "#065b70",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontFamily: "Inter, sans-serif",
                                            lineHeight: "20px",
                                          }}
                                        >
                                          Instrument
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "18px",
                                            fontWeight: 600,
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                            lineHeight: "1.5",
                                            letterSpacing: "-0.18px",
                                          }}
                                        >
                                          :
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          backgroundColor: "#fcfeff",
                                          border: "1px solid #fff4e5",
                                          borderRadius: "4px",
                                          padding: "4px 12px",
                                          width: "100%",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: "#065b70",
                                            fontFamily: "Inter, sans-serif",
                                            lineHeight: "20px",
                                          }}
                                        >
                                          {parsedData.instrument}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  {parsedData?.cfrCitation &&
                                    parsedData.cfrCitation !== "N/A" && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: "8px",
                                          alignItems: "center",
                                          padding: "2px 0",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            padding: "6px 0",
                                            width: "200px",
                                            display: "flex",
                                            gap: "2px",
                                            alignItems: "center",
                                            color: "#065b70",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontFamily: "Inter, sans-serif",
                                              lineHeight: "20px",
                                            }}
                                          >
                                            Citation
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "18px",
                                              fontWeight: 600,
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                              lineHeight: "1.5",
                                              letterSpacing: "-0.18px",
                                            }}
                                          >
                                            :
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            backgroundColor: "#fcfeff",
                                            border: "1px solid #fff4e5",
                                            borderRadius: "4px",
                                            padding: "4px 12px",
                                            width: "100%",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#065b70",
                                              fontFamily: "Inter, sans-serif",
                                              lineHeight: "20px",
                                            }}
                                          >
                                            {parsedData.cfrCitation}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                </Box>
                              </AuthoritySection>
                            </Box>
                          )}
                      </Box>
                    )}
                  </SurveyCardDetails>
                </SurveyCard>
              );
            })
          ) : (
            <Box
              sx={{ padding: "24px", textAlign: "center", color: "#737373" }}
            >
              No survey data available
            </Box>
          )}
        </Box>
      </ReportDetailsContainer>
    );
  };

  const renderUploadAccordion = (
    title: string,
    type: "survey" | "incident",
    file: File | null,
    isDragOver: boolean,
  ) => {
    const loading = getLoadingState(type);
    const error = getError(type);
    const analysis = getAnalysis(type);
    const llmSection =
      type === "survey"
        ? RISK_LLM_SECTION.surveyReport
        : RISK_LLM_SECTION.incidentReport;
    const uploadLlmDebug = getRiskSectionLlmDebug(analysis, llmSection);

    return (
      <StyledAccordion
        expanded={tabExpandedPanel === type}
        onChange={handleTabAccordionChange(type)}
      >
        <AccordionSummary
          expandIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#262626"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          aria-controls={`${type}-content`}
          id={`${type}-header`}
          sx={{
            alignItems: "center",
            borderRadius: "4px",
            border: "1px solid #9EE5F4",
            backgroundColor: "#FFF",
            boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(180deg)",
            },
          }}
        >
          <AccordionIcon>
            <Box
              component="img"
              src={type === "survey" ? surveyIconSrc : incidentIconSrc}
              alt={type === "survey" ? "Survey Report" : "Incident Report"}
              sx={{
                width: "20px",
                height: "20px",
                display: "block",
                flexShrink: 0,
              }}
            />
          </AccordionIcon>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <AccordionTitle
              className="accordion-title"
              sx={{ lineHeight: 1.25 }}
            >
              {title}
            </AccordionTitle>
          </Box>
          <Box
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ flexShrink: 0, marginRight: isAffableConnected ? 0 : 1 }}
          >
            <GlobalAdminLlmPromptButton
              dialogTitle={
                type === "survey"
                  ? "Survey report analysis — LLM prompt"
                  : "Incident report analysis — LLM prompt"
              }
              promptText={uploadLlmDebug.prompt}
              wallTimeSec={uploadLlmDebug.wallSec}
            />
          </Box>
          {isAffableConnected && (
            <Button
              variant="outlined"
              disabled={isConnectingAffable}
              onClick={(e) => {
                e.stopPropagation();
                console.log(
                  "🎯 Connect with AffableFLOW clicked - from accordion",
                );
                setAffableSourceAccordion(type);
                handleConnectAffableBPM(type === "survey" ? "Survey" : "Risk");
              }}
              sx={{
                display: "flex",
                padding: "4px 12px",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                borderRadius: "8px",
                minHeight: "32px",
                height: "32px",
                marginRight: "8px",
                border: "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                background:
                  "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                whiteSpace: "nowrap",
                "&:hover": {
                  background: "#f5fdff",
                  borderColor: "#065b70",
                  boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                },
                "&:active": {
                  background: "#e5f9fd",
                },
              }}
            >
              <AffableConnectedIcon />
              <Typography
                sx={{
                  color: "var(--Brand-Colors-color-brand-hover, #086E86)",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "13px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "normal",
                  textTransform: "none",
                }}
              >
                {isConnectingAffable
                  ? "Connecting..."
                  : "Connect with AffableFLOW"}
              </Typography>
            </Button>
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: "#ffffff",
            padding: "12px",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <DescriptionText>{getDescription(type)}</DescriptionText>

            <UploadContainer sx={{ marginTop: "8px" }}>
              {loading ||
              (isSyncingAffableRecords && affableSourceAccordion === type) ? (
                <Box
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    padding: "32px",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "#065b70" }} />
                  {!(
                    isSyncingAffableRecords && affableSourceAccordion === type
                  ) && (
                    <>
                      <Typography
                        sx={{
                          fontSize: "17px",
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        Analyzing document…
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          color: "#737373",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        This AI-assisted analysis may take a few minutes.
                      </Typography>
                    </>
                  )}
                </Box>
              ) : file && !analysis ? (
                <FileDisplayContainer
                  sx={{
                    flexDirection: getShowReport(type) ? "column" : "row",
                    width: "calc(100% - 32px)",
                  }}
                >
                  <FileInfoContainer>
                    <FileIcon>
                      <ComplianceHistoryFileGlyph />
                    </FileIcon>
                    <FileDetails>
                      <FileName>{file.name}</FileName>
                      <FileSize>
                        {(file.name.split(".").pop() || "").toLowerCase()} |{" "}
                        {formatFileSize(file.size)}
                        {getUploadDate(type) && (
                          <>
                            {" "}
                            |{" "}
                            <span style={{ textTransform: "none" }}>
                              Uploaded: {getUploadDate(type)}
                            </span>
                          </>
                        )}
                      </FileSize>
                    </FileDetails>
                  </FileInfoContainer>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      width: getShowReport(type) ? "calc(100% - 32px)" : "auto",
                      justifyContent: getShowReport(type)
                        ? "flex-end"
                        : "flex-start",
                      marginTop: getShowReport(type) ? "8px" : 0,
                    }}
                  >
                    <ReUploadButton
                      onClick={() => {
                        const input = document.getElementById(
                          `risk-analysis-${type}-file-input`,
                        ) as HTMLInputElement | null;
                        if (input) {
                          input.value = "";
                          input.click();
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                          fill="#065B70"
                        />
                        <path
                          d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                          fill="#065B70"
                        />
                      </svg>
                      Re-Upload
                    </ReUploadButton>
                    <DeleteButton
                      aria-label="Delete uploaded file"
                      onClick={() => handleDeleteFile(type)}
                    >
                      <DeleteFileIcon />
                    </DeleteButton>
                  </Box>
                </FileDisplayContainer>
              ) : !file &&
                !(shouldShowAffablePanel && affableSourceAccordion === type) ? (
                <UploadArea
                  $isDragOver={isDragOver}
                  onDrop={(e) => handleDrop(e, type)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, type)}
                  onDragLeave={(e) => handleDragLeave(e, type)}
                  onClick={() => {
                    const input = document.getElementById(
                      `risk-analysis-${type}-file-input`,
                    ) as HTMLInputElement | null;
                    input?.click();
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      {isAffableConnected && (
                        <>
                          <IconButton
                            aria-label={
                              isSyncingAffableRecords &&
                              affableSourceAccordion === type
                                ? "Uploading file with AffableFLOW..."
                                : "Upload file with AffableFLOW"
                            }
                            title={
                              isSyncingAffableRecords &&
                              affableSourceAccordion === type
                                ? "Uploading file with AffableFLOW..."
                                : "Upload file with AffableFLOW"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAffableRecordsSync(
                                type === "incident" ? "Risk" : "Survey",
                              );
                            }}
                            disabled={isSyncingAffableRecords}
                            sx={{
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              background: "#e6fafc",
                              padding: "8px 16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              "&:hover": {
                                backgroundColor: "#d1f5f8",
                              },
                              "&:active": {
                                backgroundColor: "#c1eff5",
                              },
                            }}
                          >
                            {isSyncingAffableRecords &&
                            affableSourceAccordion === type ? (
                              <CircularProgress
                                size={20}
                                sx={{ color: "#065B70" }}
                              />
                            ) : (
                              <>
                                <AffableConnectedIcon />
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#065B70",
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Upload file from AffableFLOW
                                </Typography>
                              </>
                            )}
                          </IconButton>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0px",
                            }}
                          >
                            <Box
                              sx={{
                                width: "20px",
                                height: "1px",
                                backgroundColor: "#0E9ABA",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#0E9ABA",
                                fontFamily: "Rethink Sans, sans-serif",
                                padding: "0 2px",
                              }}
                            >
                              OR
                            </Typography>
                            <Box
                              sx={{
                                width: "20px",
                                height: "1px",
                                backgroundColor: "#0E9ABA",
                              }}
                            />
                          </Box>
                        </>
                      )}
                      <UploadButton>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.5 2C3.22386 2 3 2.22386 3 2.5C3 2.77614 3.22386 3 3.5 3H12.5C12.7761 3 13 2.77614 13 2.5C13 2.22386 12.7761 2 12.5 2H3.5ZM8.35355 4.14645C8.15829 3.95118 7.84171 3.95118 7.64645 4.14645L4.14645 7.64645C3.95118 7.84171 3.95118 8.15829 4.14645 8.35355C4.34171 8.54882 4.65829 8.54882 4.85355 8.35355L7.5 5.70711V13.5C7.5 13.7761 7.72386 14 8 14C8.27614 14 8.5 13.7761 8.5 13.5V5.70711L11.1464 8.35355C11.3417 8.54882 11.6583 8.54882 11.8536 8.35355C12.0488 8.15829 12.0488 7.84171 11.8536 7.64645L8.35355 4.14645Z"
                            fill="#065B70"
                          />
                        </svg>
                        Upload Documents
                      </UploadButton>
                      <span style={{ whiteSpace: "pre-line" }}>
                        {getSupportedFileTypesText(type) && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#737373",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {getSupportedFileTypesText(type)}
                          </Typography>
                        )}
                      </span>
                    </Box>
                  </Box>
                </UploadArea>
              ) : null}
            </UploadContainer>

            <HiddenInput
              id={`risk-analysis-${type}-file-input`}
              type="file"
              accept={
                // Restrict Incident Report uploads to PDF only, as requested
                type === "incident" ? ".pdf" : ".pdf,.doc,.docx,.xls,.xlsx"
              }
              onChange={(e) => {
                console.log("📁 FILE INPUT onChange FIRED! Type:", type);
                console.log("📁 Event:", e);
                const files = e.target.files;
                console.log("📁 Files from e.target.files:", files);
                console.log("📁 Files length:", files?.length);

                if (files && files.length > 0) {
                  console.log(
                    "📁 File selected:",
                    files[0].name,
                    files[0].size,
                  );
                  console.log("📁 About to call handleFileUpload...");
                  handleFileUpload(files[0], type);
                  console.log("📁 handleFileUpload call completed");
                } else {
                  console.warn("⚠️ onChange fired but NO files selected");
                }
              }}
              onClick={(e) => {
                console.log("📁 File input CLICKED");
              }}
            />

            {error && (
              <Alert
                severity="error"
                sx={{ marginTop: "12px", fontSize: "14px" }}
              >
                {error}
              </Alert>
            )}

            {analysis && file && !loading && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "5px",
                    alignItems: "stretch",
                    flexWrap: "wrap",
                    marginTop: "5px",
                  }}
                >
                  <FileDisplayContainer
                    sx={{
                      flex: 1,
                      minWidth: "300px",
                      marginTop: 0,
                      flexDirection: "row",
                    }}
                  >
                    <FileInfoContainer>
                      <FileIcon>
                        <ComplianceHistoryFileGlyph />
                      </FileIcon>
                      <FileDetails>
                        <FileName>{file.name}</FileName>
                        <FileSize>
                          {(file.name.split(".").pop() || "").toLowerCase()} |{" "}
                          {formatFileSize(file.size)}
                          {getUploadDate(type) && (
                            <>
                              {" "}
                              |{" "}
                              <span style={{ textTransform: "none" }}>
                                Uploaded: {getUploadDate(type)}
                              </span>
                            </>
                          )}
                        </FileSize>
                      </FileDetails>
                    </FileInfoContainer>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <ReUploadButton
                        onClick={() => {
                          const input = document.getElementById(
                            `risk-analysis-${type}-file-input`,
                          ) as HTMLInputElement | null;
                          if (input) {
                            input.value = "";
                            input.click();
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                            fill="#065B70"
                          />
                          <path
                            d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                            fill="#065B70"
                          />
                        </svg>
                        Re-Upload
                      </ReUploadButton>
                      <DeleteButton
                        aria-label="Delete uploaded file"
                        onClick={() => handleDeleteFile(type)}
                      >
                        <DeleteFileIcon />
                      </DeleteButton>
                      <ViewReportButton onClick={() => toggleReport(type)}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                            fill="white"
                          />
                        </svg>
                        {getShowReport(type) ? "Hide Report" : "View Analysis"}
                      </ViewReportButton>
                    </Box>
                  </FileDisplayContainer>
                </Box>
              </>
            )}

            {getShowReport(type) &&
              analysis &&
              (type === "survey"
                ? renderSurveyReportDetails(analysis, type)
                : renderReportDetails(analysis, type))}

            {affableSourceAccordion === type && shouldShowAffablePanel && (
              <Box sx={{ marginTop: "12px" }}>{renderAffableSyncPanel()}</Box>
            )}
          </Box>
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  const renderAffableSyncPanel = () => {
    if (!shouldShowAffablePanel) return null;

    return (
      <Box>
        {/* Back to upload files button - similar to ContinuousCompliance */}
        {!isSyncingAffableRecords &&
          (affableRecords.length > 0 || affableFileAnalysis) && (
            <Box
              onClick={handleCloseAffablePanel}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                cursor: "pointer",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <AffableConnectedIcon />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#262626",
                }}
              >
                Back to upload files
              </Typography>
            </Box>
          )}

        {affableSyncError && (
          <Alert
            severity="error"
            onClose={() => setAffableSyncError(null)}
            sx={{ mb: 1 }}
          >
            {affableSyncError}
          </Alert>
        )}

        {/* Show analysis results when available */}
        {affableFileAnalysis && affableFile && (
          <Box>
            <FileDisplayContainer
              sx={{
                flexDirection: "row",
                width: "calc(100% - 32px)",
                marginBottom: showAffableFileReport ? "12px" : 0,
              }}
            >
              <FileInfoContainer>
                <FileIcon>
                  <ComplianceHistoryFileGlyph />
                </FileIcon>
                <FileDetails sx={{ maxWidth: "250px", minWidth: 0 }}>
                  <FileName
                    sx={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={affableFile.name}
                  >
                    {affableFile.name}
                  </FileName>
                  <FileSize>
                    {(affableFile.name.split(".").pop() || "").toLowerCase()} |{" "}
                    {formatFileSize(affableFile.size)}
                    {affableFileUploadDate && (
                      <>
                        {" "}
                        |{" "}
                        <span style={{ textTransform: "none" }}>
                          Uploaded: {affableFileUploadDate}
                        </span>
                      </>
                    )}
                  </FileSize>
                </FileDetails>
              </FileInfoContainer>
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <ReUploadButton
                  onClick={() => {
                    setAffableFileAnalysis(null);
                    setAffableFile(null);
                    setAffableFileUploadDate(null);
                    setShowAffableFileReport(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                      fill="#065B70"
                    />
                    <path
                      d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                      fill="#065B70"
                    />
                  </svg>
                  Re-Upload
                </ReUploadButton>
                <DeleteButton
                  aria-label="Remove Affable uploaded file"
                  onClick={() => {
                    setAffableFileAnalysis(null);
                    setAffableFile(null);
                    setAffableFileUploadDate(null);
                    setShowAffableFileReport(false);
                  }}
                >
                  <DeleteFileIcon />
                </DeleteButton>
                <ViewReportButton
                  onClick={() =>
                    setShowAffableFileReport(!showAffableFileReport)
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                      fill="white"
                    />
                  </svg>
                  {showAffableFileReport ? "Hide Report" : "View Analysis"}
                </ViewReportButton>
              </Box>
            </FileDisplayContainer>

            {showAffableFileReport && affableFileAnalysis && (
              <Box>
                {affableFileAnalysis.metadata?.document_type ===
                "survey_document"
                  ? renderSurveyReportDetails(affableFileAnalysis, "survey")
                  : renderReportDetails(affableFileAnalysis, "incident")}
              </Box>
            )}
          </Box>
        )}

        {/* When there are records and documents, show 3 parallel grids like ContinuousCompliance */}
        {/* {!affableFileAnalysis &&
          affableRecords.length > 0 &&
          affableDocuments.length > 0 && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#043c4a",
                  fontFamily: '"Rethink Sans", sans-serif',
                }}
              >
                {affableSourceAccordion === "survey"
                  ? "Survey Report"
                  : "Incident Report"}
              </Typography>
            </Box>
          )} */}

        {!affableFileAnalysis &&
          affableRecords.length > 0 &&
          affableDocuments.length > 0 &&
          (loadingAffableFile ? (
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                padding: "32px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#065b70" }} />
              <Typography
                sx={{
                  fontSize: "17px",
                  color: "#065b70",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                Analyzing document…
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#737373",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                This AI-assisted analysis may take a few minutes.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "stretch",
                mt: 1,
                mb: 2,
              }}
            >
              {/* Left column: Policy Name table (mirrors ContinuousCompliance) */}
              <Box
                sx={{
                  width: "45%",
                  minWidth: 0,
                  border: "1px solid #0a829d",
                  borderRadius: "4px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Table Header */}
                <Box
                  sx={{
                    backgroundColor: "#0a829d",
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                  >
                    Policy Name
                  </Typography>
                </Box>

                {/* Table Body - Dynamic */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: "280px",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                      borderRadius: "2px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#0a829d",
                      borderRadius: "2px",
                      "&:hover": {
                        backgroundColor: "#086e86",
                      },
                    },
                  }}
                >
                  {affableRecords.map((record: any) => {
                    const isSelected =
                      selectedAffableDocumentId === record.DocumentID;
                    const primaryColumnKey =
                      affableTableColumns.length > 0
                        ? affableTableColumns[0]
                        : "";
                    const primaryValue = primaryColumnKey
                      ? record[primaryColumnKey]
                      : "";
                    const displayValue =
                      primaryValue === null || primaryValue === undefined
                        ? "Untitled Policy"
                        : String(primaryValue);

                    return (
                      <Box
                        key={record.DocumentID}
                        onClick={() => {
                          setSelectedAffableDocumentId(record.DocumentID);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "8px 12px",
                          borderBottom: "1px solid #e5f5f8",
                          gap: "8px",
                          cursor: "pointer",
                          backgroundColor: isSelected ? "#e6fafc" : "#ffffff",
                          "&:hover": {
                            backgroundColor: isSelected ? "#d4edf3" : "#f5feff",
                          },
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M4 2C3.44772 2 3 2.44772 3 3V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V5.41421C13 5.149 12.8946 4.89464 12.7071 4.70711L10.2929 2.29289C10.1054 2.10536 9.851 2 9.58579 2H4ZM5 5H8V6H5V5ZM5 8H11V9H5V8ZM5 11H11V12H5V11Z"
                            fill="#737373"
                          />
                        </svg>
                        <Typography
                          sx={{
                            fontFamily: '"Rethink Sans", sans-serif',
                            fontSize: "13px",
                            color: "#262626",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          {displayValue}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {/* Pagination Row - Fixed at bottom */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    borderTop: "1px solid #e5f5f8",
                    gap: "4px",
                    flexShrink: 0,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      py: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: 12,
                        color: "#065b70",
                      }}
                    >
                      Showing{" "}
                      {affableRecordCount === 0
                        ? 0
                        : (affableDocsPage - 1) * affableRowsPerPage + 1}
                      -
                      {Math.min(
                        affableDocsPage * affableRowsPerPage,
                        affableRecordCount,
                      )}{" "}
                      of {affableRecordCount}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        ml: "auto",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: 12,
                          color: "#065b70",
                        }}
                      >
                        Rows per page
                      </Typography>
                      <Select
                        size="small"
                        value={affableRowsPerPage}
                        onChange={(e) => {
                          const newPageSize = Number(e.target.value);
                          setAffableRowsPerPage(newPageSize);
                          setAffableDocsPage(1);
                          handleAffableRecordsSync(
                            currentWorkflowType,
                            0,
                            newPageSize,
                          );
                        }}
                        sx={{
                          minWidth: 80,
                          width: 80,
                          height: 28,
                          "& .MuiSelect-select": {
                            padding: "4px 8px",
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              "& .MuiMenuItem-root": {
                                fontSize: 12,
                                minHeight: "28px",
                                lineHeight: "28px",
                                fontFamily: '"Rethink Sans", sans-serif',
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                      </Select>
                      <Pagination
                        count={totalAffableDocPages}
                        page={affableDocsPage}
                        onChange={(_, value) => {
                          setAffableDocsPage(value);
                          handleAffableRecordsSync(
                            currentWorkflowType,
                            value - 1,
                          );
                        }}
                        color="primary"
                        size="small"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            fontSize: 12,
                            height: 28,
                            minWidth: 28,
                            padding: "0 6px",
                          },
                          "& .MuiPaginationItem-root.Mui-selected": {
                            backgroundColor: "#0a829d",
                            color: "#ffffff",
                          },
                          "& .MuiPaginationItem-root.Mui-selected:hover": {
                            backgroundColor: "#0a829d",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Right side: 2-column documents + default panel (mirror ContinuousCompliance) */}
              {affableDocuments.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "55%",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0,
                      alignItems: "stretch",
                    }}
                  >
                    <Box
                      sx={{
                        width: "35%",
                        borderRadius: "4px 0 0 4px",
                        border: "1px solid #0a829d",
                        backgroundColor: "#ffffff",
                        overflow: "hidden",
                        height: "240px",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#0a829d",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#ffffff",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(() => {
                            const selectedRecord = affableRecords.find(
                              (r: any) =>
                                r.DocumentID === selectedAffableDocumentId,
                            );
                            if (
                              selectedRecord &&
                              affableTableColumns.length > 0
                            ) {
                              return (
                                selectedRecord[affableTableColumns[0]] ||
                                "Documents"
                              );
                            }
                            return "Documents";
                          })()}
                        </Typography>
                      </Box>

                      <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                        {selectedAffableDocument?.folders?.length ? (
                          selectedAffableDocument.folders.map((folder: any) => {
                            const isActive =
                              selectedAffableFolder &&
                              selectedAffableFolder.id === folder.id;
                            return (
                              <Box
                                key={folder.id}
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  backgroundColor: isActive
                                    ? "#e6fafc"
                                    : "transparent",
                                  "&:hover": {
                                    backgroundColor: "#f5feff",
                                  },
                                }}
                                onClick={() =>
                                  setSelectedAffableFolderId(folder.id)
                                }
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#043c4a",
                                    fontWeight: isActive ? 600 : 500,
                                  }}
                                >
                                  {folder.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: "#0a829d",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {folder.images?.length || 0}{" "}
                                  {(folder.images?.length || 0) === 1
                                    ? "file"
                                    : "files"}
                                </Typography>
                              </Box>
                            );
                          })
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#4f6f78",
                              p: 1.5,
                            }}
                          >
                            No folders available for this document yet.
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        width: "65%",
                        borderRadius: "0 4px 4px 0",
                        border: "1px solid #0a829d",
                        borderLeft: "none",
                        backgroundColor: "#ffffff",
                        overflow: "hidden",
                        height: "240px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#ffffff",
                          borderBottom: "1px solid #0a829d",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#043c4a",
                          }}
                        >
                          {selectedAffableFolder?.name || "Default"}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, flex: 1, overflow: "hidden" }}>
                        {!selectedAffableFolder ||
                        !selectedAffableFolder.images?.length ? (
                          <Typography
                            sx={{ fontSize: "14px", color: "#4f6f78" }}
                          >
                            No files available in this folder.
                          </Typography>
                        ) : (
                          <>
                            <Box
                              sx={{
                                maxHeight: "100%",
                                height: "100%",
                                overflowY: "auto",
                                overflowX: "hidden",
                                pr: 1,
                                "&::-webkit-scrollbar": {
                                  width: "4px",
                                  height: "4px",
                                },
                                "&::-webkit-scrollbar-track": {
                                  backgroundColor: "#f1f1f1",
                                  borderRadius: "2px",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                  backgroundColor: "#0a829d",
                                  borderRadius: "2px",
                                  "&:hover": {
                                    backgroundColor: "#086e86",
                                  },
                                },
                              }}
                            >
                              {selectedAffableFolder.images.map(
                                (image: any) => (
                                  <Box
                                    key={`${selectedAffableFolder.id}-${image.id}`}
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 1,
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isAffableFileSelected(
                                        selectedAffableFolder.id,
                                        image.id,
                                      )}
                                      onChange={() =>
                                        selectAffableFile(
                                          selectedAffableFolder.id,
                                          image.id,
                                        )
                                      }
                                    />
                                    <AffableFileRow>
                                      <AffableFileName>
                                        {image.name}
                                        {image.type ? ` (${image.type})` : ""}
                                      </AffableFileName>
                                      <AffableFileMeta>
                                        {image.createdAt ||
                                          "No created date provided"}
                                      </AffableFileMeta>
                                    </AffableFileRow>
                                  </Box>
                                ),
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Analyse Selected File Button */}
                  <Box
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "5px 5px",
                      mt: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleUploadSelectedAffableFile}
                      disabled={loadingAffableFile || !selectedAffableFileKey}
                      sx={{
                        backgroundColor: "#0a829d",
                        color: "#ffffff",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: '"Rethink Sans", sans-serif',
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#086e86",
                        },
                      }}
                    >
                      {loadingAffableFile ? (
                        <>
                          <CircularProgress
                            size={16}
                            sx={{ color: "#ffffff", mr: 1 }}
                          />
                          Analyzing...
                        </>
                      ) : (
                        "Analyze Selected File"
                      )}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          ))}

        {/* Fallback: no documents */}
        {affableDocuments.length === 0 &&
          !isSyncingAffableRecords &&
          !affableSyncError && (
            <Typography sx={{ fontSize: "14px", color: "#4f6f78" }}>
              No documents available for this facility yet.
            </Typography>
          )}
      </Box>
    );
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0, width: "100%" }}
    >
      <ComplianceHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
          >
            <path
              d="M8.5 6.5V15.5H11.5V6.5C11.5 5.94772 11.0523 5.5 10.5 5.5H9.5C8.94772 5.5 8.5 5.94772 8.5 6.5ZM7.5 9.5V6.5C7.5 5.39543 8.39543 4.5 9.5 4.5H10.5C11.6046 4.5 12.5 5.39543 12.5 6.5V7.5H14C15.1046 7.5 16 8.39543 16 9.5V16C16 16.2761 15.7761 16.5 15.5 16.5H4.5C4.22386 16.5 4 16.2761 4 16V11.5C4 10.3954 4.89543 9.5 6 9.5H7.5ZM7.5 15.5V10.5H6C5.44772 10.5 5 10.9477 5 11.5V15.5H7.5ZM12.5 15.5H15V9.5C15 8.94772 14.5523 8.5 14 8.5H12.5V15.5Z"
              fill="#065B70"
            />
          </svg>
          <Typography
            sx={{
              color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
            }}
          >
            Risk Analysis
          </Typography>
        </Box>
        {showWatchGuide && (
          <Button
          variant="outlined"
          disabled={!onWatchGuideClick || watchGuideLoading}
          onClick={onWatchGuideClick}
          sx={{
            display: "flex",
            flexShrink: 0,
            minHeight: "40px",
            padding: "8px 16px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            borderRadius: "8px",
            border: "1px solid #5BBFD6",
            background: "#FFFFFF",
            boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F8FDFF",
              borderColor: "#3CA9C2",
            },
          }}
        >
          {watchGuideIconSrc ? (
            <Box
              component="img"
              src={assetUrl(watchGuideIconSrc)}
              alt="Watch guide"
              sx={{ width: 16, height: 16, display: "block" }}
            />
          ) : null}
          <Typography
            sx={{
              color: "#086E86",
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              lineHeight: "normal",
            }}
          >
            {watchGuideLoading ? "Loading Guide..." : "Watch Guide"}
          </Typography>
          </Button>
        )}
      </ComplianceHeader>
      {/* <Typography
        sx={{
          color: "#525252",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
          ml: "28px",
        }}
      >
        AI-assisted analysis. Human review and verification required.
      </Typography> */}
      <RiskAnalysisContainer sx={{ mt: "12px" }}>
        <AccordionsContainer>
          {renderUploadAccordion(
            "Incident Report",
            "incident",
            incidentFile,
            dragOverIncident,
          )}
          {renderUploadAccordion(
            "Survey Report",
            "survey",
            surveyFile,
            dragOverSurvey,
          )}
          {canAccessEmployeeAgreements && (
            <StyledAccordion
              expanded={tabExpandedPanel === "employeeAgreement"}
              onChange={handleTabAccordionChange("employeeAgreement")}
            >
              <AccordionSummary
                expandIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#262626"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                sx={{
                  alignItems: "center",
                  borderRadius: "4px",
                  border: "1px solid #9EE5F4",
                  backgroundColor: "#FFF",
                  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                    transform: "rotate(180deg)",
                  },
                }}
              >
                <AccordionIcon>
                  <Box
                    component="img"
                    src={surveyIconSrc}
                    alt="Employee Agreements"
                    sx={{
                      width: "20px",
                      height: "20px",
                      display: "block",
                      flexShrink: 0,
                    }}
                  />
                </AccordionIcon>
                <AccordionTitle className="accordion-title">
                  Employee Agreements
                </AccordionTitle>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: "#ffffff",
                  padding: "12px",
                }}
              >
                <DescriptionText>
                  Upload one or more physician employment agreement files (full
                  package: base agreement, addenda, exhibits). All selected
                  files are analyzed together (Stark, AKS, FMV, and related
                  topics).
                </DescriptionText>
                <Box
                  sx={{
                    mt: 1,
                    mb: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      component="label"
                      htmlFor="employee-agreement-type-select"
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#065b70",
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      Employee type
                    </Typography>
                    <Select
                      id="employee-agreement-type-select"
                      size="small"
                      value={employeeAgreementType}
                      onChange={(e) =>
                        setEmployeeAgreementType(String(e.target.value))
                      }
                      sx={{
                        minWidth: 180,
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      <MenuItem
                        value="physician"
                        sx={{ fontFamily: "Rethink Sans, sans-serif" }}
                      >
                        Physician
                      </MenuItem>
                    </Select>
                  </Box>
                  <UploadContainer
                    sx={{
                      flex: 1,
                      minWidth: { xs: "100%", sm: 320 },
                      minHeight: { xs: 88, sm: 96 },
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 2, sm: 2.5 },
                      borderRadius: "10px",
                      border: dragOverEmployeeAgreement
                        ? "2px dashed #0a829d"
                        : "1px dashed #5bbfd6",
                      backgroundColor: dragOverEmployeeAgreement
                        ? "#f0fcff"
                        : "#fafdfe",
                      transition: "border-color 0.2s, background-color 0.2s",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverEmployeeAgreement(true);
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOverEmployeeAgreement(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragOverEmployeeAgreement(false);
                    }}
                    onDrop={handleDropEmployeeAgreement}
                  >
                    {!showEmployeeAgreementTabbedOutput ? (
                      <>
                        <Box
                          aria-hidden
                          sx={{
                            fontSize: "2.25rem",
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          📁
                        </Box>
                        <Typography
                          component="p"
                          sx={{
                            m: 0,
                            fontFamily: "Rethink Sans, sans-serif",
                            fontSize: { xs: "14px", sm: "15px" },
                            fontWeight: 500,
                            color: "#262626",
                            lineHeight: 1.45,
                            textAlign: { xs: "center", sm: "left" },
                          }}
                        >
                          Drag and drop one or more files here, or{" "}
                          <Box
                            component="span"
                            onClick={() =>
                              document
                                .getElementById("employee-agreement-file-input")
                                ?.click()
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                document
                                  .getElementById("employee-agreement-file-input")
                                  ?.click();
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            sx={{
                              color: "#0a829d",
                              cursor: "pointer",
                              fontWeight: 700,
                              textDecoration: "underline",
                              "&:hover": { color: "#065b70" },
                            }}
                          >
                            browse
                          </Box>
                        </Typography>
                        <input
                          id="employee-agreement-file-input"
                          type="file"
                          accept=".pdf,.docx,.doc,.txt"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const list = e.target.files
                              ? Array.from(e.target.files)
                              : [];
                            e.target.value = "";
                            if (list.length)
                              void handleEmployeeAgreementUpload(list);
                          }}
                        />
                      </>
                    ) : null}
                  </UploadContainer>
                </Box>
                {showEmployeeAgreementTabbedOutput ? (
                  <EmployeeAgreementResults
                    analysis={
                      employeeAgreementResult?.success &&
                      employeeAgreementResult.analysis
                        ? (employeeAgreementResult.analysis as Record<
                            string,
                            unknown
                          >)
                        : null
                    }
                    loading={loadingEmployeeAgreement}
                    statusFiles={employeeAgreementFiles}
                    statusFileResults={employeeAgreementStatusFileResults}
                    onRequestNewAnalysis={clearEmployeeAgreementAnalysis}
                  />
                ) : null}
                {errorEmployeeAgreement && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errorEmployeeAgreement}
                  </Alert>
                )}
              </AccordionDetails>
            </StyledAccordion>
          )}
          {payorContractAnalysis
            ? React.cloneElement(payorContractAnalysis, {
                accordionExpanded: tabExpandedPanel === "payorContract",
                onAccordionChange: handleTabAccordionChange("payorContract"),
              })
            : null}
          {renderRiskRecentlyReviewedDocuments()}
        </AccordionsContainer>
      </RiskAnalysisContainer>
    </Box>
  );
}
